import {
  ACCESSORIES,
  CHICAGO_CATALOG,
  CUBA_CATALOG,
  DAMAGE_TYPES,
  DOCK_TREATMENTS,
  EMPLOYEE_ROLES,
  EMPLOYEE_TRAITS,
  EVENT_TYPES,
  FACILITY_BY_ID,
  GOOD_BY_ID,
  GOODS,
  INTERIOR_OPPORTUNITIES,
  MEETING_POLICIES,
  PLOT_OPTIONS,
  PLOT_DEEDS,
  RANKS,
  SEASONS,
  SHOP_GOODS_ATLAS,
  SUPPLY_ROUTES,
  TRAFFIC_BY_ID,
  TRAFFIC_TYPES,
  VENDOR_PROFILES,
} from './data.js';
import { chance, forkSeed, normalizeSeed, pick, pickWeighted, randomFloat, randomInt } from './random.js';
import { selectRegionalVendor, selectVendorOffer, vendorOfferLifetime } from './characters.js';
import { formatCurrency as cashLabel } from './currency.js';

const DAY_MINUTES = 1440;
const START_MINUTE = 7 * 60;
const SCHEDULE_AHEAD_MINUTES = DAY_MINUTES * 2;
const LEDGER_LIMIT = 600;
const MAX_PRICE_CENTS = 1_000_000;
const PHASE_NAMES = ['foundation', 'frame', 'frontage', 'finish'];
const POKER_HARD_MAX_EXPOSURE_CENTS = 100_000;
const POKER_SAFE_SETTLEMENT_PHASES = new Set(['idle', 'showdown', 'game_over', 'game-over', 'between-hands', 'hand-settled', 'session-complete']);

const clamp = (minimum, maximum, value) => Math.max(minimum, Math.min(maximum, value));
const roundTo5 = (value) => Math.max(5, Math.round(value / 5) * 5);
const currentDay = (state) => Math.floor(state.timeMinutes / DAY_MINUTES) + 1;
const findVisitor = (state, visitorId) => state.traffic.find((visitor) => visitor.id === visitorId);
const getGood = (goodId) => GOOD_BY_ID[goodId];
const openingComplete = (state) => state.opening?.status === 'completed';
const STARTER_SHELF_GOODS = ['pelts', 'rope', 'provisions'];

function applyMarketPricing(state, goodId) {
  const item = state.inventory[goodId];
  const marketCents = state.market[goodId].priceCents;
  item.buyCeilingCents = Math.min(MAX_PRICE_CENTS, roundTo5(marketCents * 0.85));
  item.listPriceCents = Math.min(MAX_PRICE_CENTS, Math.max(roundTo5(marketCents), item.avgCostCents + 5));
}

function landMarketIsOpen(state) {
  if (!openingComplete(state)) return false;
  const elapsed = Math.max(0, state.timeMinutes - state.opening.completedMinute);
  const laterTransactions = Math.max(0, state.transactions - state.opening.transactionsAtCompletion);
  return elapsed >= 720 || laterTransactions >= 4;
}

function addLedgerEntry(state, entry) {
  state.ledger.push({
    id: `ledger-${state.nextLedgerId++}`,
    minute: state.timeMinutes,
    day: currentDay(state),
    ...entry,
  });
  if (state.ledger.length > LEDGER_LIMIT) {
    state.ledger.splice(0, state.ledger.length - LEDGER_LIMIT);
  }
}

function isPlainRecord(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function pokerSnapshotIsSafe(snapshot) {
  return Boolean(snapshot) && POKER_SAFE_SETTLEMENT_PHASES.has(String(snapshot.phase).toLowerCase());
}

function assertJsonTree(value, seen = new Set()) {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new TypeError('Poker engine snapshot cannot contain non-finite numbers');
    return;
  }
  if (typeof value !== 'object') throw new TypeError('Poker engine snapshot can contain only JSON values');
  if (seen.has(value)) throw new TypeError('Poker engine snapshot cannot contain circular references');
  seen.add(value);
  if (Array.isArray(value)) value.forEach((item) => assertJsonTree(item, seen));
  else Object.values(value).forEach((item) => assertJsonTree(item, seen));
  seen.delete(value);
}

function clonePokerSnapshot(snapshot, revision, currentStackCents) {
  if (!isPlainRecord(snapshot)) throw new TypeError('Poker engine snapshot must be a JSON object');
  if (!Number.isInteger(snapshot.version) || snapshot.version < 1) throw new Error('Poker engine snapshot needs a positive integer version');
  if (!Number.isInteger(snapshot.revision) || snapshot.revision < 0) throw new Error('Poker engine snapshot needs a nonnegative engine revision');
  if (!Number.isInteger(snapshot.handNumber) || snapshot.handNumber < 0) throw new Error('Poker engine snapshot is missing its hand number');
  if (!Number.isInteger(snapshot.dealer) || snapshot.dealer < 0 || snapshot.dealer > 3) throw new Error('Poker engine snapshot has an invalid dealer seat');
  if (typeof snapshot.phase !== 'string' || !snapshot.phase.trim()) throw new Error('Poker engine snapshot is missing its phase');
  if (!Array.isArray(snapshot.seats) || snapshot.seats.length !== 4 || snapshot.seats.some((seat) => !isPlainRecord(seat) || !Number.isInteger(seat.stack) || seat.stack < 0)) {
    throw new Error('Poker engine snapshot must contain four seats with nonnegative integer stacks');
  }
  if (snapshot.seats[0].stack !== currentStackCents) throw new Error('Poker current stack must match player seat zero in the engine snapshot');
  if (!Array.isArray(snapshot.deck) || !Number.isInteger(snapshot.deckIndex) || snapshot.deckIndex < 0 || snapshot.deckIndex > snapshot.deck.length) throw new Error('Poker engine snapshot has invalid deck state');
  if (!Number.isInteger(snapshot.pot) || snapshot.pot < 0 || !Number.isInteger(snapshot.tableChips) || snapshot.tableChips < 0) throw new Error('Poker engine snapshot has invalid pot or table chip state');
  if (snapshot.seats.reduce((sum, seat) => sum + seat.stack, snapshot.pot) !== snapshot.tableChips) throw new Error('Poker engine snapshot does not conserve table chips');
  assertJsonTree(snapshot);
  let json;
  try {
    json = JSON.stringify(snapshot);
  } catch {
    throw new TypeError('Poker engine snapshot must be JSON-serializable');
  }
  if (!json) throw new TypeError('Poker engine snapshot must be JSON-serializable');
  const cloned = JSON.parse(json);
  if (JSON.stringify(cloned) !== json) throw new TypeError('Poker engine snapshot must be fully JSON-serializable');
  return cloned;
}

function migratedPokerHistoryEntry(hand) {
  const buyInCents = Number.isInteger(hand.wagerCents) && hand.wagerCents >= 0 ? hand.wagerCents : 0;
  const creditedCents = Number.isInteger(hand.playerCreditCents) && hand.playerCreditCents >= 0 ? hand.playerCreditCents : 0;
  return {
    sessionId: hand.id || `legacy-poker-session-${hand.openedMinute || 0}`,
    nonce: `legacy-settled-${forkSeed(hand.id || buyInCents, hand.settledMinute || 0).toString(36)}`,
    plotIndex: [1, 2, 4, 5].includes(hand.plotIndex) ? hand.plotIndex : 2,
    buyInCents,
    escrowCents: buyInCents,
    currentStackCents: creditedCents,
    maxExposureCents: Math.max(buyInCents, creditedCents, hand.maxPlayerCreditCents || 0),
    engineSnapshot: null,
    revision: 0,
    status: 'settled',
    openedMinute: Number.isInteger(hand.openedMinute) ? hand.openedMinute : START_MINUTE,
    checkpointMinute: Number.isInteger(hand.openedMinute) ? hand.openedMinute : START_MINUTE,
    settledMinute: Number.isInteger(hand.settledMinute) ? hand.settledMinute : START_MINUTE,
    creditedCents,
    playerNetCents: creditedCents - buyInCents,
    migratedLegacy: true,
  };
}

function hydratePokerState(state) {
  if (!state.poker || typeof state.poker !== 'object') {
    state.poker = { nextSessionId: 1, activeSession: null, history: [] };
    return;
  }
  const legacyEnvelope = Array.isArray(state.poker.openHands) || Number.isInteger(state.poker.nextHandId);
  if (legacyEnvelope) {
    const legacyOpenHands = Array.isArray(state.poker.openHands) ? state.poker.openHands.filter((hand) => isPlainRecord(hand)) : [];
    const history = (Array.isArray(state.poker.history) ? state.poker.history : []).map((hand) => (
      Object.hasOwn(hand, 'sessionId') ? hand : migratedPokerHistoryEntry(hand)
    ));
    let activeSession = null;
    if (legacyOpenHands.length > 0) {
      const buyInCents = legacyOpenHands.reduce((sum, hand) => sum + (Number.isInteger(hand.wagerCents) && hand.wagerCents > 0 ? hand.wagerCents : 0), 0);
      const legacyExposure = legacyOpenHands.reduce((sum, hand) => sum + Math.max(hand.wagerCents || 0, hand.maxPlayerCreditCents || 0), 0);
      const legacyIds = legacyOpenHands.map((hand) => hand.id).filter(Boolean);
      const sessionId = `migrated-poker-session-${Math.max(1, state.poker.nextHandId || 1)}`;
      activeSession = {
        sessionId,
        nonce: `migrated-${forkSeed(state.seed, `${sessionId}:${legacyIds.join(':')}`).toString(36)}`,
        plotIndex: [1, 2, 4, 5].includes(legacyOpenHands[0].plotIndex) ? legacyOpenHands[0].plotIndex : 2,
        buyInCents,
        escrowCents: buyInCents,
        currentStackCents: buyInCents,
        maxExposureCents: Math.max(buyInCents, legacyExposure),
        engineSnapshot: null,
        revision: 0,
        status: 'frozen',
        openedMinute: Math.min(...legacyOpenHands.map((hand) => Number.isInteger(hand.openedMinute) ? hand.openedMinute : state.timeMinutes)),
        checkpointMinute: state.timeMinutes,
        frozenMinute: state.timeMinutes,
        frozenReason: 'Legacy poker hands require a fresh host checkpoint before play can resume',
        migratedLegacy: true,
        legacyHandIds: legacyIds,
      };
    }
    state.poker = {
      nextSessionId: Math.max(1, state.poker.nextHandId || 1),
      activeSession,
      history,
    };
    return;
  }
  if (!Number.isInteger(state.poker.nextSessionId) || state.poker.nextSessionId < 1) state.poker.nextSessionId = 1;
  if (!Object.hasOwn(state.poker, 'activeSession')) state.poker.activeSession = null;
  if (!Array.isArray(state.poker.history)) state.poker.history = [];
}

function inventoryBulk(state) {
  return GOODS.reduce((sum, good) => sum + state.inventory[good.id].qty * good.bulk, 0);
}

function reservedBulk(state) {
  return state.orders.reduce((sum, order) => {
    if (order.status !== 'in-transit') return sum;
    return sum + order.qty * getGood(order.goodId).bulk;
  }, 0);
}

function shopFacility(state) {
  return FACILITY_BY_ID[state.plots[2].facilityId] || FACILITY_BY_ID['shop-shack'];
}

function operatingFacilityIds(state) {
  return new Set(state.plots.filter((plot) => plot.owned && plot.facilityId && !plot.construction).map((plot) => plot.facilityId));
}

function goodFacilityBlockReason(state, good) {
  if (!good?.requiredFacilityIds?.length) return null;
  if (good.id === 'artwork') {
    const gallery = state.plots.find((plot) => plot.owned
      && plot.facilityId === 'art-gallery'
      && !plot.construction
      && plot.condition > 20
      && !plot.damage.some((damage) => damage.severity >= 3));
    if (!gallery) return 'Artwork trading requires an operating, undamaged Frontier Art Gallery';
  }
  const operating = operatingFacilityIds(state);
  if (good.requiredFacilityIds.some((facilityId) => operating.has(facilityId))) return null;
  const facilityNames = good.requiredFacilityIds.map((facilityId) => FACILITY_BY_ID[facilityId]?.name || facilityId);
  return `${good.name} trading requires an operating ${facilityNames.join(' or ')}`;
}

function chicagoTransportLevel(state) {
  const operating = operatingFacilityIds(state);
  return Object.entries(CHICAGO_CATALOG.transportUnlocks).reduce((best, [facilityId, level]) => operating.has(facilityId) ? Math.max(best, level) : best, 0);
}

function advancedRiverTransportReady(state) {
  const operating = operatingFacilityIds(state);
  return CUBA_CATALOG.requiredFacilityIds.some((facilityId) => operating.has(facilityId));
}

function chicagoAccessBlockReason(state, good) {
  if (!openingComplete(state)) return 'Complete Eli Mercer’s opening fur trade first';
  if (good.tags.includes('import')) return `${good.name} is available only through the Havana import route`;
  if (good.tags.includes('no-wholesale')) return `${good.name} is available only from rare travelers`;
  if (CHICAGO_CATALOG.starterQuotesCents[good.id]) return null;
  const requiredLevel = Math.max(1, good.rank);
  if (chicagoTransportLevel(state) < requiredLevel) return `${good.name} requires an operating transport facility rated for level ${requiredLevel} freight`;
  return null;
}

function storageCapacity(state) {
  return state.plots.reduce((sum, plot, index) => {
    if (index === 2) return sum;
    return sum + (FACILITY_BY_ID[plot.facilityId]?.capacityBonus || 0);
  }, shopFacility(state).capacity);
}

function coldStorageCapacity(state) {
  return state.plots.reduce((sum, plot, index) => {
    if (index === 2 || !plot.owned) return sum;
    return sum + (FACILITY_BY_ID[plot.facilityId]?.coldCapacity || 0);
  }, 0);
}

function refrigeratedShelfLife(state) {
  return state.plots.reduce((longest, plot, index) => {
    if (index === 2 || !plot.owned) return longest;
    return Math.max(longest, FACILITY_BY_ID[plot.facilityId]?.coldShelfLifeMinutes || 0);
  }, 0);
}

function availableColdStorage(state) {
  return Math.max(0, coldStorageCapacity(state) - state.inventory['fresh-fish'].qty);
}

function displaySlots(state) {
  return shopFacility(state).displaySlots;
}

function deedPurchaseBlockReason(state, plotIndex) {
  const plot = state.plots[plotIndex - 1];
  const deed = PLOT_DEEDS[plotIndex];
  if (!plot || !deed) return 'Unknown plot';
  if (plot.owned) return 'Plot already owned';
  if (plotIndex === 3) return 'Plot 3 is already owned';
  if (!landMarketIsOpen(state)) return openingComplete(state)
    ? 'Land deeds open after 12 game hours or four further trades'
    : 'Complete Eli Mercer’s opening fur trade before buying land';
  if (state.cashCents < plot.deedCostCents) return `Need ${cashLabel(plot.deedCostCents - state.cashCents)} more for the deed`;
  return null;
}

function constructionBlockReason(state, plotIndex, facilityId) {
  const plot = state.plots[plotIndex - 1];
  const facility = FACILITY_BY_ID[facilityId];
  if (!plot) return 'Unknown plot';
  if (!facility) return 'Unknown facility';
  if (!PLOT_OPTIONS[plotIndex]?.includes(facilityId)) return `${facility.name} is not allowed on Plot ${plotIndex}`;
  if ((plotIndex === 3) !== (facility.kind === 'shop')) return 'Only Plot 3 may contain the trading shop';
  if (!plot.owned) return `Purchase the Plot ${plotIndex} deed first`;
  if (facility.rank > state.rank) return `${facility.name} is not unlocked`;
  if (plot.construction) return 'That plot already has construction underway';
  const existing = FACILITY_BY_ID[plot.facilityId];
  if (existing?.id === facilityId) return `${facility.name} is already present`;
  if (plotIndex === 3 && existing && facility.tier <= existing.tier) return 'Shop upgrades must advance to a higher tier';
  const currentBonus = existing?.capacityBonus || 0;
  const futureCapacity = storageCapacity(state) - currentBonus + (facility.capacityBonus || 0);
  if (inventoryBulk(state) + reservedBulk(state) > futureCapacity) return 'Move stock before replacing this storage facility';
  const currentColdCapacity = existing?.coldCapacity || 0;
  const futureColdCapacity = coldStorageCapacity(state) - currentColdCapacity + (facility.coldCapacity || 0);
  if (state.inventory['fresh-fish'].qty > futureColdCapacity) return 'Sell or discard fresh fish before removing refrigerated storage';
  if (state.labor.available < facility.laborRequired) return `Need ${facility.laborRequired} labor; ${state.labor.available} available`;
  const totalStartCostCents = facility.costCents + facility.laborCostCents;
  if (state.cashCents < totalStartCostCents) return `Need ${cashLabel(totalStartCostCents - state.cashCents)} more for construction and labor`;
  return null;
}

function plotProjectBlockReason(state, plotIndex, facilityId, cashReserveCents = 0) {
  const plot = state.plots[plotIndex - 1];
  const facility = FACILITY_BY_ID[facilityId];
  if (!plot) return 'Unknown plot';
  if (!facility) return 'Unknown facility';
  if (!plot.owned) {
    const deedReason = deedPurchaseBlockReason(state, plotIndex);
    if (deedReason) return deedReason;
  }
  const deedCostCents = plot.owned ? 0 : plot.deedCostCents;
  const afterDeed = plot.owned ? state : {
    ...state,
    cashCents: state.cashCents - deedCostCents,
    plots: state.plots.map((item) => item.index === plotIndex ? { ...item, owned: true } : item),
  };
  const constructionReason = constructionBlockReason(afterDeed, plotIndex, facilityId);
  if (constructionReason) return constructionReason;
  const requiredCents = deedCostCents + facility.costCents + facility.laborCostCents + cashReserveCents;
  if (state.cashCents < requiredCents) return `Need ${cashLabel(requiredCents - state.cashCents)} more for this project while keeping ${cashLabel(cashReserveCents)} for trading`;
  return null;
}

function identityTotals(state) {
  const totals = { wild: 0, hospitable: 0, industrious: 0, prestigious: 0, civic: 0 };
  const addIdentity = (identity) => {
    Object.entries(identity || {}).forEach(([key, value]) => {
      if (!Number.isFinite(value)) return;
      totals[key] = (Number.isFinite(totals[key]) ? totals[key] : 0) + value;
    });
  };
  state.plots.forEach((plot) => addIdentity(FACILITY_BY_ID[plot.facilityId]?.identity));
  state.decorations.forEach((decoration) => {
    const accessory = ACCESSORIES.find((item) => item.id === decoration.accessoryId);
    const tree = treeLifecycleSnapshot(state, decoration, accessory);
    addIdentity(tree ? tree.identity : accessory?.identity);
  });
  return totals;
}

function townPopularity(state) {
  const identity = identityTotals(state);
  const civicDecor = state.decorations.reduce((sum, decoration) => {
    const accessory = ACCESSORIES.find((item) => item.id === decoration.accessoryId);
    return sum + (accessory?.popularity || 0);
  }, 0);
  const developedPlots = state.plots.filter((plot, index) => index !== 2 && plot.facilityId && FACILITY_BY_ID[plot.facilityId]?.kind !== 'nature').length;
  return clamp(0, 100, Math.floor(state.reputation / 20) + civicDecor + identity.civic * 2 + developedPlots * 6);
}

function publicComfort(state) {
  return state.decorations.reduce((sum, decoration) => {
    const accessory = ACCESSORIES.find((item) => item.id === decoration.accessoryId);
    return sum + (accessory?.comfortEffect || 0);
  }, 0);
}

function employeeRole(roleId) {
  return EMPLOYEE_ROLES.find((role) => role.id === roleId);
}

function employeeTrait(traitId) {
  return EMPLOYEE_TRAITS.find((trait) => trait.id === traitId);
}

function damageType(typeId) {
  return DAMAGE_TYPES.find((type) => type.id === typeId);
}

function dockTreatmentType(treatmentId) {
  return DOCK_TREATMENTS.find((treatment) => treatment.id === treatmentId);
}

function employeeRolePower(state, roleId, assignment = null) {
  return state.employees.hired.reduce((sum, employee) => {
    if (employee.roleId !== roleId || !employee.assignment) return sum;
    if (assignment && employee.assignment.type !== assignment) return sum;
    const traitBonus = employee.traitIds.reduce((bonus, traitId) => bonus + (employeeTrait(traitId)?.performanceBps || 0), 0) / 10000;
    const reliability = (0.5 + employee.loyalty / 200) * (0.55 + employee.integrity / 225);
    return sum + employee.skill * reliability * (1 + traitBonus);
  }, 0);
}

function dockSafetyBonus(state) {
  return state.dockTreatments.reduce((sum, owned) => {
    if (owned.plotIndex == null) return sum;
    return sum + (dockTreatmentType(owned.treatmentId)?.safetyBonus || 0);
  }, 0);
}

function townSafety(state) {
  const ownedPlots = state.plots.filter((plot) => plot.owned);
  const averageCondition = ownedPlots.length
    ? ownedPlots.reduce((sum, plot) => sum + plot.condition, 0) / ownedPlots.length
    : 100;
  const damageCount = ownedPlots.reduce((sum, plot) => sum + plot.damage.length, 0);
  const guardPower = employeeRolePower(state, 'guard');
  const investigatorPower = employeeRolePower(state, 'investigator');
  const lampBonus = state.decorations.filter((decoration) => decoration.accessoryId === 'street-lantern').length * 2;
  return clamp(0, 100, Math.round(35 + averageCondition * 0.5 + guardPower * 3 + investigatorPower + dockSafetyBonus(state) + lampBonus - damageCount * 4));
}

function fireProtection(state) {
  const wardenPower = employeeRolePower(state, 'fire-warden');
  const wellBonus = state.decorations.filter((decoration) => decoration.accessoryId === 'water-well').length * 3;
  const rainBarrelBonus = state.decorations.filter((decoration) => decoration.accessoryId === 'rain-barrels').length * 2;
  return clamp(0, 80, Math.round(wardenPower * 5 + wellBonus + rainBarrelBonus));
}

function dockServiceBonusBps(state, facilityId) {
  const plotIds = new Set(state.plots.filter((plot) => plot.facilityId === facilityId).map((plot) => plot.index));
  return Math.min(3500, state.dockTreatments.reduce((sum, owned) => {
    if (!plotIds.has(owned.plotIndex)) return sum;
    return sum + (dockTreatmentType(owned.treatmentId)?.serviceBonusBps || 0);
  }, 0));
}

function effectiveVendorAskCents(state, askCents) {
  const buyerDiscountBps = Math.min(1000, Math.round(employeeRolePower(state, 'buyer') * 180));
  return roundTo5(askCents * (10000 - buyerDiscountBps) / 10000);
}

function createEmployeeCandidate(state, day) {
  const sequence = state.employees.nextCandidateId++;
  const role = pick(state, EMPLOYEE_ROLES);
  // The 66-face portrait roster is traversed before cycling, so repeated hiring
  // slates do not immediately recycle the same people.
  const person = VENDOR_PROFILES[(sequence - 1) % VENDOR_PROFILES.length];
  const firstTrait = pick(state, EMPLOYEE_TRAITS);
  const remainingTraits = EMPLOYEE_TRAITS.filter((trait) => trait.id !== firstTrait.id);
  const secondTrait = pick(state, remainingTraits);
  const skill = randomInt(state, 1, 5);
  const wageVarianceBps = randomInt(state, 8800, 11800);
  return {
    id: `candidate-${sequence}`,
    personId: person.id,
    name: person.name,
    roleId: role.id,
    skill,
    wageCents: roundTo5(role.baseWageCents * wageVarianceBps * (8500 + skill * 600) / 1e8),
    loyalty: randomInt(state, 42, 84),
    integrity: randomInt(state, 18, 98),
    traitIds: [firstTrait.id, secondTrait.id],
    appearance: {
      ...person.portrait,
      pose: 'front',
      honestyCue: false,
    },
    knowledge: { skill: 20, loyalty: 10, integrity: 0, traits: 0 },
    availableDay: day,
    expiresDay: day + 7,
  };
}

function refreshEmployeeCandidates(state, day, initial = false) {
  if (!initial && day < state.employees.nextRefreshDay) return;
  state.employees.pastCandidates.push(...state.employees.candidates.map((candidate) => ({ ...candidate, outcome: 'expired' })));
  if (state.employees.pastCandidates.length > 132) state.employees.pastCandidates.splice(0, state.employees.pastCandidates.length - 132);
  state.employees.candidates = Array.from({ length: 5 }, () => createEmployeeCandidate(state, day));
  state.employees.nextRefreshDay = day + 7;
  if (!initial) {
    addLedgerEntry(state, { kind: 'employee-candidates', accepted: true, totalCents: 0, reason: 'A new slate of agent candidates reached the ledger' });
  }
}

function processEmployeeDay(state, day) {
  refreshEmployeeCandidates(state, day);
  const investigatorPower = employeeRolePower(state, 'investigator');
  for (const employee of state.employees.hired) {
    const paidCents = Math.min(state.cashCents, employee.wageCents);
    state.cashCents -= paidCents;
    const fullyPaid = paidCents === employee.wageCents;
    employee.loyalty = clamp(0, 100, employee.loyalty + (fullyPaid ? 1 : -10));
    employee.knowledge.skill = clamp(0, 100, employee.knowledge.skill + randomInt(state, 2, 5));
    employee.knowledge.loyalty = clamp(0, 100, employee.knowledge.loyalty + randomInt(state, 2, 4));
    employee.knowledge.integrity = clamp(0, 100, employee.knowledge.integrity + 1 + Math.floor(investigatorPower * 2));
    employee.knowledge.traits = clamp(0, 100, employee.knowledge.traits + 1 + Math.floor(investigatorPower));
    addLedgerEntry(state, {
      kind: 'employee-wage',
      accepted: fullyPaid,
      employeeId: employee.id,
      roleId: employee.roleId,
      totalCents: -paidCents,
      reason: fullyPaid ? `${employee.name}'s daily wage paid` : `${employee.name}'s wage was partly unpaid`,
    });

    if (!chance(state, 0.2)) continue;
    const traitPerformance = employee.traitIds.reduce((sum, traitId) => sum + (employeeTrait(traitId)?.performanceBps || 0), 0) / 1000;
    const performance = employee.skill * 12 + employee.loyalty * 0.25 + employee.integrity * 0.12 + traitPerformance + randomInt(state, -22, 22);
    const misconduct = employee.integrity < 35 && chance(state, (40 - employee.integrity) / 100);
    if (misconduct) {
      const loss = Math.min(state.cashCents, employee.wageCents * randomInt(state, 1, 3));
      state.cashCents -= loss;
      employee.knowledge.integrity = clamp(0, 100, employee.knowledge.integrity + 18);
      state.reputation = Math.max(0, state.reputation - 1);
      addLedgerEntry(state, { kind: 'employee-performance', accepted: false, employeeId: employee.id, totalCents: -loss, reason: `A documented cash or inventory discrepancy occurred on ${employee.name}'s assignment` });
    } else if (performance >= 70) {
      state.reputation += 1;
      employee.loyalty = clamp(0, 100, employee.loyalty + 1);
      addLedgerEntry(state, { kind: 'employee-performance', accepted: true, employeeId: employee.id, totalCents: 0, reason: `${employee.name} completed an especially strong day` });
    } else if (performance < 38) {
      state.reputation = Math.max(0, state.reputation - 1);
      addLedgerEntry(state, { kind: 'employee-performance', accepted: false, employeeId: employee.id, totalCents: 0, reason: `${employee.name}'s assignment produced a documented service error` });
    }
  }
  const clerkPower = employeeRolePower(state, 'clerk');
  state.shopTrust = clamp(0.2, 1, state.shopTrust + clerkPower * 0.0007);
}

function horticultureIncomeForDay(state, day) {
  const season = seasonForDay(day).id;
  const seen = new Set();
  let baseCents = 0;
  const active = [];
  for (const decoration of state.decorations) {
    const accessory = ACCESSORIES.find((item) => item.id === decoration.accessoryId);
    if (!accessory?.horticulture || seen.has(accessory.id)) continue;
    seen.add(accessory.id);
    const tree = treeLifecycleSnapshot(state, decoration, accessory);
    const seasonalBps = accessory.horticulture.peakSeason === season ? accessory.horticulture.peakBps : 10000;
    const incomeCents = Math.round((tree?.dailyCents ?? accessory.horticulture.dailyCents) * seasonalBps / 10000);
    baseCents += incomeCents;
    active.push({ accessoryId: accessory.id, incomeCents, effect: accessory.horticulture.effect });
  }
  if (baseCents <= 0) return { totalCents: 0, active };
  const gardenerPower = employeeRolePower(state, 'gardener');
  const processingBps = horticultureSupport(state).processingBps;
  const multiplierBps = Math.min(18000, 10000 + Math.round(gardenerPower * 900) + processingBps);
  const totalCents = Math.round(baseCents * multiplierBps / 10000);
  state.cashCents += totalCents;
  addLedgerEntry(state, { kind: 'horticulture-income', accepted: true, totalCents, reason: `Gardens and horticultural improvements earned ${cashLabel(totalCents)}` });
  return { totalCents, active };
}

function addMarketGardenProduce(state, plot, operation, requestedQty, harvestKind) {
  const facility = FACILITY_BY_ID[plot.facilityId];
  const good = getGood(facility.garden.goodId);
  const roomQty = Math.max(0, Math.floor((storageCapacity(state) - inventoryBulk(state) - reservedBulk(state)) / good.bulk));
  const qty = Math.min(requestedQty, roomQty);
  if (qty > 0) {
    const item = state.inventory[good.id];
    const oldValue = item.avgCostCents * item.qty;
    item.qty += qty;
    item.avgCostCents = Math.round(oldValue / item.qty);
    addPerishableBatch(state, good.id, qty);
  }
  operation.lastYieldUnits = qty;
  operation.totalYieldUnits += qty;
  addLedgerEntry(state, {
    kind: 'garden-harvest',
    accepted: qty > 0,
    plotIndex: plot.index,
    goodId: good.id,
    qty,
    unitCents: 0,
    totalCents: 0,
    reason: qty > 0 ? `${harvestKind} market-garden harvest added ${qty} ${good.name}` : `${harvestKind} market-garden harvest had no storage room`,
  });
}

function processMarketGardensForDay(state, day) {
  const year = gameYearForDay(day);
  const season = seasonForDay(day).id;
  const support = horticultureSupport(state);
  for (const plot of state.plots) {
    const facility = FACILITY_BY_ID[plot.facilityId];
    if (!plot.owned || plot.construction || !facility?.garden) continue;
    const key = String(plot.index);
    const operation = state.horticulture.operations[key] || {
      plotIndex: plot.index,
      plantedYear: null,
      improvedSeedYear: null,
      lastHarvestYear: null,
      lastProtectedHarvestYear: null,
      totalHarvests: 0,
      totalYieldUnits: 0,
      lastYieldUnits: 0,
    };
    state.horticulture.operations[key] = operation;
    const greenhouse = greenhouseOnPlot(state, plot.index);
    operation.phase = gardenPhaseForDay(day, greenhouse);
    operation.lastProcessedDay = day;
    operation.greenhouseProtected = greenhouse;

    if (season === 'spring' && operation.plantedYear !== year) {
      operation.plantedYear = year;
      operation.improvedSeedYear = null;
      if (state.inventory.seeds.qty > 0) {
        state.inventory.seeds.qty -= 1;
        if (state.inventory.seeds.qty === 0) state.inventory.seeds.avgCostCents = 0;
        operation.improvedSeedYear = year;
      }
      addLedgerEntry(state, { kind: 'garden-planted', accepted: true, plotIndex: plot.index, goodId: facility.garden.goodId, qty: 0, totalCents: 0, reason: operation.improvedSeedYear === year ? 'Market Garden planted with selected seed stock' : 'Market Garden planted from its saved seed reserve' });
    }

    const harvest = (protectedHarvest) => {
      const greenhouseBps = greenhouse ? 2500 : 0;
      const seedBps = operation.improvedSeedYear === year ? 1500 : 0;
      const gardenerBps = Math.min(3000, Math.round(employeeRolePower(state, 'gardener', 'gardens') * 600));
      const windmillBps = support.yieldBps + Math.floor(support.waterBps / 2);
      const seasonBps = protectedHarvest ? 5500 : 10000;
      const qty = Math.max(1, Math.round(facility.garden.baseYieldUnits * (10000 + greenhouseBps + seedBps + gardenerBps + windmillBps) * seasonBps / 100_000_000));
      addMarketGardenProduce(state, plot, operation, qty, protectedHarvest ? 'Protected winter' : 'Autumn');
      operation.totalHarvests += 1;
    };

    if (season === 'autumn' && operation.plantedYear === year && operation.lastHarvestYear !== year) {
      harvest(false);
      operation.lastHarvestYear = year;
    }
    if (season === 'winter' && greenhouse && operation.plantedYear === year && operation.lastProtectedHarvestYear !== year) {
      harvest(true);
      operation.lastProtectedHarvestYear = year;
    }
  }
  state.horticulture.lastProcessedDay = day;
}

function productiveFacilityOperation(state, plot, facility, day) {
  const key = String(plot.index);
  let operation = state.production.operations[key];
  if (!operation || operation.facilityId !== facility.id) {
    operation = {
      plotIndex: plot.index,
      facilityId: facility.id,
      progressDays: 0,
      cyclesCompleted: 0,
      lastProcessedDay: day - 1,
      lastCompletedDay: null,
      lastStatus: 'idle',
      lastOutput: {},
    };
    state.production.operations[key] = operation;
  }
  return operation;
}

function productionStorageFits(state, recipe) {
  const inputBulk = Object.entries(recipe.inputs).reduce((sum, [goodId, qty]) => sum + getGood(goodId).bulk * qty, 0);
  const outputBulk = Object.entries(recipe.outputs).reduce((sum, [goodId, qty]) => sum + getGood(goodId).bulk * qty, 0);
  return inventoryBulk(state) + reservedBulk(state) - inputBulk + outputBulk <= storageCapacity(state);
}

function processProductiveLandForDay(state, day) {
  const activePlotKeys = new Set();
  for (const plot of state.plots) {
    const facility = FACILITY_BY_ID[plot.facilityId];
    if (!plot.owned || plot.construction || !facility?.production) continue;
    const key = String(plot.index);
    activePlotKeys.add(key);
    const operation = productiveFacilityOperation(state, plot, facility, day);
    operation.lastProcessedDay = day;
    if (plot.condition <= 20 || plot.damage.some((damage) => damage.severity >= 3)) {
      operation.progressDays = 0;
      operation.lastStatus = 'halted-damage';
      continue;
    }
    const missingInputs = Object.entries(facility.production.inputs)
      .filter(([goodId, qty]) => state.inventory[goodId].qty < qty)
      .map(([goodId]) => goodId);
    if (missingInputs.length > 0) {
      operation.progressDays = 0;
      operation.lastStatus = 'waiting-inputs';
      continue;
    }
    if (!productionStorageFits(state, facility.production)) {
      operation.lastStatus = 'waiting-storage';
      continue;
    }
    operation.progressDays += 1;
    operation.lastStatus = 'working';
    if (operation.progressDays < facility.production.cycleDays) continue;

    let inputValueCents = 0;
    for (const [goodId, qty] of Object.entries(facility.production.inputs)) {
      const item = state.inventory[goodId];
      inputValueCents += item.avgCostCents * qty;
      item.qty -= qty;
      consumePerishableBatches(state, goodId, qty);
      if (item.qty === 0) item.avgCostCents = 0;
    }
    const totalOutputBaseValue = Object.entries(facility.production.outputs)
      .reduce((sum, [goodId, qty]) => sum + getGood(goodId).baseCents * qty, 0);
    for (const [goodId, qty] of Object.entries(facility.production.outputs)) {
      const item = state.inventory[goodId];
      const oldValueCents = item.avgCostCents * item.qty;
      const allocatedInputCents = Math.round(inputValueCents * getGood(goodId).baseCents * qty / totalOutputBaseValue);
      item.qty += qty;
      item.avgCostCents = Math.round((oldValueCents + allocatedInputCents) / item.qty);
      addPerishableBatch(state, goodId, qty);
      state.market[goodId].flowUnitsToday += qty;
    }
    operation.progressDays = 0;
    operation.cyclesCompleted += 1;
    operation.lastCompletedDay = day;
    operation.lastStatus = 'produced';
    operation.lastOutput = { ...facility.production.outputs };
    addLedgerEntry(state, {
      kind: 'production',
      accepted: true,
      plotIndex: plot.index,
      facilityId: facility.id,
      inputs: { ...facility.production.inputs },
      outputs: { ...facility.production.outputs },
      totalCents: 0,
      reason: `${facility.name} completed a production cycle`,
    });
  }
  Object.keys(state.production.operations).forEach((key) => {
    if (!activePlotKeys.has(key)) delete state.production.operations[key];
  });
  state.production.lastProcessedDay = day;
}

function casinoOperation(state, plot, day = currentDay(state)) {
  const key = String(plot.index);
  let operation = state.casino.operations[key];
  if (!operation || operation.facilityId !== plot.facilityId) {
    operation = {
      plotIndex: plot.index,
      facilityId: plot.facilityId,
      lastProcessedDay: day - 1,
      visitorsToday: 0,
      wealthUnitsToday: 0,
      lastVisitors: 0,
      lastGrossWagersCents: 0,
      lastPayoutsCents: 0,
      lastOperatingCostCents: 0,
      lastHouseResultCents: 0,
      lastNetCents: 0,
      lifetimeVisitors: 0,
      lifetimeGrossWagersCents: 0,
      lifetimePayoutsCents: 0,
      lifetimeOperatingCostCents: 0,
      lifetimeNetCents: 0,
      winningDays: 0,
      losingDays: 0,
      daysOpen: 0,
      status: 'open',
      lastBlockReason: null,
    };
    state.casino.operations[key] = operation;
  }
  return operation;
}

function casinoOperatingBlockReason(state, plot) {
  const facility = FACILITY_BY_ID[plot?.facilityId];
  if (!plot || !plot.owned || facility?.id !== 'river-casino') return 'Build a River Casino & Gambling Hall on an owned non-shop plot';
  if (plot.construction) return 'Finish casino construction before opening tables';
  if (plot.condition < 40 || plot.damage.some((damage) => damage.severity >= 3)) return 'Repair severe casino property damage before reopening';
  if (state.reputation < facility.casino.minReputation) return `Reach ${facility.casino.minReputation} reputation before opening casino tables`;
  if (townSafety(state) < facility.casino.minSafety) return `Raise town safety to ${facility.casino.minSafety} before opening casino tables`;
  return null;
}

function recordCasinoVisitors(state, visitor) {
  const candidates = matchingFacilityPlots(state, TRAFFIC_BY_ID[visitor.typeId])
    .filter(({ facility, plot }) => facility.casino && !casinoOperatingBlockReason(state, plot));
  if (candidates.length === 0) return;
  const selected = candidates.length === 1 ? candidates[0] : candidates[randomInt(state, 0, candidates.length - 1)];
  const operation = casinoOperation(state, selected.plot);
  operation.visitorsToday += visitor.groupSize;
  operation.wealthUnitsToday += visitor.groupSize * visitor.wealth;
}

function processCasinosForDay(state, day) {
  const activeKeys = new Set();
  for (const plot of state.plots) {
    const facility = FACILITY_BY_ID[plot.facilityId];
    if (!plot.owned || plot.construction || !facility?.casino) continue;
    const key = String(plot.index);
    activeKeys.add(key);
    const operation = casinoOperation(state, plot, day);
    const blockReason = casinoOperatingBlockReason(state, plot);
    operation.lastProcessedDay = day;
    if (blockReason) {
      operation.status = 'blocked';
      operation.lastBlockReason = blockReason;
      operation.lastVisitors = operation.visitorsToday;
      operation.visitorsToday = 0;
      operation.wealthUnitsToday = 0;
      operation.lastGrossWagersCents = 0;
      operation.lastPayoutsCents = 0;
      operation.lastOperatingCostCents = 0;
      operation.lastHouseResultCents = 0;
      operation.lastNetCents = 0;
      continue;
    }

    const visitors = operation.visitorsToday;
    const averageWealth = visitors > 0 ? operation.wealthUnitsToday / visitors : 0;
    const synergyBps = clamp(6000, 14000, 7000 + state.reputation * 4 + townSafety(state) * 35);
    const wealthBps = 10000 + Math.min(4000, Math.round(averageWealth * 1500));
    const grossWagersCents = Math.round(visitors * facility.casino.averageStakeCents * synergyBps * wealthBps / 1e8);
    const expectedHouseCents = Math.round(grossWagersCents * facility.casino.houseEdgeBps / 10000);
    const varianceCents = Math.round(grossWagersCents * facility.casino.varianceBps / 10000);
    const houseResultCents = expectedHouseCents + randomInt(state, -varianceCents, varianceCents);
    const payoutsCents = Math.max(0, grossWagersCents - houseResultCents);
    const operatingCostCents = facility.casino.tableOperatingCents;
    const rawNetCents = houseResultCents - operatingCostCents;
    const appliedNetCents = rawNetCents >= 0 ? rawNetCents : -Math.min(state.cashCents, -rawNetCents);
    state.cashCents += appliedNetCents;
    if (appliedNetCents > 0) operation.winningDays += 1;
    else if (appliedNetCents < 0) operation.losingDays += 1;
    if (appliedNetCents !== rawNetCents) state.reputation = Math.max(0, state.reputation - 2);
    operation.status = appliedNetCents === rawNetCents ? 'open' : 'underfunded';
    operation.lastBlockReason = appliedNetCents === rawNetCents ? null : 'House losses exceeded available operating cash';
    operation.lastVisitors = visitors;
    operation.lastGrossWagersCents = grossWagersCents;
    operation.lastPayoutsCents = payoutsCents;
    operation.lastOperatingCostCents = operatingCostCents;
    operation.lastHouseResultCents = houseResultCents;
    operation.lastNetCents = appliedNetCents;
    operation.lifetimeVisitors += visitors;
    operation.lifetimeGrossWagersCents += grossWagersCents;
    operation.lifetimePayoutsCents += payoutsCents;
    operation.lifetimeOperatingCostCents += operatingCostCents;
    operation.lifetimeNetCents += appliedNetCents;
    operation.daysOpen += 1;
    operation.visitorsToday = 0;
    operation.wealthUnitsToday = 0;
    addLedgerEntry(state, {
      kind: 'casino-house',
      accepted: appliedNetCents >= 0,
      plotIndex: plot.index,
      facilityId: facility.id,
      visitors,
      grossWagersCents,
      payoutsCents,
      operatingCostCents,
      houseResultCents,
      houseCents: appliedNetCents,
      playerCents: 0,
      totalCents: appliedNetCents,
      reason: appliedNetCents >= 0 ? 'Casino house closed the day ahead after payouts and table costs' : 'Casino house closed the day behind after payouts and table costs',
    });
  }
  Object.keys(state.casino.operations).forEach((key) => {
    if (!activeKeys.has(key)) delete state.casino.operations[key];
  });
  state.casino.lastProcessedDay = day;
}

function interiorMaturitySnapshot(state, plot) {
  if (!plot.facilityId || !Number.isInteger(plot.builtMinute)) return null;
  const ageMinutes = Math.max(0, state.timeMinutes - plot.builtMinute);
  const ageDays = Math.floor(ageMinutes / DAY_MINUTES);
  const productionOperation = state.production.operations[String(plot.index)];
  const productionCycles = productionOperation?.facilityId === plot.facilityId ? productionOperation.cyclesCompleted : 0;
  const serviceVisits = plot.serviceActivityCount;
  const shopTrades = plot.index === 3 ? state.ledger.filter((entry) => ['buy', 'sell'].includes(entry.kind) && entry.minute >= plot.builtMinute).length : 0;
  const activityCount = productionCycles + serviceVisits + shopTrades;
  const completeFromActivity = ageDays >= 14 && activityCount >= 4;
  const completeFromElapsedTime = ageDays >= 45;
  const stage = completeFromActivity || completeFromElapsedTime ? 'complete'
    : ageDays >= 3 || activityCount >= 1 ? 'intermediate'
      : 'beginner';
  const daysToNextStage = stage === 'beginner' ? Math.max(0, 3 - ageDays)
    : stage === 'intermediate' ? Math.max(0, (activityCount >= 4 ? 14 : 45) - ageDays)
      : 0;
  return {
    stage,
    builtMinute: plot.builtMinute,
    ageMinutes,
    ageDays,
    activityCount,
    productionCycles,
    serviceVisits,
    shopTrades,
    daysToNextStage,
    completeFromActivity,
    completeFromElapsedTime,
  };
}

function interiorOpportunityCatalog(facilityId) {
  return INTERIOR_OPPORTUNITIES[facilityId] || null;
}

function createInteriorOpportunityRecord(state, plot, cycle = 1, availableMinute = state.timeMinutes, lastResolution = null) {
  const catalog = interiorOpportunityCatalog(plot.facilityId);
  if (!catalog) return null;
  const opportunityId = `interior-opportunity-${state.interiors.nextOpportunityId++}`;
  return {
    opportunityId,
    plotIndex: plot.index,
    facilityId: plot.facilityId,
    cycle,
    seed: forkSeed(state.seed, `${opportunityId}:${plot.index}:${plot.facilityId}:${cycle}`),
    choiceIds: catalog.choices.map((choice) => choice.id),
    status: availableMinute <= state.timeMinutes ? 'available' : 'cooldown',
    createdMinute: state.timeMinutes,
    availableMinute,
    lastResolution,
  };
}

function ensureInteriorOpportunities(state) {
  if (!state.interiors || typeof state.interiors !== 'object') state.interiors = { nextOpportunityId: 1, byPlot: {}, history: [] };
  if (!Number.isInteger(state.interiors.nextOpportunityId) || state.interiors.nextOpportunityId < 1) state.interiors.nextOpportunityId = 1;
  if (!state.interiors.byPlot || typeof state.interiors.byPlot !== 'object') state.interiors.byPlot = {};
  if (!Array.isArray(state.interiors.history)) state.interiors.history = [];
  Object.keys(state.interiors.byPlot).forEach((key) => {
    const plot = state.plots[Number(key) - 1];
    const record = state.interiors.byPlot[key];
    if (!plot?.owned || plot.construction || !interiorOpportunityCatalog(plot.facilityId) || record?.facilityId !== plot.facilityId) delete state.interiors.byPlot[key];
  });
  state.plots.forEach((plot) => {
    if (!plot.owned || plot.construction || !interiorOpportunityCatalog(plot.facilityId)) return;
    const key = String(plot.index);
    if (!state.interiors.byPlot[key]) state.interiors.byPlot[key] = createInteriorOpportunityRecord(state, plot);
    const record = state.interiors.byPlot[key];
    if (record.status === 'cooldown' && record.availableMinute <= state.timeMinutes) record.status = 'available';
  });
}

function interiorOutcome(record, choice) {
  const holder = { rngState: forkSeed(record.seed, choice.id) };
  return pickWeighted(holder, choice.outcomes, (outcome) => outcome.weight);
}

function inventoryQuantityBlockReason(state, quantities, label) {
  for (const [goodId, qty] of Object.entries(quantities || {})) {
    if (state.inventory[goodId].qty < qty) return `Need ${qty} ${getGood(goodId).name} ${label}`;
  }
  return null;
}

function interiorChoiceBlockReason(state, plot, record, choice, outcome = interiorOutcome(record, choice)) {
  if (!plot?.owned || plot.construction || plot.facilityId !== record.facilityId) return 'The building is not operating';
  if (record.status !== 'available' || record.availableMinute > state.timeMinutes) return `Opportunity returns in ${Math.max(0, record.availableMinute - state.timeMinutes)} minutes`;
  if (plot.condition <= 20 || plot.damage.some((damage) => damage.severity >= 3)) return 'Repair severe property damage before using the interior';
  if (state.cashCents < choice.cashCostCents) return `Need ${cashLabel(choice.cashCostCents - state.cashCents)} more`;
  const requirementBlock = inventoryQuantityBlockReason(state, choice.requires, 'on hand');
  if (requirementBlock) return requirementBlock;
  const inputBlock = inventoryQuantityBlockReason(state, choice.inputs, 'to commit');
  if (inputBlock) return inputBlock;
  const inputBulk = Object.entries(choice.inputs || {}).reduce((sum, [goodId, qty]) => sum + getGood(goodId).bulk * qty, 0);
  const outputBulk = Object.entries(outcome.outputs || {}).reduce((sum, [goodId, qty]) => sum + getGood(goodId).bulk * qty, 0);
  if (inventoryBulk(state) + reservedBulk(state) - inputBulk + outputBulk > storageCapacity(state)) return 'Not enough storage for this opportunity’s resolved goods';
  return null;
}

function interiorChoiceView(state, plot, record, choice) {
  const outcome = interiorOutcome(record, choice);
  const blockReason = interiorChoiceBlockReason(state, plot, record, choice, outcome);
  const outcomeWeight = choice.outcomes.reduce((sum, candidate) => sum + candidate.weight, 0);
  return {
    choiceId: choice.id,
    name: choice.name,
    description: choice.description,
    cashCostCents: choice.cashCostCents,
    cooldownMinutes: choice.cooldownMinutes,
    inputs: Object.entries(choice.inputs || {}).map(([goodId, qty]) => ({ goodId, name: getGood(goodId).name, qty, availableQty: state.inventory[goodId].qty })),
    requirements: Object.entries(choice.requires || {}).map(([goodId, qty]) => ({ goodId, name: getGood(goodId).name, qty, availableQty: state.inventory[goodId].qty })),
    possibleOutcomes: choice.outcomes.map((candidate) => ({
      outcomeId: candidate.id,
      label: candidate.label,
      probabilityBps: Math.round(candidate.weight * 10000 / outcomeWeight),
      cashRewardCents: candidate.cashRewardCents || 0,
      outputs: Object.entries(candidate.outputs || {}).map(([goodId, qty]) => ({ goodId, name: getGood(goodId).name, qty })),
      reputationDelta: candidate.reputationDelta || 0,
      conditionDelta: candidate.conditionDelta || 0,
    })),
    canResolve: blockReason === null,
    blockReason,
  };
}

function interiorOpportunityView(state, record) {
  const plot = state.plots[record.plotIndex - 1];
  const catalog = interiorOpportunityCatalog(record.facilityId);
  return {
    opportunityId: record.opportunityId,
    plotIndex: record.plotIndex,
    facilityId: record.facilityId,
    facilityName: FACILITY_BY_ID[record.facilityId].name,
    title: catalog.title,
    cycle: record.cycle,
    status: record.status,
    createdMinute: record.createdMinute,
    availableMinute: record.availableMinute,
    cooldownRemainingMinutes: Math.max(0, record.availableMinute - state.timeMinutes),
    lastResolution: record.lastResolution ? { ...record.lastResolution } : null,
    choices: catalog.choices.filter((choice) => record.choiceIds.includes(choice.id)).map((choice) => interiorChoiceView(state, plot, record, choice)),
  };
}

function propertyRisks(state, plot, day = currentDay(state)) {
  if (!plot.owned) return { crimeBps: 0, fireBps: 0, stormBps: 0 };
  const facility = FACILITY_BY_ID[plot.facilityId];
  const vacantPressure = facility ? 0 : 90 + plot.neglectDays * 18;
  const conditionPressure = Math.max(0, 70 - plot.condition) * 9;
  const damagePressure = plot.damage.length * 45;
  const safety = townSafety(state);
  const guardMitigation = employeeRolePower(state, 'guard') * 80;
  const investigatorMitigation = employeeRolePower(state, 'investigator') * 35;
  const wardenMitigation = employeeRolePower(state, 'fire-warden') * 100 + fireProtection(state) * 10;
  const chimneyPressure = (facility?.chimney || 0) * 35 + (facility?.productionFireRiskBps || 0);
  const season = seasonForDay(day).id;
  const stormSeasonPressure = season === 'spring' ? 130 : season === 'winter' ? 90 : 35;
  const riverExposure = facility?.kind === 'river' ? 65 : 0;
  const treeRisk = state.decorations
    .filter((decoration) => decoration.plotIndex === plot.index)
    .map((decoration) => treeLifecycleSnapshot(state, decoration))
    .filter(Boolean)
    .reduce((sum, tree) => ({ fireBps: sum.fireBps + tree.fireRiskBps, stormBps: sum.stormBps + tree.stormRiskBps }), { fireBps: 0, stormBps: 0 });
  const greenhouseProtection = greenhouseOnPlot(state, plot.index) ? 45 : 0;
  return {
    crimeBps: Math.round(clamp(5, 1800, 30 + vacantPressure + conditionPressure + damagePressure + Math.max(0, 65 - safety) * 7 - guardMitigation - investigatorMitigation)),
    fireBps: Math.round(clamp(4, 1500, 25 + vacantPressure * 0.55 + conditionPressure + chimneyPressure + damagePressure + treeRisk.fireBps - wardenMitigation)),
    stormBps: Math.round(clamp(5, 1300, stormSeasonPressure + riverExposure + conditionPressure * 0.7 + treeRisk.stormBps - greenhouseProtection - dockSafetyBonus(state) * 15)),
  };
}

function applyPropertyDamage(state, plot, source) {
  const candidates = DAMAGE_TYPES.filter((type) => type.sources.includes(source));
  const unused = candidates.filter((type) => !plot.damage.some((damage) => damage.typeId === type.id));
  const type = pick(state, unused.length ? unused : candidates);
  const severity = randomInt(state, 1, 3);
  const conditionLoss = type.conditionLoss + (severity - 1) * 3;
  const damage = {
    id: `damage-${state.nextDamageId++}`,
    typeId: type.id,
    source,
    severity,
    createdMinute: state.timeMinutes,
    conditionLoss,
    repairCostCents: type.baseRepairCents * severity,
  };
  plot.damage.push(damage);
  if (plot.damage.length > 8) plot.damage.shift();
  plot.condition = Math.max(0, plot.condition - conditionLoss);
  plot.neglectDays += source === 'neglect' ? 2 : 1;
  if (source === 'crime') {
    addLedgerEntry(state, { kind: 'crime', accepted: false, plotIndex: plot.index, damageId: damage.id, totalCents: 0, reason: `A documented property crime damaged Plot ${plot.index}` });
  }
  addLedgerEntry(state, {
    kind: 'property-damage',
    accepted: false,
    plotIndex: plot.index,
    damageId: damage.id,
    damageType: damage.typeId,
    source,
    totalCents: 0,
    reason: `${type.name} recorded on Plot ${plot.index} after ${source}`,
  });
}

function processPropertiesForDay(state, day) {
  for (const plot of state.plots) {
    if (!plot.owned) continue;
    const vacant = !plot.facilityId;
    if (vacant || plot.condition < 60) plot.neglectDays += 1;
    else plot.neglectDays = Math.max(0, plot.neglectDays - 1);
    if (vacant && day % 3 === 0) plot.condition = Math.max(0, plot.condition - 1);
    else if (!vacant && day % 30 === 0) plot.condition = Math.max(0, plot.condition - 1);

    if (plot.neglectDays >= 10 && !plot.damage.some((damage) => damage.source === 'neglect') && chance(state, 0.2)) {
      applyPropertyDamage(state, plot, 'neglect');
      continue;
    }
    const risks = propertyRisks(state, plot, day);
    if (chance(state, risks.fireBps / 10000)) applyPropertyDamage(state, plot, 'fire');
    else if (chance(state, risks.stormBps / 10000)) applyPropertyDamage(state, plot, 'storm');
    else if (chance(state, risks.crimeBps / 10000)) applyPropertyDamage(state, plot, 'crime');
  }
}

function vendorProfile(profileId) {
  return VENDOR_PROFILES.find((profile) => profile.id === profileId);
}

function meetingRelationship(state, profileId) {
  if (!state.meetings.relationships[profileId]) {
    state.meetings.relationships[profileId] = {
      score: 0,
      encounters: 0,
      accepted: 0,
      declined: 0,
      counters: 0,
      lastMinute: null,
    };
  }
  return state.meetings.relationships[profileId];
}

function meetingIsVisible(policy, offer) {
  if (offer.mandatory) return true;
  if (policy === 'none') return false;
  if (policy === 'all') return true;
  if (policy === 'highlighted') return offer.highlighted || offer.rare;
  if (policy === 'high-value') return offer.totalCents >= 2500;
  return offer.standard || offer.highlighted || offer.rare || offer.tutorial;
}

function meetingBlockReason(state, offer) {
  if (!offer || offer.status !== 'pending') return 'Offer is no longer pending';
  if (offer.expiresMinute != null && offer.expiresMinute <= state.timeMinutes) return 'Offer has expired';
  const good = getGood(offer.goodId);
  if (!good) return 'Offer references an unknown good';
  const facilityBlockReason = goodFacilityBlockReason(state, good);
  if (facilityBlockReason) return facilityBlockReason;
  if (offer.direction === 'buy') {
    if (state.cashCents < offer.totalCents) return 'Not enough cash for this meeting offer';
    if (inventoryBulk(state) + reservedBulk(state) + good.bulk * offer.qty > storageCapacity(state)) return 'Storage is full';
    if (good.coldChain && coldStorageCapacity(state) <= 0) return 'Fresh fish requires an operating Ice House';
    if (good.coldChain && offer.qty > availableColdStorage(state)) return 'Ice House cold storage is full';
    if (good.id === 'gold-dust' && state.dailyGoldBought + offer.qty > 2) return 'Daily rare-gold limit reached';
  } else if (offer.direction === 'sell') {
    const visitor = findVisitor(state, offer.visitorId);
    if (visitor?.goodsSoldToPost?.includes(offer.goodId)) return 'Same-stop buyback is not allowed';
    if (state.inventory[offer.goodId].qty < offer.qty) return 'Not enough inventory for this meeting offer';
  } else {
    return 'Offer direction is invalid';
  }
  return null;
}

function archiveMeeting(state, offer, status) {
  offer.status = status;
  offer.resolvedMinute = state.timeMinutes;
  state.meetings.pending = state.meetings.pending.filter((candidate) => candidate.id !== offer.id);
  if (status === 'expired') {
    state.meetings.expired.push(offer);
    if (state.meetings.expired.length > 100) state.meetings.expired.splice(0, state.meetings.expired.length - 100);
  } else {
    state.meetings.history.push(offer);
    if (state.meetings.history.length > 150) state.meetings.history.splice(0, state.meetings.history.length - 150);
  }
}

function createMeetingOffer(state, details) {
  const profile = vendorProfile(details.profileId);
  const relationship = meetingRelationship(state, profile.id);
  relationship.encounters += 1;
  relationship.lastMinute = state.timeMinutes;
  const offer = {
    id: `meeting-${state.meetings.nextOfferId++}`,
    visitorId: details.visitorId,
    profileId: profile.id,
    vendorName: profile.name,
    vendorRole: profile.role,
    homeRegion: profile.homeRegion || null,
    tradeNote: profile.tradeNote || null,
    personality: profile.personality,
    portrait: { ...profile.portrait },
    conversation: { ...profile.conversation },
    conversationLine: profile.conversation.greeting,
    direction: details.direction,
    goodId: details.goodId,
    qty: details.qty,
    unitCents: details.unitCents,
    totalCents: details.unitCents * details.qty,
    createdMinute: state.timeMinutes,
    expiresMinute: details.expiresMinute,
    status: 'pending',
    source: details.source || 'visitor',
    tutorial: Boolean(details.tutorial),
    mandatory: Boolean(details.mandatory),
    highlighted: Boolean(profile.highlighted),
    rare: Boolean(profile.rare),
    standard: Boolean(details.standard),
    counterCount: 0,
    patienceRemaining: profile.patience,
    lastCounterCents: null,
  };
  state.meetings.pending.push(offer);
  if (state.meetings.pending.length > 12) {
    const displaced = state.meetings.pending.shift();
    displaced.conversationLine = 'The crowded meeting book let this offer lapse.';
    archiveMeeting(state, displaced, 'expired');
  }
  return offer;
}

function createTutorialMeeting(state) {
  return createMeetingOffer(state, {
    visitorId: 'tutorial-trapper',
    profileId: 'fur-trader-eli',
    direction: 'buy',
    goodId: 'pelts',
    qty: 2,
    unitCents: 250,
    expiresMinute: null,
    source: 'tutorial',
    tutorial: true,
    mandatory: true,
    standard: true,
  });
}

function maybeCreateVisitorMeeting(state, visitor) {
  if (!openingComplete(state)) return;
  if (visitor.meetingOfferGenerated || state.meetings.pending.length >= 12) return;
  visitor.meetingOfferGenerated = true;
  const selection = selectRegionalVendor(VENDOR_PROFILES, { seed: state.seed, rank: state.rank, typeId: visitor.typeId, profileCycle: state.meetings.profileCycle });
  const profile = selection?.profile;
  if (!profile) return;
  const probability = profile.rare ? 0.55 : profile.highlighted ? 0.4 : 0.24;
  if (!chance(state, probability)) return;
  const candidates = [
    ...visitor.cargo.filter((entry) => entry.qty > 0).map((entry) => ({ direction: 'buy', goodId: entry.goodId, qty: Math.min(3, entry.qty), unitCents: entry.askCents })),
    ...visitor.needs
      .filter((entry) => entry.qty > 0 && !visitor.goodsSoldToPost?.includes(entry.goodId))
      .map((entry) => ({ direction: 'sell', goodId: entry.goodId, qty: Math.min(3, entry.qty), unitCents: Math.min(entry.willingnessCents, Math.floor(entry.budgetCents / Math.max(1, Math.min(3, entry.qty)))) })),
  ].filter((candidate) => Number.isInteger(candidate.qty) && candidate.qty > 0 && Number.isInteger(candidate.unitCents) && candidate.unitCents > 0);
  if (candidates.length === 0) return;
  const artSpecialists = new Set(['gallery-margot', 'collector-adelaide', 'museum-everett']);
  const artworkCandidate = candidates.find((candidate) => candidate.goodId === 'artwork');
  const candidate = artSpecialists.has(profile.id) && artworkCandidate ? { ...artworkCandidate } : selectVendorOffer(profile, candidates, { seed: state.seed, visitorId: visitor.id });
  if (profile.personality === 'swindler') {
    candidate.unitCents = roundTo5(candidate.unitCents * (candidate.direction === 'buy' ? 1.3 : 0.72));
  }
  createMeetingOffer(state, {
    visitorId: visitor.id,
    profileId: profile.id,
    ...candidate,
    expiresMinute: state.timeMinutes + vendorOfferLifetime(profile),
    source: 'visitor',
    standard: profile.highlighted || chance(state, 0.3),
  });
  state.meetings.profileCycle = selection.profileCycle;
}

function processMeetings(state) {
  const expired = state.meetings.pending.filter((offer) => !offer.mandatory && offer.expiresMinute <= state.timeMinutes);
  for (const offer of expired) {
    offer.conversationLine = 'The visitor moved on while the town continued around them.';
    archiveMeeting(state, offer, 'expired');
    addLedgerEntry(state, {
      kind: 'meeting-expired',
      accepted: false,
      meetingId: offer.id,
      visitorId: offer.visitorId,
      goodId: offer.goodId,
      qty: offer.qty,
      unitCents: offer.unitCents,
      totalCents: 0,
      reason: `${offer.vendorName}'s optional offer expired`,
    });
  }
}

function seasonForDay(day) {
  return SEASONS[Math.floor((day - 1) / 8) % SEASONS.length];
}

function gameYearForDay(day) {
  return Math.floor((day - 1) / 32) + 1;
}

function treeLifecycleSnapshot(state, decoration, accessory = ACCESSORIES.find((item) => item.id === decoration.accessoryId)) {
  if (!accessory?.treeLifecycle) return null;
  const placedMinute = Number.isInteger(decoration.placedMinute) ? decoration.placedMinute : START_MINUTE;
  const ageDays = Math.max(0, Math.floor((state.timeMinutes - placedMinute) / DAY_MINUTES));
  const stages = accessory.treeLifecycle.stages;
  let stageIndex = 0;
  for (let index = 0; index < stages.length; index += 1) {
    if (ageDays >= stages[index].minAgeDays) stageIndex = index;
  }
  const stage = stages[stageIndex];
  const nextStage = stages[stageIndex + 1] || null;
  const spanDays = nextStage ? nextStage.minAgeDays - stage.minAgeDays : null;
  const progressBps = nextStage ? clamp(0, 10000, Math.floor((ageDays - stage.minAgeDays) * 10000 / spanDays)) : 10000;
  return {
    stageId: stage.id,
    stageIndex,
    ageDays,
    ageYears: Math.floor(ageDays / accessory.treeLifecycle.gameYearDays),
    nextStageId: nextStage?.id || null,
    daysToNextStage: nextStage ? Math.max(0, nextStage.minAgeDays - ageDays) : null,
    progressBps,
    visualScale: stage.visualScale,
    visualFrame: stage.visualFrame,
    dailyCents: stage.dailyCents,
    stormRiskBps: stage.stormRiskBps,
    fireRiskBps: stage.fireRiskBps,
    identity: { ...stage.identity },
  };
}

function horticultureSupport(state) {
  const windmill = state.plots.find((plot) => plot.owned && !plot.construction && plot.facilityId === 'working-windmill');
  const support = windmill ? FACILITY_BY_ID['working-windmill'].gardenSupport : null;
  return {
    windmillPlotIndex: windmill?.index || null,
    yieldBps: support?.yieldBps || 0,
    processingBps: support?.processingBps || 0,
    waterBps: support?.waterBps || 0,
  };
}

function greenhouseOnPlot(state, plotIndex) {
  return state.decorations.some((decoration) => decoration.plotIndex === plotIndex && decoration.accessoryId === 'market-greenhouse');
}

function gardenPhaseForDay(day, protectedSeason = false) {
  const season = seasonForDay(day).id;
  if (season === 'spring') return 'planting';
  if (season === 'summer') return 'growing';
  if (season === 'autumn') return 'harvest';
  return protectedSeason ? 'protected-harvest' : 'fallow';
}

function activeEventPriceBps(state, goodId, day = currentDay(state)) {
  return state.events.reduce((sum, event) => {
    if (event.startDay <= day && event.endDay >= day) {
      return sum + (EVENT_TYPES.find((type) => type.id === event.typeId)?.priceBps?.[goodId] || 0);
    }
    return sum;
  }, 0);
}

function activeTrafficBoost(state, typeId, day = currentDay(state)) {
  return state.events.reduce((sum, event) => {
    if (event.startDay <= day && event.endDay >= day) {
      const type = EVENT_TYPES.find((item) => item.id === event.typeId);
      return sum + (type?.traffic?.includes(typeId) ? 10 : 0);
    }
    return sum;
  }, 0);
}

function updateMarketForDay(state, day) {
  const season = seasonForDay(day).id;
  const targetFlowMilli = (6 + state.rank * 2) * 1000;

  GOODS.forEach((good) => {
    const market = state.market[good.id];
    market.flowEmaMilli = Math.round(market.flowEmaMilli * 0.7 + market.flowUnitsToday * 300);
    market.flowUnitsToday = 0;
    market.noiseBps = Math.round(market.noiseBps * 0.75 + randomInt(state, -600, 600));
    const scarcityBps = clamp(
      -2000,
      3000,
      Math.round((1800 * (targetFlowMilli - market.flowEmaMilli)) / Math.max(1000, targetFlowMilli)),
    );
    const seasonBps = (good.season[season] || 0) * 100;
    const eventBps = activeEventPriceBps(state, good.id, day);
    const factorBps = clamp(5500, 18000, 10000 + scarcityBps + seasonBps + eventBps + market.noiseBps);
    market.priceCents = roundTo5((good.baseCents * factorBps) / 10000);
    if (state.inventory[good.id].pricingMode === 'market') applyMarketPricing(state, good.id);
  });
}

function updateRank(state) {
  const day = currentDay(state);
  let nextRank = 0;
  for (const rank of RANKS) {
    if (state.reputation >= rank.reputation && state.transactions >= rank.trades && day >= rank.day) {
      nextRank = rank.level;
    }
  }
  if (nextRank !== state.rank) {
    state.rank = nextRank;
    addLedgerEntry(state, { kind: 'rank', accepted: true, reason: RANKS[nextRank].name });
  }
}

function maybeStartEvent(state, day) {
  state.events = state.events.filter((event) => event.endDay >= day);
  if (day <= 2 || day % 4 !== 0 || !chance(state, 0.48)) return;
  const activeIds = new Set(state.events.map((event) => event.typeId));
  const eligible = EVENT_TYPES.filter((event) => {
    if (activeIds.has(event.id)) return false;
    return Object.keys(event.priceBps).some((goodId) => getGood(goodId).rank <= state.rank + 1);
  });
  if (eligible.length === 0) return;
  const type = pickWeighted(state, eligible, (event) => event.chanceWeight);
  state.events.push({
    id: `event-${state.nextEventId++}`,
    typeId: type.id,
    startDay: day + 1,
    endDay: day + type.durationDays,
    forecast: true,
  });
}

function payDailyUpkeep(state) {
  const upkeep = state.plots.reduce((sum, plot) => sum + (FACILITY_BY_ID[plot.facilityId]?.upkeepCents || 0), 0);
  if (upkeep <= 0) return;
  const paid = Math.min(state.cashCents, upkeep);
  state.cashCents -= paid;
  addLedgerEntry(state, {
    kind: 'upkeep',
    accepted: paid === upkeep,
    totalCents: -paid,
    reason: paid === upkeep ? 'Daily wages and upkeep' : 'Upkeep partly unpaid',
  });
  if (paid < upkeep) state.reputation = Math.max(0, state.reputation - 1);
}

function armRecoveryIfNeeded(state) {
  if (!openingComplete(state)) return;
  if (state.cashCents < 200 && inventoryBulk(state) === 0 && !state.recovery.active) {
    state.recovery = { active: true, arrivalsRemaining: 2 };
  }
}

function processShelfRotationForDay(state, day) {
  if (!openingComplete(state)) return;
  const lastCommerceMinute = state.ledger.reduce((latest, entry) => (
    ['buy', 'sell', 'meeting-buy', 'meeting-sell', 'stock-liquidation'].includes(entry.kind) ? Math.max(latest, entry.minute) : latest
  ), state.opening.completedMinute || START_MINUTE);
  const stagnantDays = Math.max(0, day - (Math.floor(lastCommerceMinute / DAY_MINUTES) + 1));
  const capacity = storageCapacity(state);
  const fullnessBps = Math.floor((inventoryBulk(state) + reservedBulk(state)) * 10000 / Math.max(1, capacity));
  if (stagnantDays < 3 || fullnessBps < 7500) return;

  let changed = false;
  GOODS.forEach((good) => {
    const item = state.inventory[good.id];
    if (item.displayed && item.qty === 0) {
      item.displayed = false;
      changed = true;
    }
  });
  let availableSlots = displaySlots(state) - GOODS.filter((good) => state.inventory[good.id].displayed).length;
  const rotationCandidates = GOODS
    .filter((good) => good.rank <= state.rank && state.inventory[good.id].qty > 0 && !state.inventory[good.id].displayed && !goodFacilityBlockReason(state, good))
    .sort((left, right) => state.inventory[right.id].qty * right.bulk - state.inventory[left.id].qty * left.bulk || left.id.localeCompare(right.id));
  for (const good of rotationCandidates) {
    if (availableSlots <= 0) break;
    state.inventory[good.id].displayed = true;
    availableSlots -= 1;
    changed = true;
  }
  if (changed) {
    addLedgerEntry(state, { kind: 'shelf-rotation', accepted: true, totalCents: 0, reason: 'Slow-moving stocked goods rotated onto open display space' });
  }

  if (stagnantDays < 7 || fullnessBps < 9000) return;
  const liquidationGood = GOODS
    .filter((good) => state.inventory[good.id].qty > 0
      && !good.perishable
      && !good.tags.some((tag) => ['rare', 'import', 'luxury'].includes(tag))
      && !goodFacilityBlockReason(state, good))
    .sort((left, right) => state.inventory[right.id].qty * right.bulk - state.inventory[left.id].qty * left.bulk || left.id.localeCompare(right.id))[0];
  if (!liquidationGood) return;
  const item = state.inventory[liquidationGood.id];
  const unitCents = roundTo5(state.market[liquidationGood.id].priceCents * 0.6);
  item.qty -= 1;
  if (item.qty === 0) item.avgCostCents = 0;
  state.cashCents += unitCents;
  state.market[liquidationGood.id].flowUnitsToday += 1;
  state.transactions += 1;
  state.reputation += 1;
  addLedgerEntry(state, {
    kind: 'stock-liquidation',
    accepted: true,
    goodId: liquidationGood.id,
    qty: 1,
    unitCents,
    totalCents: unitCents,
    reason: 'One slow-moving unit sold to a bulk peddler at a conservative clearance price',
  });
}

function dailyUpdate(state, day) {
  updateRank(state);
  maybeStartEvent(state, day);
  updateMarketForDay(state, day);
  payDailyUpkeep(state);
  processEmployeeDay(state, day);
  processMarketGardensForDay(state, day);
  processProductiveLandForDay(state, day);
  processCasinosForDay(state, day);
  horticultureIncomeForDay(state, day);
  processShelfRotationForDay(state, day);
  processPropertiesForDay(state, day);
  armRecoveryIfNeeded(state);
  state.dailyServiceCents = 0;
  state.dailyGoldBought = 0;
}

function offerPrice(state, good, supplyFactorBps = randomInt(state, 5800, 10600)) {
  const reputationDiscountBps = Math.min(1200, Math.floor(state.reputation / 12));
  const qualityBps = randomInt(state, 8800, 11400);
  const urgencyBps = randomInt(state, 8200, 11000);
  return roundTo5(
    (state.market[good.id].priceCents * supplyFactorBps * qualityBps * urgencyBps * (10000 - reputationDiscountBps))
      / 1e16,
  );
}

function willingnessPrice(state, good, type) {
  const needBps = randomInt(state, 9000, 15000);
  const wealthBps = [7800, 10500, 13800][type.wealth] || 10000;
  const urgencyBps = randomInt(state, 10000, 13500);
  return roundTo5((state.market[good.id].priceCents * needBps * wealthBps * urgencyBps) / 1e12);
}

function unlockedGoodsFor(state, ids, tier = 2) {
  return ids
    .map((id) => getGood(id))
    .filter((good) => good && good.rank <= state.rank && good.tier <= tier);
}

function createVisitor(state, type, arrivalMinute, overrides = {}) {
  const supplyGoods = unlockedGoodsFor(state, type.supply, 2);
  const demandGoods = unlockedGoodsFor(state, type.demand, type.wealth);
  const cargo = supplyGoods
    .filter((good) => chance(state, (good.trafficSupplyChanceBps ?? 7200) / 10000))
    .slice(0, 2)
    .map((good) => ({
      goodId: good.id,
      qty: randomInt(state, 1, type.supplyQtyMax || (type.id.includes('freight') || type.id.includes('caravan') ? 8 : 4)),
      askCents: Math.max(
        offerPrice(state, good),
        type.supplyPriceFloorBps ? roundTo5(state.market[good.id].priceCents * type.supplyPriceFloorBps / 10000) : 0,
      ),
    }));
  const needs = demandGoods
    .filter((good) => chance(state, (good.trafficDemandChanceBps ?? 7800) / 10000))
    .slice(0, 3)
    .map((good) => ({
      goodId: good.id,
      qty: randomInt(state, 1, type.wealth === 2 ? 4 : 3),
      willingnessCents: willingnessPrice(state, good, type),
      budgetCents: Math.round(
        randomInt(state, 2, 6)
        * state.market[good.id].priceCents
        * (type.budgetMultiplierBps || 10000)
        / 10000,
      ),
    }));

  if (cargo.length === 0 && needs.length === 0 && supplyGoods.length > 0) {
    const good = pick(state, supplyGoods);
    cargo.push({ goodId: good.id, qty: 1, askCents: offerPrice(state, good) });
  }
  if (cargo.length === 0 && needs.length === 0 && demandGoods.length > 0) {
    const good = pick(state, demandGoods);
    needs.push({ goodId: good.id, qty: 1, willingnessCents: willingnessPrice(state, good, type), budgetCents: good.baseCents * 3 });
  }

  const visitor = {
    id: `visitor-${state.nextVisitorId++}`,
    typeId: type.id,
    name: type.name,
    lane: type.lane,
    origin: type.origin || (type.lane === 'river' ? 'Upper River' : 'Overland Trail'),
    wealth: type.wealth,
    activity: type.activity || null,
    groupSize: randomInt(state, 1, type.wealth === 2 ? 6 : 4),
    arrivalMinute,
    departureMinute: arrivalMinute + 30,
    expiresMinute: arrivalMinute + 210,
    status: 'scheduled',
    direction: 'incoming',
    stopReasons: [],
    cargo,
    needs,
    goodsSoldToPost: [],
    tradeResolved: false,
    forcedStop: false,
    ...overrides,
  };
  visitor.stopReasons = overrides.stopReasons || [
    ...(visitor.cargo.some((offer) => offer.qty > 0) ? ['SELL_CARGO'] : []),
    ...(visitor.needs.some((need) => need.qty > 0) ? ['BUY_GOOD'] : []),
    ...(visitor.activity === 'fishing' ? ['FISHING'] : []),
  ];
  return visitor;
}

function scheduleTutorialTraffic(state) {
  const trapper = createVisitor(state, TRAFFIC_BY_ID['trapper-canoe'], START_MINUTE + 60, {
    id: 'tutorial-trapper',
    cargo: [],
    needs: [],
    forcedStop: true,
    tradeResolved: true,
  });
  state.traffic.push(trapper);
}

function scheduleOpeningOutfitter(state) {
  if (state.traffic.some((visitor) => visitor.id === 'tutorial-outfitter')) return;
  const outfitter = createVisitor(state, TRAFFIC_BY_ID['keelboat'], state.timeMinutes + (state.opening.marketCircuitVersion >= 2 ? 20 : 180), {
    id: 'tutorial-outfitter',
    cargo: [],
    needs: [{ goodId: 'pelts', qty: 2, willingnessCents: 600, budgetCents: 1200 }],
    forcedStop: true,
  });
  state.traffic.push(outfitter);
  state.traffic.sort((left, right) => left.arrivalMinute - right.arrivalMinute || left.id.localeCompare(right.id));
}

// A finite local market circuit makes the first working day teach the full
// buy/display/sell loop. These are real, paid trades with finite parcels and
// budgets, not milestone rewards. Older saves never receive this circuit.
function scheduleOpeningMarketCircuit(state) {
  if (![1, 2].includes(state.opening.marketCircuitVersion) || state.opening.marketCircuitScheduled) return;
  state.opening.marketCircuitScheduled = true;
  for (let visit = 0; visit < 8; visit += 1) {
    const arrivalMinute = state.timeMinutes + 80 + visit * 180;
    const cargo = STARTER_SHELF_GOODS.map((goodId) => ({
      goodId,
      qty: 3,
      askCents: roundTo5(state.market[goodId].priceCents * (0.67 + randomInt(state, 0, 7) / 100)),
    }));
    const needs = STARTER_SHELF_GOODS.map((goodId) => ({
      goodId,
      qty: 3,
      willingnessCents: roundTo5(state.market[goodId].priceCents * 1.18),
      budgetCents: roundTo5(state.market[goodId].priceCents * 1.18) * 3,
    }));
    state.traffic.push(createVisitor(state, TRAFFIC_BY_ID.keelboat, arrivalMinute, {
      id: `opening-market-supplier-${visit + 1}`, name: 'Local supply boat', cargo, needs: [],
      forcedStop: true, openingMarketCircuit: true,
    }));
    state.traffic.push(createVisitor(state, TRAFFIC_BY_ID['settler-wagon'], arrivalMinute + 90, {
      id: `opening-market-customer-${visit + 1}`, name: 'Local market customer', cargo: [], needs,
      forcedStop: true, openingMarketCircuit: true,
    }));
  }
  state.traffic.sort((left, right) => left.arrivalMinute - right.arrivalMinute || left.id.localeCompare(right.id));
}

function scheduleDay(state, dayIndex) {
  const rankAtScheduling = state.rank;
  const count = 10 + rankAtScheduling * 3 + randomInt(state, 0, 5);
  const eligible = TRAFFIC_TYPES.filter((type) => type.rank <= rankAtScheduling);
  for (let index = 0; index < count; index += 1) {
    const type = pickWeighted(state, eligible, (candidate) => candidate.weight + activeTrafficBoost(state, candidate.id, dayIndex + 1));
    const minuteOfDay = randomInt(state, 6 * 60, 21 * 60);
    const arrivalMinute = dayIndex * DAY_MINUTES + minuteOfDay;
    if (arrivalMinute <= state.timeMinutes + 20) continue;
    state.traffic.push(createVisitor(state, type, arrivalMinute));
  }
  const popularity = townPopularity(state);
  if (state.rank >= 3 && popularity >= 45) {
    const ambientCount = clamp(1, 5, Math.floor((popularity - 30) / 15));
    const comfort = publicComfort(state);
    for (let index = 0; index < ambientCount; index += 1) {
      const arrivalMinute = dayIndex * DAY_MINUTES + randomInt(state, 8 * 60, 19 * 60);
      if (arrivalMinute <= state.timeMinutes + 20) continue;
      state.ambientActors.push({
        id: `ambient-${state.nextAmbientId++}`,
        kind: 'unhoused-straggler',
        label: 'Resting traveler',
        arrivalMinute,
        departureMinute: arrivalMinute + randomInt(state, comfort > 0 ? 240 : 90, comfort > 0 ? 600 : 240),
        expiresMinute: arrivalMinute + randomInt(state, comfort > 0 ? 660 : 300, comfort > 0 ? 840 : 420),
        status: 'scheduled',
        pose: 'walking',
        comfort: clamp(0, 5, comfort),
        interactable: false,
        clickable: false,
        tradeEligible: false,
        countsForTraffic: false,
        scores: false,
      });
    }
  }
  state.traffic.sort((left, right) => left.arrivalMinute - right.arrivalMinute || left.id.localeCompare(right.id));
  state.ambientActors.sort((left, right) => left.arrivalMinute - right.arrivalMinute || left.id.localeCompare(right.id));
}

function ensureSchedule(state) {
  const finalDay = Math.floor((state.timeMinutes + SCHEDULE_AHEAD_MINUTES) / DAY_MINUTES);
  while (state.nextScheduleDay <= finalDay) {
    scheduleDay(state, state.nextScheduleDay);
    state.nextScheduleDay += 1;
  }
}

function facilityMatches(type, facility) {
  return facility
    && facility.kind !== 'shop'
    && (facility.visitorWealthMin == null || type.wealth >= facility.visitorWealthMin)
    && (facility.serviceLane === type.lane || facility.serviceTags?.includes(type.id));
}

function matchingFacilityPlots(state, type) {
  return state.plots
    .filter((plot, index) => index !== 2 && !plot.construction)
    .map((plot) => ({ plot, facility: FACILITY_BY_ID[plot.facilityId] }))
    .filter(({ facility }) => facilityMatches(type, facility));
}

function matchingFacilities(state, type) {
  return matchingFacilityPlots(state, type).map(({ facility }) => facility);
}

function visitorReasons(state, visitor) {
  const reasons = [];
  if (visitor.cargo.some((offer) => offer.qty > 0)) reasons.push('SELL_CARGO');
  if (visitor.needs.some((need) => {
    const stock = state.inventory[need.goodId];
    return need.qty > 0 && stock.displayed && stock.qty > 0;
  })) reasons.push('BUY_GOOD');
  if (matchingFacilities(state, TRAFFIC_BY_ID[visitor.typeId]).length > 0) reasons.push('USE_FACILITY');
  if (visitor.activity === 'fishing') reasons.push('FISHING');
  if (visitor.forcedStop) reasons.push('RELATIONSHIP');
  return reasons;
}

function facilityStopBonus(state, visitor) {
  const facilities = matchingFacilities(state, TRAFFIC_BY_ID[visitor.typeId]);
  return Math.min(0.36, facilities.reduce((sum, facility) => sum
    + (facility.stopBonusBps || 0) / 10000
    + (visitor.lane === 'road' ? (facility.roadTravelBonusBps || 0) / 10000 : 0), 0));
}

function maybeAcquireGalleryArtwork(state, visitor, plot, facility) {
  const curation = facility.curation;
  if (!curation) return;
  const opportunityRoll = forkSeed(state.seed, `gallery-curation:${visitor.id}:${plot.index}:${plot.serviceActivityCount}`) % 10000;
  if (opportunityRoll >= curation.chanceBps) return;
  const good = getGood(curation.goodId);
  const item = state.inventory[curation.goodId];
  const costSpan = curation.maxCostBps - curation.minCostBps + 1;
  const costBps = curation.minCostBps + forkSeed(state.seed, `gallery-cost:${visitor.id}:${plot.index}`) % costSpan;
  const unitCents = roundTo5(state.market[curation.goodId].priceCents * costBps / 10000);
  let reason = null;
  if (good.rank > state.rank) reason = `${good.name} is not unlocked`;
  else if (goodFacilityBlockReason(state, good)) reason = goodFacilityBlockReason(state, good);
  else if (item.qty >= curation.maxStock) reason = `Gallery curation stock is limited to ${curation.maxStock}`;
  else if (item.buyCeilingCents <= 0) reason = 'Artwork buying is disabled';
  else if (unitCents > item.buyCeilingCents) reason = 'Curator price is above the artwork buy ceiling';
  else if (inventoryBulk(state) + reservedBulk(state) + good.bulk > storageCapacity(state)) reason = 'Storage is full';
  else if (state.cashCents < unitCents) reason = 'Not enough cash for the curated artwork';
  if (reason) {
    addLedgerEntry(state, {
      kind: 'gallery-curation',
      accepted: false,
      visitorId: visitor.id,
      plotIndex: plot.index,
      facilityId: facility.id,
      goodId: good.id,
      qty: 1,
      unitCents,
      totalCents: 0,
      reason,
    });
    return;
  }
  const oldValue = item.avgCostCents * item.qty;
  state.cashCents -= unitCents;
  item.qty += 1;
  item.avgCostCents = Math.round((oldValue + unitCents) / item.qty);
  state.market[good.id].flowUnitsToday += 1;
  state.transactions += 1;
  state.reputation += 2;
  addLedgerEntry(state, {
    kind: 'gallery-curation',
    accepted: true,
    visitorId: visitor.id,
    plotIndex: plot.index,
    facilityId: facility.id,
    goodId: good.id,
    qty: 1,
    unitCents,
    totalCents: -unitCents,
    reason: `${facility.name} paid to acquire one curated work from ${visitor.name}`,
  });
}

function resolveServiceIncome(state, visitor) {
  const type = TRAFFIC_BY_ID[visitor.typeId];
  matchingFacilityPlots(state, type).forEach(({ facility, plot }) => {
    let incomeBps = 10000;
    if (facility.reputationIncomeThreshold) {
      incomeBps = clamp(1500, 11000, Math.round(1500 + 8500 * state.reputation / facility.reputationIncomeThreshold));
    }
    if (facility.hotelSynergyBps && state.plots.some((candidate) => FACILITY_BY_ID[candidate.facilityId]?.kind === 'hospitality')) {
      incomeBps += facility.hotelSynergyBps;
    }
    if (facility.safetySensitive) incomeBps = Math.round(incomeBps * clamp(4000, 11000, townSafety(state) * 120) / 10000);
    if (facility.eventIncomeTags?.some((eventId) => state.events.some((event) => event.typeId === eventId && event.startDay <= currentDay(state) && event.endDay >= currentDay(state)))) incomeBps += 2500;
    if (facility.kind === 'river') incomeBps += dockServiceBonusBps(state, facility.id) + Math.round(employeeRolePower(state, 'dockhand', 'river') * 650);
    if (['hospitality', 'prestige', 'entertainment'].includes(facility.kind)) incomeBps += Math.round(employeeRolePower(state, 'host', 'hospitality') * 550);
    const conditionBps = Math.max(2500, (plot?.condition ?? 100) * 100);
    const gross = Math.round(facility.serviceCents * Math.min(4, visitor.groupSize) * incomeBps * conditionBps / 1e8);
    // Services deliberately remain secondary to shop trade.
    const dailyCap = Math.max(500, Math.round(state.cashCents * 0.35) + 500);
    const amount = Math.max(0, Math.min(gross, dailyCap - state.dailyServiceCents));
    if (amount <= 0) return;
    const repairInput = facility.repairService;
    if (repairInput) {
      const item = state.inventory[repairInput.inputGoodId];
      if (item.qty < repairInput.inputQty) {
        addLedgerEntry(state, {
          kind: 'service-refused',
          accepted: false,
          visitorId: visitor.id,
          plotIndex: plot.index,
          facilityId: facility.id,
          goodId: repairInput.inputGoodId,
          qty: repairInput.inputQty,
          totalCents: 0,
          reason: `${facility.name} lacked ${getGood(repairInput.inputGoodId).name} for ${repairInput.label}`,
        });
        return;
      }
      item.qty -= repairInput.inputQty;
      consumePerishableBatches(state, repairInput.inputGoodId, repairInput.inputQty);
      if (item.qty === 0) item.avgCostCents = 0;
      state.market[repairInput.inputGoodId].flowUnitsToday += repairInput.inputQty;
    }
    state.cashCents += amount;
    state.dailyServiceCents += amount;
    plot.serviceActivityCount += 1;
    addLedgerEntry(state, {
      kind: 'service',
      accepted: true,
      visitorId: visitor.id,
      plotIndex: plot.index,
      facilityId: facility.id,
      inputGoodId: repairInput?.inputGoodId || null,
      inputQty: repairInput?.inputQty || 0,
      totalCents: amount,
      reason: repairInput ? `${visitor.name} paid for ${repairInput.label} at ${facility.name}` : `${visitor.name} used ${facility.name}`,
    });
    maybeAcquireGalleryArtwork(state, visitor, plot, facility);
  });
}

function refuse(state, visitor, kind, goodId, reason, unitCents, qty = 0) {
  addLedgerEntry(state, {
    kind: 'refused',
    dealKind: kind,
    accepted: false,
    visitorId: visitor.id,
    goodId,
    qty,
    unitCents,
    totalCents: 0,
    reason,
  });
}

function automaticDeals(state, visitor) {
  if (!openingComplete(state)) return;
  if (visitor.tradeResolved) return;
  visitor.tradeResolved = true;
  if (!Array.isArray(visitor.goodsSoldToPost)) visitor.goodsSoldToPost = [];
  const goodsSoldToPost = new Set(visitor.goodsSoldToPost);

  for (const offer of visitor.cargo) {
    if (offer.qty <= 0) continue;
    const item = state.inventory[offer.goodId];
    const good = getGood(offer.goodId);
    const negotiatedAskCents = effectiveVendorAskCents(state, offer.askCents);
    const facilityBlockReason = goodFacilityBlockReason(state, good);
    if (facilityBlockReason) {
      refuse(state, visitor, 'buy', offer.goodId, facilityBlockReason, offer.askCents, offer.qty);
      continue;
    }
    if (good.coldChain && coldStorageCapacity(state) <= 0) {
      refuse(state, visitor, 'buy', offer.goodId, 'Fresh fish requires an operating Ice House', offer.askCents, offer.qty);
      continue;
    }
    if (item.buyCeilingCents <= 0) {
      refuse(state, visitor, 'buy', offer.goodId, 'Buying disabled', offer.askCents, offer.qty);
      continue;
    }
    if (negotiatedAskCents > item.buyCeilingCents) {
      refuse(state, visitor, 'buy', offer.goodId, 'Offer above buy ceiling', negotiatedAskCents, offer.qty);
      continue;
    }
    const roomQty = Math.floor((storageCapacity(state) - inventoryBulk(state) - reservedBulk(state)) / good.bulk);
    const coldRoomQty = good.coldChain ? availableColdStorage(state) : offer.qty;
    const stockRoomQty = Number.isInteger(item.autoStockTargetQty) ? Math.max(0, item.autoStockTargetQty - item.qty) : offer.qty;
    const reserveCents = item.autoStockCashReserveCents || 0;
    const affordableQty = Math.floor(Math.max(0, state.cashCents - reserveCents) / negotiatedAskCents);
    const rareLimit = offer.goodId === 'gold-dust' ? Math.max(0, 2 - state.dailyGoldBought) : offer.qty;
    const quantity = Math.min(offer.qty, roomQty, coldRoomQty, stockRoomQty, affordableQty, rareLimit);
    if (quantity <= 0) {
      const reason = roomQty <= 0 ? 'Storage full'
        : coldRoomQty <= 0 ? 'Ice House cold storage is full'
        : stockRoomQty <= 0 ? 'Working shelf restock target reached'
        : affordableQty <= 0 ? reserveCents && state.cashCents >= negotiatedAskCents ? 'Working cash reserve protected' : 'Not enough cash'
          : 'Daily rare-gold limit reached';
      refuse(state, visitor, 'buy', offer.goodId, reason, negotiatedAskCents, offer.qty);
      continue;
    }
    transact(state, visitor.id, 'buy', offer.goodId, quantity);
    goodsSoldToPost.add(offer.goodId);
  }

  for (const need of visitor.needs) {
    if (need.qty <= 0) continue;
    if (goodsSoldToPost.has(need.goodId)) {
      refuse(state, visitor, 'sell', need.goodId, 'Same-stop buyback is not allowed', state.inventory[need.goodId].listPriceCents, need.qty);
      continue;
    }
    const item = state.inventory[need.goodId];
    const good = getGood(need.goodId);
    const facilityBlockReason = goodFacilityBlockReason(state, good);
    if (facilityBlockReason) {
      refuse(state, visitor, 'sell', need.goodId, facilityBlockReason, item.listPriceCents, need.qty);
      continue;
    }
    if (!item.displayed || item.qty <= 0) {
      refuse(state, visitor, 'sell', need.goodId, 'Not displayed or out of stock', item.listPriceCents, need.qty);
      continue;
    }
    if (item.listPriceCents > need.willingnessCents) {
      refuse(state, visitor, 'sell', need.goodId, 'Price above willingness', item.listPriceCents, need.qty);
      continue;
    }
    const market = state.market[need.goodId].priceCents;
    const advantage = (need.willingnessCents - item.listPriceCents) / Math.max(1, market * 0.12);
    const probability = clamp(0.15, 0.97, (1 / (1 + Math.exp(-advantage))) * (0.8 + state.shopTrust * 0.2));
    // Prearranged opening customers honor their published price and budget.
    // Normal visitors still make their usual individual purchasing decision.
    if (!visitor.openingMarketCircuit && visitor.id !== 'tutorial-outfitter' && !chance(state, probability)) {
      refuse(state, visitor, 'sell', need.goodId, 'Buyer reconsidered', item.listPriceCents, need.qty);
      continue;
    }
    const quantity = Math.min(need.qty, item.qty, Math.floor(need.budgetCents / item.listPriceCents));
    if (quantity <= 0) {
      refuse(state, visitor, 'sell', need.goodId, 'Budget too small', item.listPriceCents, need.qty);
      continue;
    }
    transact(state, visitor.id, 'sell', need.goodId, quantity);
  }
}

function processRecoveryArrival(state) {
  if (!openingComplete(state) || !state.recovery.active) return;
  state.recovery.arrivalsRemaining -= 1;
  if (state.recovery.arrivalsRemaining > 0) return;
  const item = state.inventory.pelts;
  item.qty += 2;
  item.avgCostCents = 0;
  item.displayed = true;
  state.recovery = { active: false, arrivalsRemaining: 0 };
  addLedgerEntry(state, {
    kind: 'recovery',
    accepted: true,
    goodId: 'pelts',
    qty: 2,
    unitCents: 0,
    totalCents: 0,
    reason: 'Two consignment pelts supplied by the homestead grant',
  });
}

function resolveArrival(state, visitor) {
  const reasons = visitorReasons(state, visitor);
  visitor.stopReasons = reasons;
  const hasTrade = reasons.includes('BUY_GOOD') || reasons.includes('SELL_CARGO');
  const score = 0.05 + (hasTrade ? 0.38 : 0) + (reasons.includes('FISHING') ? 0.3 : 0) + facilityStopBonus(state, visitor) + (visitor.forcedStop ? 0.5 : 0);
  const stops = reasons.length > 0 && (visitor.forcedStop || chance(state, clamp(0.03, 0.92, score)));
  visitor.status = stops ? 'stopped' : 'passing';
  visitor.direction = stops ? 'at-post' : 'outgoing';
  visitor.departureMinute = state.timeMinutes + (stops ? randomInt(state, 45, 120) : 25);
  visitor.expiresMinute = visitor.departureMinute + 180;
  processRecoveryArrival(state);
  if (stops) {
    recordCasinoVisitors(state, visitor);
    resolveServiceIncome(state, visitor);
    automaticDeals(state, visitor);
    maybeCreateVisitorMeeting(state, visitor);
  }
}

function processTraffic(state) {
  for (const visitor of state.traffic) {
    if (visitor.status === 'scheduled' && visitor.arrivalMinute <= state.timeMinutes) {
      resolveArrival(state, visitor);
    } else if ((visitor.status === 'stopped' || visitor.status === 'passing') && visitor.departureMinute <= state.timeMinutes) {
      visitor.status = 'outgoing';
      visitor.direction = 'outgoing';
    } else if (visitor.status === 'outgoing' && visitor.expiresMinute <= state.timeMinutes) {
      visitor.status = 'expired';
    }
  }
  state.traffic = state.traffic.filter((visitor) => visitor.status !== 'expired');
}

function processAmbientActors(state) {
  for (const actor of state.ambientActors) {
    if (actor.status === 'scheduled' && actor.arrivalMinute <= state.timeMinutes) {
      actor.status = 'resting';
      actor.pose = actor.comfort > 0 ? 'sheltered-rest' : 'curb-rest';
    } else if (actor.status === 'resting' && actor.departureMinute <= state.timeMinutes) {
      actor.status = 'outgoing';
      actor.pose = 'walking';
    } else if (actor.status === 'outgoing' && actor.expiresMinute <= state.timeMinutes) {
      actor.status = 'expired';
    }
  }
  state.ambientActors = state.ambientActors.filter((actor) => actor.status !== 'expired');
}

function addPerishableBatch(state, goodId, qty) {
  const good = getGood(goodId);
  if (!good?.perishable || qty <= 0) return;
  const shelfLifeMinutes = good.coldChain ? refrigeratedShelfLife(state) : good.shelfLifeMinutes;
  state.inventory[goodId].perishableBatches.push({
    qty,
    acquiredMinute: state.timeMinutes,
    expiresMinute: state.timeMinutes + shelfLifeMinutes,
    refrigerated: Boolean(good.coldChain),
  });
  state.inventory[goodId].perishableBatches.sort((left, right) => left.expiresMinute - right.expiresMinute);
}

function consumePerishableBatches(state, goodId, qty) {
  const item = state.inventory[goodId];
  if (!Array.isArray(item.perishableBatches)) return;
  let remaining = qty;
  for (const batch of item.perishableBatches) {
    const used = Math.min(batch.qty, remaining);
    batch.qty -= used;
    remaining -= used;
    if (remaining === 0) break;
  }
  item.perishableBatches = item.perishableBatches.filter((batch) => batch.qty > 0);
}

function processPerishables(state) {
  for (const good of GOODS.filter((candidate) => candidate.perishable)) {
    const item = state.inventory[good.id];
    if (good.coldChain && coldStorageCapacity(state) === 0) {
      item.perishableBatches.forEach((batch) => {
        batch.refrigerated = false;
        batch.expiresMinute = Math.min(batch.expiresMinute, state.timeMinutes + good.shelfLifeMinutes);
      });
    }
    const expired = item.perishableBatches.filter((batch) => batch.expiresMinute <= state.timeMinutes);
    const spoiledQty = expired.reduce((sum, batch) => sum + batch.qty, 0);
    if (spoiledQty <= 0) continue;
    item.perishableBatches = item.perishableBatches.filter((batch) => batch.expiresMinute > state.timeMinutes);
    item.qty = Math.max(0, item.qty - spoiledQty);
    if (item.qty === 0) item.avgCostCents = 0;
    addLedgerEntry(state, {
      kind: 'spoilage',
      accepted: false,
      goodId: good.id,
      qty: spoiledQty,
      unitCents: 0,
      totalCents: 0,
      reason: `${good.name} spoiled after its ${good.coldChain ? 'refrigerated ' : ''}shelf life`,
    });
  }
}

function processOrders(state) {
  for (const order of state.orders) {
    if (order.status !== 'in-transit' || order.arrivalMinute > state.timeMinutes) continue;
    const item = state.inventory[order.goodId];
    const oldValue = item.avgCostCents * item.qty;
    item.qty += order.qty;
    item.avgCostCents = Math.round((oldValue + order.unitCents * order.qty) / item.qty);
    addPerishableBatch(state, order.goodId, order.qty);
    order.status = 'delivered';
    addLedgerEntry(state, {
      kind: 'delivery',
      accepted: true,
      orderId: order.id,
      goodId: order.goodId,
      qty: order.qty,
      unitCents: order.unitCents,
      totalCents: 0,
      routeId: order.routeId,
      reason: order.routeId === 'cuba' ? 'Havana import delivered' : 'Chicago freight delivered',
    });
  }
}

function finishConstruction(state, plot) {
  const completed = plot.construction;
  plot.facilityId = completed.facilityId;
  plot.builtMinute = state.timeMinutes;
  plot.serviceActivityCount = 0;
  plot.kind = FACILITY_BY_ID[completed.facilityId].kind;
  plot.construction = null;
  plot.condition = 100;
  plot.neglectDays = 0;
  plot.damage = [];
  if (plot.kind !== 'river') {
    state.dockTreatments.forEach((treatment) => {
      if (treatment.plotIndex === plot.index) {
        treatment.plotIndex = null;
        treatment.x = null;
        treatment.y = null;
      }
    });
  }
  state.labor.available = Math.min(state.labor.capacity, state.labor.available + completed.laborRequired);
  addLedgerEntry(state, {
    kind: 'construction-complete',
    accepted: true,
    plotIndex: plot.index,
    facilityId: completed.facilityId,
    reason: `${FACILITY_BY_ID[completed.facilityId].name} opened`,
  });
}

function advanceConstruction(state, elapsed) {
  for (const plot of state.plots) {
    const job = plot.construction;
    if (!job) continue;
    job.phaseProgressMinutes += elapsed;
    while (plot.construction && job.phaseProgressMinutes >= job.phaseDurationMinutes) {
      job.phaseProgressMinutes -= job.phaseDurationMinutes;
      if (job.phase >= job.phasesTotal) {
        finishConstruction(state, plot);
      } else {
        job.phase += 1;
        job.phaseName = PHASE_NAMES[job.phase - 1] || `phase-${job.phase}`;
      }
    }
  }
}

function syncSmokeEmitters(state) {
  const emitters = [];
  for (const plot of state.plots) {
    const facility = FACILITY_BY_ID[plot.facilityId];
    if (facility?.chimney) {
      emitters.push({
        id: `smoke-plot-${plot.index}`,
        kind: 'chimney',
        sourceId: `plot-${plot.index}`,
        plotIndex: plot.index,
        intensity: facility.chimney,
        active: !plot.construction,
      });
    }
  }
  for (const visitor of state.traffic) {
    const type = TRAFFIC_BY_ID[visitor.typeId];
    if (type?.smoke && ['passing', 'stopped', 'outgoing'].includes(visitor.status)) {
      emitters.push({
        id: `smoke-${visitor.id}`,
        kind: 'steamboat',
        sourceId: visitor.id,
        lane: visitor.lane,
        intensity: type.smoke,
        active: true,
      });
    }
  }
  state.smokeEmitters = emitters;
}

export function createInitialState(seed = 'trading-post') {
  const normalizedSeed = normalizeSeed(seed);
  const state = {
    version: 1,
    seed: normalizedSeed,
    rngState: normalizedSeed,
    timeMinutes: START_MINUTE,
    cashCents: 2200,
    reputation: 0,
    transactions: 0,
    affluentClientsServed: 0,
    rank: 0,
    shopTrust: 0.72,
    opening: {
      status: 'awaiting-fur-trader',
      completedMinute: null,
      transactionsAtCompletion: null,
      tutorialOfferId: null,
      marketCircuitVersion: 2,
    },
    market: {},
    inventory: {},
    plots: [1, 2, 3, 4, 5].map((index) => ({
      index,
      owned: index === 3,
      deedCostCents: PLOT_DEEDS[index].deedCostCents,
      deedPurchasedMinute: index === 3 ? START_MINUTE : null,
      kind: index === 3 ? 'shop' : 'nature',
      facilityId: index === 3 ? 'shop-shack' : null,
      builtMinute: index === 3 ? START_MINUTE : null,
      serviceActivityCount: 0,
      construction: null,
      condition: 100,
      neglectDays: 0,
      damage: [],
    })),
    labor: { available: 6, capacity: 6 },
    traffic: [],
    ambientActors: [],
    orders: [],
    events: [],
    ledger: [],
    decorations: [],
    dockTreatments: [],
    smokeEmitters: [],
    employees: {
      candidates: [],
      hired: [],
      history: [],
      pastCandidates: [],
      nextCandidateId: 1,
      nextEmployeeId: 1,
      nextRefreshDay: 1,
      lastAudit: null,
    },
    meetings: {
      policy: 'standard',
      pending: [],
      expired: [],
      history: [],
      relationships: {},
      profileCycle: [],
      nextOfferId: 1,
      lastResolution: null,
    },
    recovery: { active: false, arrivalsRemaining: 0 },
    horticulture: { lastProcessedDay: 1, operations: {} },
    production: { lastProcessedDay: 1, operations: {} },
    casino: { lastProcessedDay: 1, operations: {} },
    poker: { nextSessionId: 1, activeSession: null, history: [] },
    interiors: { nextOpportunityId: 1, byPlot: {}, history: [] },
    dailyServiceCents: 0,
    dailyGoldBought: 0,
    nextVisitorId: 1,
    nextAmbientId: 1,
    nextLedgerId: 1,
    nextOrderId: 1,
    nextEventId: 1,
    nextDecorationId: 1,
    nextDockTreatmentId: 1,
    nextDamageId: 1,
    nextScheduleDay: 0,
  };

  GOODS.forEach((good) => {
    state.market[good.id] = {
      priceCents: good.baseCents,
      noiseBps: 0,
      flowEmaMilli: 0,
      flowUnitsToday: 0,
    };
    state.inventory[good.id] = {
      qty: 0,
      avgCostCents: 0,
      listPriceCents: roundTo5(good.baseCents * 1.3),
      buyCeilingCents: STARTER_SHELF_GOODS.includes(good.id) ? roundTo5(good.baseCents * 0.72) : 0,
      displayed: false,
      perishableBatches: good.perishable ? [] : null,
    };
  });
  refreshEmployeeCandidates(state, currentDay(state), true);
  scheduleTutorialTraffic(state);
  ensureSchedule(state);
  state.opening.tutorialOfferId = createTutorialMeeting(state).id;
  syncSmokeEmitters(state);
  assertInvariants(state);
  return state;
}

// Hydrates the current v1 envelope without invalidating saves made before the
// mandatory opening trade existed. Progressed legacy towns stay progressed;
// untouched legacy starts adopt the revised opening.
export function hydrateState(state) {
  if (!state || typeof state !== 'object') throw new Error('Save state is invalid');
  if (!state.market || typeof state.market !== 'object') state.market = {};
  if (!state.inventory || typeof state.inventory !== 'object') state.inventory = {};
  GOODS.forEach((good) => {
    if (!state.market[good.id]) {
      state.market[good.id] = { priceCents: good.baseCents, noiseBps: 0, flowEmaMilli: 0, flowUnitsToday: 0 };
    }
    if (!state.inventory[good.id]) {
      state.inventory[good.id] = {
        qty: 0,
        avgCostCents: 0,
        listPriceCents: roundTo5(good.baseCents * 1.3),
        buyCeilingCents: roundTo5(good.baseCents * 0.72),
        displayed: false,
        perishableBatches: good.perishable ? [] : null,
      };
    }
  });
  const tutorialOffers = [
    ...(state.meetings?.pending || []),
    ...(state.meetings?.expired || []),
    ...(state.meetings?.history || []),
  ].filter((offer) => offer.tutorial || offer.profileId === 'fur-trader-eli' && offer.visitorId === 'tutorial-trapper');
  const tutorialTrapper = state.traffic?.find((visitor) => visitor.id === 'tutorial-trapper');
  (state.traffic || []).forEach((visitor) => {
    if (!Array.isArray(visitor.goodsSoldToPost)) visitor.goodsSoldToPost = [];
  });
  if (tutorialTrapper) {
    tutorialTrapper.cargo = [];
    tutorialTrapper.needs = [];
    tutorialTrapper.tradeResolved = true;
  }
  if (!state.horticulture || typeof state.horticulture !== 'object') state.horticulture = { lastProcessedDay: currentDay(state), operations: {} };
  if (!state.horticulture.operations || typeof state.horticulture.operations !== 'object') state.horticulture.operations = {};
  if (!Number.isInteger(state.horticulture.lastProcessedDay)) state.horticulture.lastProcessedDay = currentDay(state);
  if (!state.production || typeof state.production !== 'object') state.production = { lastProcessedDay: currentDay(state), operations: {} };
  if (!state.production.operations || typeof state.production.operations !== 'object') state.production.operations = {};
  if (!Number.isInteger(state.production.lastProcessedDay)) state.production.lastProcessedDay = currentDay(state);
  if (!state.casino || typeof state.casino !== 'object') state.casino = { lastProcessedDay: currentDay(state), operations: {} };
  if (!state.casino.operations || typeof state.casino.operations !== 'object') state.casino.operations = {};
  if (!Number.isInteger(state.casino.lastProcessedDay)) state.casino.lastProcessedDay = currentDay(state);
  hydratePokerState(state);
  (state.plots || []).forEach((plot) => {
    if (!plot.facilityId) {
      plot.builtMinute = null;
    } else if (!Number.isInteger(plot.builtMinute)) {
      plot.builtMinute = plot.index === 3
        ? START_MINUTE
        : Number.isInteger(plot.deedPurchasedMinute) ? plot.deedPurchasedMinute : state.timeMinutes;
    }
    if (!Number.isInteger(plot.serviceActivityCount) || plot.serviceActivityCount < 0) plot.serviceActivityCount = 0;
  });
  (state.decorations || []).forEach((decoration) => {
    if (!Number.isInteger(decoration.placedMinute)) decoration.placedMinute = START_MINUTE;
  });
  ensureInteriorOpportunities(state);

  if (!state.opening) {
    const inventoryProgress = GOODS.some((good) => (state.inventory?.[good.id]?.qty || 0) > 0);
    const progressed = state.timeMinutes > START_MINUTE
      || state.transactions > 0
      || inventoryProgress
      || tutorialOffers.some((offer) => ['accepted', 'declined', 'expired'].includes(offer.status));
    const acceptedTutorial = tutorialOffers.find((offer) => offer.status === 'accepted');
    if (progressed) {
      state.meetings.pending = state.meetings.pending.filter((offer) => !tutorialOffers.includes(offer));
      state.meetings.expired = state.meetings.expired.filter((offer) => !tutorialOffers.includes(offer));
      state.opening = {
        status: 'completed',
        completedMinute: acceptedTutorial?.resolvedMinute ?? START_MINUTE,
        transactionsAtCompletion: 0,
        tutorialOfferId: acceptedTutorial?.id || null,
        migratedLegacy: true,
      };
    } else {
      GOODS.forEach((good) => { state.inventory[good.id].displayed = false; });
      let tutorial = state.meetings.pending.find((offer) => offer.tutorial || offer.profileId === 'fur-trader-eli' && offer.visitorId === 'tutorial-trapper');
      if (!tutorial) tutorial = createTutorialMeeting(state);
      Object.assign(tutorial, { tutorial: true, mandatory: true, standard: true, expiresMinute: null });
      state.traffic = state.traffic.filter((visitor) => visitor.id !== 'tutorial-outfitter');
      state.opening = {
        status: 'awaiting-fur-trader',
        completedMinute: null,
        transactionsAtCompletion: null,
        tutorialOfferId: tutorial.id,
        migratedLegacy: true,
      };
    }
  }
  // A returning player who has not bought Eli's stock receives the same new
  // opening lesson. Established merchants retain their timeline and policies.
  if (state.opening.status === 'awaiting-fur-trader' && state.transactions === 0) {
    state.opening.marketCircuitVersion = 2;
  }
  return state;
}

export function setPrice(state, goodId, cents) {
  if (!getGood(goodId)) throw new RangeError(`Unknown good: ${goodId}`);
  if (!Number.isInteger(cents) || cents < 5 || cents > MAX_PRICE_CENTS) {
    throw new RangeError('Price must be between 5 silver coins and 10,000 notes');
  }
  state.inventory[goodId].listPriceCents = cents;
  state.inventory[goodId].pricingMode = 'fixed';
  return state;
}

export function setBuyCeiling(state, goodId, cents) {
  if (!getGood(goodId)) throw new RangeError(`Unknown good: ${goodId}`);
  if (!Number.isInteger(cents) || cents < 0 || cents > MAX_PRICE_CENTS) {
    throw new RangeError('Buying price must be between 0 notes and 10,000 notes');
  }
  state.inventory[goodId].buyCeilingCents = cents;
  state.inventory[goodId].pricingMode = 'fixed';
  // Price edits, including pausing and resuming buying, retain the player's
  // starter stock limit and working reserve. Goods without a preset remain
  // unrestricted, as before.
  return state;
}

// Existing prices remain fixed unless the merchant explicitly chooses market
// pricing. Validate the complete policy before changing any of its settings.
export function setRestockPolicy(state, goodId, policy = {}) {
  if (!getGood(goodId)) throw new RangeError(`Unknown good: ${goodId}`);
  if (!isPlainRecord(policy)) throw new TypeError('Restocking policy must be an object');
  const item = state.inventory[goodId];
  const targetQty = Object.hasOwn(policy, 'targetQty') ? policy.targetQty : item.autoStockTargetQty ?? null;
  const cashReserveCents = Object.hasOwn(policy, 'cashReserveCents') ? policy.cashReserveCents : item.autoStockCashReserveCents ?? 0;
  const pricingMode = Object.hasOwn(policy, 'pricingMode') ? policy.pricingMode : item.pricingMode ?? 'fixed';
  if (targetQty !== null && (!Number.isSafeInteger(targetQty) || targetQty < 0)) throw new RangeError('Restock target must be a nonnegative whole quantity or unlimited');
  if (!Number.isSafeInteger(cashReserveCents) || cashReserveCents < 0) throw new RangeError('Working cash reserve must be a nonnegative amount');
  if (!['market', 'fixed'].includes(pricingMode)) throw new RangeError('Pricing mode must be market or fixed');
  if (targetQty === null) delete item.autoStockTargetQty;
  else item.autoStockTargetQty = targetQty;
  item.autoStockCashReserveCents = cashReserveCents;
  item.pricingMode = pricingMode;
  if (pricingMode === 'market') applyMarketPricing(state, goodId);
  return state;
}

export function setDisplay(state, goodId, enabled) {
  const good = getGood(goodId);
  if (!good) throw new RangeError(`Unknown good: ${goodId}`);
  if (enabled && good.rank > state.rank) throw new Error(`${good.name} is not unlocked`);
  const facilityBlockReason = enabled ? goodFacilityBlockReason(state, good) : null;
  if (facilityBlockReason) throw new Error(facilityBlockReason);
  const item = state.inventory[goodId];
  if (enabled && !item.displayed) {
    const used = GOODS.filter((candidate) => state.inventory[candidate.id].displayed).length;
    if (used >= displaySlots(state)) throw new Error('No display slot available');
  }
  item.displayed = Boolean(enabled);
  return state;
}

export function setMeetingPolicy(state, policyId) {
  if (!MEETING_POLICIES.some((policy) => policy.id === policyId)) throw new RangeError(`Unknown meeting policy: ${policyId}`);
  state.meetings.policy = policyId;
  state.meetings.lastResolution = { outcome: 'policy-changed', policyId, minute: state.timeMinutes };
  return state;
}

// The first sale teaches one complete, paid trade before introducing an
// assortment or restocking. This is an explicit setup, never a load migration.
export function configureIntroShelf(state) {
  if (!openingComplete(state)) throw new Error('Complete Eli Mercer’s opening fur trade first');
  GOODS.forEach((good) => {
    const item = state.inventory[good.id];
    item.displayed = good.id === 'pelts';
    item.buyCeilingCents = 0;
    item.pricingMode = 'fixed';
    delete item.autoStockTargetQty;
    delete item.autoStockCashReserveCents;
  });
  const pelts = state.inventory.pelts;
  pelts.listPriceCents = Math.min(MAX_PRICE_CENTS, Math.max(roundTo5(state.market.pelts.priceCents * 1.05), pelts.avgCostCents + 5));
  state.opening.introShelfMinute = state.timeMinutes;
  addLedgerEntry(state, { kind: 'intro-shelf', accepted: true, totalCents: 0, reason: 'Opening pelts displayed at a fair price; automatic purchasing paused until the first sale' });
  assertInvariants(state);
  return state;
}

// Explicit player preset: preserves quantities and cash; no inventory is given
// away and no existing save is silently repriced when loaded.
export function configureStarterShelf(state) {
  if (!openingComplete(state)) throw new Error('Complete Eli Mercer’s opening fur trade first');
  GOODS.forEach((good) => {
    const item = state.inventory[good.id];
    const selected = STARTER_SHELF_GOODS.includes(good.id);
    item.displayed = selected;
    item.buyCeilingCents = 0;
    item.pricingMode = selected ? 'market' : 'fixed';
    if (selected) {
      item.autoStockTargetQty = 6;
      item.autoStockCashReserveCents = Math.min(500, Math.floor(state.cashCents / 2));
    } else {
      delete item.autoStockTargetQty;
      delete item.autoStockCashReserveCents;
    }
    if (selected) applyMarketPricing(state, good.id);
  });
  state.opening.workingShelfMinute = state.timeMinutes;
  addLedgerEntry(state, { kind: 'shelf-setup', accepted: true, totalCents: 0, reason: 'Working shelf follows the market for pelts, rope and provisions; other automatic buying paused' });
  assertInvariants(state);
  return state;
}

// A deliberate next assortment for a larger shop. The affordable staples
// remain central; small soap and boot targets leave room and cash for growth.
export function configureExpandedShelf(state) {
  if (!openingComplete(state)) throw new Error('Complete Eli Mercer’s opening fur trade first');
  const targets = { pelts: 6, rope: 6, provisions: 6, soap: 4, boots: 2 };
  if (displaySlots(state) < Object.keys(targets).length) throw new Error('Open a General Store with at least five display slots first');
  for (const goodId of Object.keys(targets)) {
    const good = getGood(goodId);
    if (good.rank > state.rank) throw new Error(`${good.name} requires a higher settlement rank`);
    const blockReason = goodFacilityBlockReason(state, good);
    if (blockReason) throw new Error(blockReason);
  }
  const reserve = Math.min(500, Math.floor(state.cashCents / 2));
  GOODS.forEach((good) => {
    const item = state.inventory[good.id];
    item.displayed = Object.hasOwn(targets, good.id);
    item.buyCeilingCents = 0;
    item.pricingMode = item.displayed ? 'market' : 'fixed';
    if (item.displayed) {
      item.autoStockTargetQty = targets[good.id];
      item.autoStockCashReserveCents = reserve;
      applyMarketPricing(state, good.id);
    } else {
      delete item.autoStockTargetQty;
      delete item.autoStockCashReserveCents;
    }
  });
  addLedgerEntry(state, { kind: 'shelf-setup', accepted: true, totalCents: 0, reason: 'Expanded shelf follows the market for pelts, rope, provisions, soap and boots; other automatic buying paused' });
  assertInvariants(state);
  return state;
}

export function acceptMeetingOffer(state, offerId) {
  processMeetings(state);
  const offer = state.meetings.pending.find((candidate) => candidate.id === offerId);
  if (!offer) throw new Error('Meeting offer is no longer pending');
  if (!openingComplete(state) && !offer.mandatory) throw new Error('Complete Eli Mercer’s opening fur trade first');
  processPerishables(state);
  const blockReason = meetingBlockReason(state, offer);
  if (blockReason) throw new Error(blockReason);
  const good = getGood(offer.goodId);
  const item = state.inventory[offer.goodId];
  const relationship = meetingRelationship(state, offer.profileId);
  const costBasisCents = offer.direction === 'sell' ? item.avgCostCents * offer.qty : null;

  if (offer.direction === 'buy') {
    const oldValue = item.avgCostCents * item.qty;
    state.cashCents -= offer.totalCents;
    item.qty += offer.qty;
    item.avgCostCents = Math.round((oldValue + offer.totalCents) / item.qty);
    addPerishableBatch(state, offer.goodId, offer.qty);
    if (offer.goodId === 'gold-dust') state.dailyGoldBought += offer.qty;
  } else {
    state.cashCents += offer.totalCents;
    item.qty -= offer.qty;
    consumePerishableBatches(state, offer.goodId, offer.qty);
    if (item.qty === 0) item.avgCostCents = 0;
  }

  const visitor = findVisitor(state, offer.visitorId);
  if (visitor) {
    if (!Array.isArray(visitor.goodsSoldToPost)) visitor.goodsSoldToPost = [];
    if (offer.direction === 'buy' && !visitor.goodsSoldToPost.includes(offer.goodId)) visitor.goodsSoldToPost.push(offer.goodId);
    const collection = offer.direction === 'buy' ? visitor.cargo : visitor.needs;
    const linked = collection.find((entry) => entry.goodId === offer.goodId);
    if (linked) linked.qty = Math.max(0, linked.qty - offer.qty);
  }
  state.market[offer.goodId].flowUnitsToday += offer.qty;
  state.transactions += 1;
  state.reputation += offer.highlighted || offer.rare ? 3 : 2;
  relationship.score = clamp(-10, 20, relationship.score + 2);
  relationship.accepted += 1;
  relationship.lastMinute = state.timeMinutes;
  if (offer.mandatory && offer.tutorial && !openingComplete(state)) {
    state.opening.status = 'completed';
    state.opening.completedMinute = state.timeMinutes;
    state.opening.transactionsAtCompletion = state.transactions;
    state.inventory.pelts.displayed = true;
    if (state.opening.marketCircuitVersion >= 2) {
      // Opening stock is already paid for. Let the first customer demonstrate
      // the sale before automatic purchasing can commit any more cash.
      GOODS.forEach((candidate) => { state.inventory[candidate.id].buyCeilingCents = 0; });
    }
    scheduleOpeningOutfitter(state);
    scheduleOpeningMarketCircuit(state);
  }
  offer.conversationLine = offer.conversation.accept;
  offer.finalUnitCents = offer.unitCents;
  archiveMeeting(state, offer, 'accepted');
  addLedgerEntry(state, {
    kind: offer.direction === 'buy' ? 'meeting-buy' : 'meeting-sell',
    accepted: true,
    meetingId: offer.id,
    profileId: offer.profileId,
    visitorId: offer.visitorId,
    goodId: offer.goodId,
    qty: offer.qty,
    unitCents: offer.unitCents,
    totalCents: offer.direction === 'buy' ? -offer.totalCents : offer.totalCents,
    ...(offer.direction === 'sell' ? { costBasisCents, grossProfitCents: offer.totalCents - costBasisCents } : {}),
    reason: `${offer.vendorName} meeting offer accepted`,
  });
  if (offer.direction === 'sell' && state.opening.marketCircuitVersion >= 2 && !Number.isInteger(state.opening.firstSaleMinute)) {
    state.opening.firstSaleMinute = state.timeMinutes;
    state.opening.firstSaleRevenueCents = offer.totalCents;
    state.opening.firstSaleProfitCents = offer.totalCents - costBasisCents;
  }
  state.meetings.lastResolution = {
    offerId: offer.id,
    outcome: 'accepted',
    unitCents: offer.unitCents,
    conversationLine: offer.conversationLine,
    minute: state.timeMinutes,
  };
  updateRank(state);
  assertInvariants(state);
  return state;
}

export function counterMeetingOffer(state, offerId, proposedUnitCents) {
  processMeetings(state);
  const offer = state.meetings.pending.find((candidate) => candidate.id === offerId);
  if (!offer) throw new Error('Meeting offer is no longer pending');
  if (offer.direction === 'sell' && findVisitor(state, offer.visitorId)?.goodsSoldToPost?.includes(offer.goodId)) {
    throw new Error('Same-stop buyback is not allowed');
  }
  if (!Number.isInteger(proposedUnitCents) || proposedUnitCents < 5 || proposedUnitCents > MAX_PRICE_CENTS) {
    throw new RangeError('Counteroffer must be between 5 silver coins and 10,000 notes per item');
  }
  const profile = vendorProfile(offer.profileId);
  const relationship = meetingRelationship(state, offer.profileId);
  const good = getGood(offer.goodId);
  const scarcityBps = Math.round(state.market[good.id].priceCents * 10000 / good.baseCents);
  const relationshipBps = relationship.score * 50;
  const floorBps = clamp(8000, 9950, 10000 - profile.flexibilityBps + Math.max(0, scarcityBps - 10000) / 4 - relationshipBps);
  const ceilingBps = clamp(10050, 13000, 10000 + profile.flexibilityBps + Math.max(0, scarcityBps - 10000) / 4 + relationshipBps);
  const floorCents = Math.ceil(offer.unitCents * floorBps / 10000);
  const ceilingCents = Math.floor(offer.unitCents * ceilingBps / 10000);
  const favorableToVendor = offer.direction === 'buy'
    ? proposedUnitCents >= offer.unitCents
    : proposedUnitCents <= offer.unitCents;
  const acceptable = offer.direction === 'buy'
    ? proposedUnitCents >= floorCents
    : proposedUnitCents <= ceilingCents;
  const acceptanceChance = clamp(0.15, 0.95, (3500 + profile.patience * 900 + relationship.score * 250 - offer.counterCount * 600) / 10000);
  const counterRng = { rngState: forkSeed(state.seed, `${offer.id}:${offer.counterCount}:${proposedUnitCents}`) };
  const accepted = favorableToVendor || (acceptable && randomFloat(counterRng) < acceptanceChance);
  offer.counterCount += 1;
  offer.lastCounterCents = proposedUnitCents;
  relationship.counters += 1;
  relationship.lastMinute = state.timeMinutes;

  if (accepted) {
    offer.unitCents = proposedUnitCents;
    offer.totalCents = offer.unitCents * offer.qty;
    offer.conversationLine = offer.conversation.accept;
    acceptMeetingOffer(state, offer.id);
    state.meetings.lastResolution.outcome = 'counter-accepted';
    return state;
  }

  offer.patienceRemaining -= 1;
  if (offer.patienceRemaining <= 0) {
    if (offer.mandatory) {
      offer.patienceRemaining = 1;
      offer.conversationLine = offer.conversation.counter;
      state.meetings.lastResolution = {
        offerId: offer.id,
        outcome: 'counter-rejected',
        proposedUnitCents,
        unitCents: offer.unitCents,
        patienceRemaining: offer.patienceRemaining,
        conversationLine: offer.conversationLine,
        minute: state.timeMinutes,
      };
      addLedgerEntry(state, { kind: 'meeting-counter', accepted: false, meetingId: offer.id, profileId: offer.profileId, goodId: offer.goodId, qty: offer.qty, unitCents: offer.unitCents, totalCents: 0, reason: `${offer.vendorName} declined that counter but kept the opening offer available` });
      assertInvariants(state);
      return state;
    }
    declineMeetingOffer(state, offer.id);
    state.meetings.lastResolution.outcome = 'counter-declined';
    return state;
  }

  const vendorCounter = offer.direction === 'buy'
    ? roundTo5((offer.unitCents + Math.max(proposedUnitCents, floorCents)) / 2)
    : roundTo5((offer.unitCents + Math.min(proposedUnitCents, ceilingCents)) / 2);
  offer.unitCents = vendorCounter;
  offer.totalCents = offer.unitCents * offer.qty;
  offer.conversationLine = offer.conversation.counter;
  addLedgerEntry(state, {
    kind: 'meeting-counter',
    accepted: false,
    meetingId: offer.id,
    profileId: offer.profileId,
    goodId: offer.goodId,
    qty: offer.qty,
    unitCents: vendorCounter,
    totalCents: 0,
    reason: `${offer.vendorName} returned a counteroffer`,
  });
  state.meetings.lastResolution = {
    offerId: offer.id,
    outcome: 'countered',
    proposedUnitCents,
    unitCents: vendorCounter,
    patienceRemaining: offer.patienceRemaining,
    conversationLine: offer.conversationLine,
    minute: state.timeMinutes,
  };
  assertInvariants(state);
  return state;
}

export function declineMeetingOffer(state, offerId) {
  processMeetings(state);
  const offer = state.meetings.pending.find((candidate) => candidate.id === offerId);
  if (!offer) throw new Error('Meeting offer is no longer pending');
  if (offer.mandatory) throw new Error('The opening fur trade cannot be declined');
  const relationship = meetingRelationship(state, offer.profileId);
  relationship.score = clamp(-10, 20, relationship.score - 1);
  relationship.declined += 1;
  relationship.lastMinute = state.timeMinutes;
  offer.conversationLine = offer.conversation.decline;
  archiveMeeting(state, offer, 'declined');
  addLedgerEntry(state, {
    kind: 'meeting-declined',
    accepted: false,
    meetingId: offer.id,
    profileId: offer.profileId,
    visitorId: offer.visitorId,
    goodId: offer.goodId,
    qty: offer.qty,
    unitCents: offer.unitCents,
    totalCents: 0,
    reason: `${offer.vendorName} meeting offer declined`,
  });
  state.meetings.lastResolution = {
    offerId: offer.id,
    outcome: 'declined',
    conversationLine: offer.conversationLine,
    minute: state.timeMinutes,
  };
  assertInvariants(state);
  return state;
}

export function transact(state, visitorId, kind, goodId, qty) {
  if (!openingComplete(state)) throw new Error('Complete Eli Mercer’s opening fur trade first');
  const visitor = findVisitor(state, visitorId);
  const good = getGood(goodId);
  if (!visitor || visitor.status !== 'stopped') throw new Error('Visitor is not stopped at the trading post');
  if (!Array.isArray(visitor.goodsSoldToPost)) visitor.goodsSoldToPost = [];
  if (!good) throw new RangeError(`Unknown good: ${goodId}`);
  const facilityBlockReason = goodFacilityBlockReason(state, good);
  if (facilityBlockReason) throw new Error(facilityBlockReason);
  if (!Number.isInteger(qty) || qty <= 0) throw new RangeError('Quantity must be a positive integer');
  const item = state.inventory[goodId];

  if (kind === 'buy') {
    const offer = visitor.cargo.find((entry) => entry.goodId === goodId);
    if (!offer || offer.qty < qty) throw new Error('Visitor does not have that quantity for sale');
    const negotiatedAskCents = effectiveVendorAskCents(state, offer.askCents);
    if (negotiatedAskCents > item.buyCeilingCents) throw new Error('Offer is above the buy ceiling');
    if (goodId === 'gold-dust' && state.dailyGoldBought + qty > 2) throw new Error('Daily rare-gold limit reached');
    if (good.coldChain && coldStorageCapacity(state) <= 0) throw new Error('Fresh fish requires an operating Ice House');
    if (good.coldChain && qty > availableColdStorage(state)) throw new Error('Ice House cold storage is full');
    const total = negotiatedAskCents * qty;
    if (total > state.cashCents) throw new Error('Not enough cash');
    if (inventoryBulk(state) + reservedBulk(state) + good.bulk * qty > storageCapacity(state)) throw new Error('Storage is full');
    const oldValue = item.avgCostCents * item.qty;
    state.cashCents -= total;
    item.qty += qty;
    item.avgCostCents = Math.round((oldValue + total) / item.qty);
    addPerishableBatch(state, goodId, qty);
    offer.qty -= qty;
    if (!Array.isArray(visitor.goodsSoldToPost)) visitor.goodsSoldToPost = [];
    if (!visitor.goodsSoldToPost.includes(goodId)) visitor.goodsSoldToPost.push(goodId);
    if (goodId === 'gold-dust') state.dailyGoldBought += qty;
    state.market[goodId].flowUnitsToday += qty;
    state.transactions += 1;
    state.reputation += 1;
    addLedgerEntry(state, { kind: 'buy', accepted: true, visitorId, goodId, qty, unitCents: negotiatedAskCents, listedAskCents: offer.askCents, totalCents: -total, reason: negotiatedAskCents < offer.askCents ? 'Buying agent negotiated and accepted automatically' : 'Offer accepted automatically' });
  } else if (kind === 'sell') {
    const need = visitor.needs.find((entry) => entry.goodId === goodId);
    if (!need || need.qty < qty) throw new Error('Visitor does not want that quantity');
    if (!item.displayed || item.qty < qty) throw new Error('Displayed stock is insufficient');
    if (item.listPriceCents > need.willingnessCents) throw new Error('Price exceeds visitor willingness');
    const total = item.listPriceCents * qty;
    if (total > need.budgetCents) throw new Error('Visitor budget is insufficient');
    const unitProfit = item.listPriceCents - item.avgCostCents;
    const costBasisCents = item.avgCostCents * qty;
    state.cashCents += total;
    item.qty -= qty;
    consumePerishableBatches(state, goodId, qty);
    if (item.qty === 0) item.avgCostCents = 0;
    need.qty -= qty;
    need.budgetCents -= total;
    state.market[goodId].flowUnitsToday += qty;
    state.transactions += 1;
    state.reputation += 1 + Math.max(0, Math.floor((unitProfit * qty) / 500));
    if (visitor.wealth === 2 && !visitor.affluentCounted) {
      visitor.affluentCounted = true;
      state.affluentClientsServed += 1;
    }
    state.shopTrust = clamp(0.2, 1, state.shopTrust + (item.listPriceCents <= state.market[goodId].priceCents * 1.15 ? 0.002 : -0.003));
    addLedgerEntry(state, { kind: 'sell', accepted: true, visitorId, goodId, qty, unitCents: item.listPriceCents, totalCents: total, costBasisCents, grossProfitCents: total - costBasisCents, reason: 'Shelf price accepted automatically' });
    if (state.opening.marketCircuitVersion >= 2 && !Number.isInteger(state.opening.firstSaleMinute)) {
      state.opening.firstSaleMinute = state.timeMinutes;
      state.opening.firstSaleRevenueCents = total;
      state.opening.firstSaleProfitCents = total - costBasisCents;
    }
  } else {
    throw new RangeError('Transaction kind must be buy or sell');
  }
  updateRank(state);
  return state;
}

function chicagoFreightDiscountBps(state) {
  const warehouseDiscountBps = state.plots.reduce((best, plot) => Math.max(best, FACILITY_BY_ID[plot.facilityId]?.freightDiscountBps || 0), 0);
  const agentDiscountBps = Math.min(1200, Math.round(employeeRolePower(state, 'freight-agent', 'freight') * 180));
  return Math.min(5000, warehouseDiscountBps + agentDiscountBps);
}

function placeChicagoOrder(state, goodId, qty) {
  const good = getGood(goodId);
  if (!good) throw new RangeError(`Unknown good: ${goodId}`);
  const accessBlockReason = chicagoAccessBlockReason(state, good);
  if (accessBlockReason) throw new Error(accessBlockReason);
  if (!Number.isInteger(qty) || qty < CHICAGO_CATALOG.minQuantity) {
    throw new RangeError(`Chicago orders require at least ${CHICAGO_CATALOG.minQuantity} units`);
  }
  const fixedQuoteCents = CHICAGO_CATALOG.starterQuotesCents[goodId] || null;
  const unitCents = fixedQuoteCents || roundTo5((state.market[goodId].priceCents * CHICAGO_CATALOG.premiumBps) / 10000);
  const freightCents = Math.ceil(good.bulk * qty * CHICAGO_CATALOG.freightPerBulkCents * (10000 - chicagoFreightDiscountBps(state)) / 10000);
  const totalCents = unitCents * qty + freightCents;
  if (totalCents > state.cashCents) throw new Error('Not enough cash for the Chicago order');
  if (inventoryBulk(state) + reservedBulk(state) + good.bulk * qty > storageCapacity(state)) throw new Error('The order will not fit in storage');
  const leadHours = randomInt(state, CHICAGO_CATALOG.minLeadHours, CHICAGO_CATALOG.maxLeadHours);
  state.cashCents -= totalCents;
  const order = {
    id: `order-${state.nextOrderId++}`,
    goodId,
    qty,
    unitCents: Math.round(totalCents / qty),
    merchandiseUnitCents: unitCents,
    quotedUnitCents: unitCents,
    starterQuote: fixedQuoteCents != null,
    freightCents,
    placedMinute: state.timeMinutes,
    arrivalMinute: state.timeMinutes + leadHours * 60,
    status: 'in-transit',
    routeId: 'chicago',
  };
  state.orders.push(order);
  addLedgerEntry(state, { kind: 'order', accepted: true, orderId: order.id, goodId, qty, unitCents: order.unitCents, totalCents: -totalCents, reason: `Chicago order; ${leadHours} hour lead` });
  return state;
}

function placeCubaOrder(state, goodId, qty) {
  if (!openingComplete(state)) throw new Error('Complete Eli Mercer’s opening fur trade first');
  const good = getGood(goodId);
  if (!good || !CUBA_CATALOG.goodIds.includes(goodId)) throw new RangeError('The Havana route carries only its listed imports');
  const unlocked = state.rank >= CUBA_CATALOG.minRank
    && state.reputation >= CUBA_CATALOG.minReputation
    && state.affluentClientsServed >= CUBA_CATALOG.minAffluentClients;
  if (!unlocked) throw new Error('The Havana route requires Regional Port rank, 850 reputation, and five affluent clients');
  if (!advancedRiverTransportReady(state)) throw new Error('The Havana route requires an operating Steamboat Dock');
  if (!Number.isInteger(qty) || qty < CUBA_CATALOG.minQuantity) throw new RangeError(`Havana orders require at least ${CUBA_CATALOG.minQuantity} units`);
  if (inventoryBulk(state) + reservedBulk(state) + good.bulk * qty > storageCapacity(state)) throw new Error('The order will not fit in storage');
  const volatilityBps = randomInt(state, CUBA_CATALOG.premiumBpsMin, CUBA_CATALOG.premiumBpsMax);
  const unitCents = roundTo5(state.market[goodId].priceCents * volatilityBps / 10000);
  const freightCents = good.bulk * qty * CUBA_CATALOG.freightPerBulkCents;
  const totalCents = unitCents * qty + freightCents;
  if (totalCents > state.cashCents) throw new Error('Not enough cash for the Havana import');
  const baseLeadHours = randomInt(state, CUBA_CATALOG.minLeadHours, CUBA_CATALOG.maxLeadHours);
  const delayHours = chance(state, CUBA_CATALOG.delayChanceBps / 10000) ? randomInt(state, 24, CUBA_CATALOG.maxDelayHours) : 0;
  state.cashCents -= totalCents;
  const order = {
    id: `order-${state.nextOrderId++}`,
    routeId: 'cuba',
    goodId,
    qty,
    unitCents: Math.round(totalCents / qty),
    merchandiseUnitCents: unitCents,
    freightCents,
    volatilityBps,
    delayHours,
    placedMinute: state.timeMinutes,
    arrivalMinute: state.timeMinutes + (baseLeadHours + delayHours) * 60,
    status: 'in-transit',
  };
  state.orders.push(order);
  addLedgerEntry(state, {
    kind: 'order',
    accepted: true,
    routeId: 'cuba',
    orderId: order.id,
    goodId,
    qty,
    unitCents: order.unitCents,
    totalCents: -totalCents,
    reason: `Havana import; ${baseLeadHours + delayHours} hour lead${delayHours ? ` including ${delayHours} hour delay` : ''}`,
  });
  return state;
}

export function orderFromRoute(state, routeId, goodId, qty) {
  if (routeId === 'chicago') return placeChicagoOrder(state, goodId, qty);
  if (routeId === 'cuba') return placeCubaOrder(state, goodId, qty);
  throw new RangeError(`Unknown supply route: ${routeId}`);
}

export function orderFromChicago(state, goodId, qty) {
  return orderFromRoute(state, 'chicago', goodId, qty);
}

export function orderFromCuba(state, goodId, qty) {
  return orderFromRoute(state, 'cuba', goodId, qty);
}

function findPokerPlot(state, requestedPlotIndex = null) {
  const casinoFacility = FACILITY_BY_ID['river-casino'];
  const candidates = state.plots.filter((plot) => plot.facilityId === casinoFacility.id && casinoOperatingBlockReason(state, plot) === null);
  const plot = requestedPlotIndex == null ? candidates[0] : candidates.find((candidate) => candidate.index === requestedPlotIndex);
  if (plot) return plot;
  const requested = requestedPlotIndex == null
    ? state.plots.find((candidate) => candidate.facilityId === casinoFacility.id)
    : state.plots[requestedPlotIndex - 1];
  throw new Error(casinoOperatingBlockReason(state, requested));
}

function pokerHistoryMatch(state, sessionId) {
  return state.poker.history.find((session) => session.sessionId === sessionId);
}

function validatePokerIdentity(session, sessionId, nonce) {
  if (!session || session.sessionId !== sessionId) throw new Error('Poker session is not active');
  if (session.nonce !== nonce) throw new Error('Poker session nonce does not match');
}

function validatePokerRevision(session, expectedRevision) {
  if (!Number.isInteger(expectedRevision) || expectedRevision !== session.revision) {
    throw new Error(`Poker session revision must match the latest revision ${session.revision}`);
  }
}

function settlePokerSession(state, session, status) {
  const creditedCents = session.currentStackCents;
  const pokerHouseCents = session.escrowCents - creditedCents;
  const settled = {
    ...session,
    engineSnapshot: session.engineSnapshot ? JSON.parse(JSON.stringify(session.engineSnapshot)) : null,
    status,
    creditedCents,
    playerNetCents: creditedCents - session.buyInCents,
    settledMinute: state.timeMinutes,
  };
  delete settled.frozenMinute;
  delete settled.frozenReason;
  state.cashCents += creditedCents;
  state.poker.activeSession = null;
  state.poker.history.push(settled);
  if (state.poker.history.length > 200) state.poker.history.splice(0, state.poker.history.length - 200);
  addLedgerEntry(state, {
    kind: 'poker-session',
    entryType: status === 'aborted' ? 'abort-settlement' : 'settlement',
    accepted: true,
    sessionId: session.sessionId,
    plotIndex: session.plotIndex,
    buyInCents: session.buyInCents,
    currentStackCents: creditedCents,
    playerCents: creditedCents,
    escrowCents: -session.escrowCents,
    pokerHouseCents,
    autonomousHouseCents: 0,
    totalCents: creditedCents,
    reason: status === 'aborted'
      ? 'Poker session recovered and closed from its latest safe checkpoint'
      : 'Poker session closed from its latest safe checkpoint',
  });
  assertInvariants(state);
  return state;
}

export function beginPokerSession(state, buyInCents, options = {}) {
  const casinoFacility = FACILITY_BY_ID['river-casino'];
  if (!Number.isInteger(buyInCents)) throw new RangeError('Poker buy-in must be an integer number of cents');
  if (buyInCents < casinoFacility.casino.minTableStakeCents || buyInCents > casinoFacility.casino.maxTableStakeCents) {
    throw new RangeError(`Poker buy-in must be from ${casinoFacility.casino.minTableStakeCents} to ${casinoFacility.casino.maxTableStakeCents} cents`);
  }
  if (!isPlainRecord(options)) throw new TypeError('Poker session options must be an object');
  if (state.poker.activeSession) throw new Error('A poker session is already active');
  const plot = findPokerPlot(state, options.plotIndex ?? null);
  if (state.cashCents < buyInCents) throw new Error('Not enough cash for that poker buy-in');
  const maxExposureCents = options.maxExposureCents;
  if (!Number.isInteger(maxExposureCents) || maxExposureCents < buyInCents || maxExposureCents > POKER_HARD_MAX_EXPOSURE_CENTS) {
    throw new RangeError(`Poker max exposure must be an integer from the buy-in through ${POKER_HARD_MAX_EXPOSURE_CENTS} cents`);
  }
  const hasInitialSnapshot = options.engineSnapshot !== undefined;
  const initialRevision = hasInitialSnapshot ? (options.checkpointRevision ?? options.engineSnapshot?.revision) : -1;
  if (!Number.isInteger(initialRevision) || initialRevision < -1) throw new Error('Poker checkpoint revision must be -1 before initialization or a nonnegative integer');
  const engineSnapshot = hasInitialSnapshot ? clonePokerSnapshot(options.engineSnapshot, initialRevision, buyInCents) : null;
  if (engineSnapshot && (engineSnapshot.tableChips < buyInCents || engineSnapshot.tableChips > maxExposureCents)) {
    throw new Error('Poker table chips must fit within the declared max exposure');
  }
  const ordinal = state.poker.nextSessionId++;
  const sessionId = `poker-session-${ordinal}`;
  const session = {
    sessionId,
    nonce: `poker_${forkSeed(state.seed, `${sessionId}:${state.timeMinutes}:${state.rngState}`).toString(36).padStart(10, '0')}_${String(ordinal).padStart(4, '0')}`,
    plotIndex: plot.index,
    buyInCents,
    escrowCents: buyInCents,
    currentStackCents: buyInCents,
    maxExposureCents,
    engineSnapshot,
    revision: initialRevision,
    status: 'active',
    openedMinute: state.timeMinutes,
    checkpointMinute: state.timeMinutes,
    migratedLegacy: false,
  };
  state.cashCents -= buyInCents;
  state.poker.activeSession = session;
  addLedgerEntry(state, {
    kind: 'poker-session',
    entryType: 'escrow-debit',
    accepted: true,
    sessionId,
    plotIndex: plot.index,
    buyInCents,
    currentStackCents: buyInCents,
    playerCents: -buyInCents,
    escrowCents: buyInCents,
    pokerHouseCents: 0,
    autonomousHouseCents: 0,
    totalCents: -buyInCents,
    reason: 'Poker buy-in moved from player cash into session escrow',
  });
  assertInvariants(state);
  return state;
}

export function checkpointPokerSession(state, sessionId, nonce, checkpoint) {
  const session = state.poker.activeSession;
  validatePokerIdentity(session, sessionId, nonce);
  if (!isPlainRecord(checkpoint)) throw new TypeError('Poker checkpoint must be an object');
  if (!['active', 'frozen'].includes(session.status)) throw new Error('Poker session cannot be checkpointed in its current status');
  if (!Number.isInteger(checkpoint.revision) || checkpoint.revision !== session.revision + 1) {
    throw new Error(`Poker checkpoint revision must advance exactly to ${session.revision + 1}`);
  }
  if (!Number.isInteger(checkpoint.currentStackCents) || checkpoint.currentStackCents < 0 || checkpoint.currentStackCents > session.maxExposureCents) {
    throw new RangeError(`Poker current stack must be an integer from 0 through ${session.maxExposureCents} cents`);
  }
  if (checkpoint.maxExposureCents !== session.maxExposureCents) throw new Error('Poker checkpoint max exposure must match the session declaration');
  const engineSnapshot = clonePokerSnapshot(checkpoint.engineSnapshot, checkpoint.revision, checkpoint.currentStackCents);
  if (engineSnapshot.tableChips < checkpoint.currentStackCents || engineSnapshot.tableChips > session.maxExposureCents) {
    throw new Error('Poker table chips must fit within the declared max exposure');
  }
  session.currentStackCents = checkpoint.currentStackCents;
  session.engineSnapshot = engineSnapshot;
  session.revision = checkpoint.revision;
  session.checkpointMinute = state.timeMinutes;
  assertInvariants(state);
  return state;
}

export function freezePokerSession(state, sessionId, nonce, reason = 'Host requested a recoverable poker pause') {
  const session = state.poker.activeSession;
  validatePokerIdentity(session, sessionId, nonce);
  if (typeof reason !== 'string' || !reason.trim()) throw new TypeError('Poker freeze reason must be nonempty text');
  if (session.status === 'frozen') return state;
  session.status = 'frozen';
  session.frozenMinute = state.timeMinutes;
  session.frozenReason = reason.trim();
  addLedgerEntry(state, {
    kind: 'poker-session',
    entryType: 'freeze',
    accepted: true,
    sessionId,
    playerCents: 0,
    escrowCents: 0,
    pokerHouseCents: 0,
    autonomousHouseCents: 0,
    totalCents: 0,
    reason: session.frozenReason,
  });
  assertInvariants(state);
  return state;
}

export function resumePokerSession(state, sessionId, nonce) {
  const session = state.poker.activeSession;
  validatePokerIdentity(session, sessionId, nonce);
  if (session.status !== 'frozen') throw new Error('Poker session is not frozen');
  if (!session.engineSnapshot) throw new Error('Frozen poker session needs a complete host checkpoint before it can resume');
  session.status = 'active';
  delete session.frozenMinute;
  delete session.frozenReason;
  assertInvariants(state);
  return state;
}

export function endPokerSession(state, sessionId, nonce, expectedRevision) {
  const session = state.poker.activeSession;
  if (!session || session.sessionId !== sessionId) {
    const settled = pokerHistoryMatch(state, sessionId);
    if (!settled) throw new Error('Poker session is not active');
    if (settled.nonce !== nonce) throw new Error('Poker session nonce does not match');
    validatePokerRevision(settled, expectedRevision);
    return state;
  }
  validatePokerIdentity(session, sessionId, nonce);
  validatePokerRevision(session, expectedRevision);
  if (session.status !== 'active') throw new Error('Frozen poker session must be resumed or safely aborted before settlement');
  if (!pokerSnapshotIsSafe(session.engineSnapshot)) throw new Error('Poker session cannot close during an unsettled hand');
  return settlePokerSession(state, session, 'settled');
}

export function abortPokerSession(state, sessionId, nonce, expectedRevision) {
  const session = state.poker.activeSession;
  if (!session || session.sessionId !== sessionId) {
    const settled = pokerHistoryMatch(state, sessionId);
    if (!settled || settled.status !== 'aborted') throw new Error('Poker session is not active');
    if (settled.nonce !== nonce) throw new Error('Poker session nonce does not match');
    validatePokerRevision(settled, expectedRevision);
    return state;
  }
  validatePokerIdentity(session, sessionId, nonce);
  validatePokerRevision(session, expectedRevision);
  if (!pokerSnapshotIsSafe(session.engineSnapshot)) {
    if (session.status !== 'frozen') freezePokerSession(state, sessionId, nonce, 'Unsafe mid-hand abort frozen for host recovery');
    return state;
  }
  return settlePokerSession(state, session, 'aborted');
}

// Compatibility names intentionally retain the escrow contract; they cannot
// issue a payout amount independently of the latest validated engine snapshot.
export function debitPokerBankroll(state, buyInCents, options = {}) {
  return beginPokerSession(state, buyInCents, options);
}

export function creditPokerBankroll(state, sessionId, nonce, expectedRevision) {
  return endPokerSession(state, sessionId, nonce, expectedRevision);
}

export function settlePokerBankroll(state, sessionId, nonce, expectedRevision) {
  return endPokerSession(state, sessionId, nonce, expectedRevision);
}

export function resolveInteriorOpportunity(state, plotIndex, choiceId) {
  if (!Number.isInteger(plotIndex) || plotIndex < 1 || plotIndex > 5) throw new RangeError('Plot index must be 1 through 5');
  if (typeof choiceId !== 'string' || !choiceId) throw new RangeError('Interior opportunity choice id is required');
  ensureInteriorOpportunities(state);
  const plot = state.plots[plotIndex - 1];
  const record = state.interiors.byPlot[String(plotIndex)];
  if (!record) throw new Error('This plot has no interior opportunity');
  const catalog = interiorOpportunityCatalog(record.facilityId);
  const choice = catalog.choices.find((candidate) => candidate.id === choiceId && record.choiceIds.includes(candidate.id));
  if (!choice) throw new RangeError(`Unknown interior opportunity choice: ${choiceId}`);
  const outcome = interiorOutcome(record, choice);
  const blockReason = interiorChoiceBlockReason(state, plot, record, choice, outcome);
  if (blockReason) throw new Error(blockReason);

  const committedInputs = { ...(choice.inputs || {}) };
  const outputGoods = { ...(outcome.outputs || {}) };
  const inputValueCents = Object.entries(committedInputs).reduce((sum, [goodId, qty]) => {
    const item = state.inventory[goodId];
    return sum + (item.avgCostCents || state.market[goodId].priceCents) * qty;
  }, 0);
  state.cashCents -= choice.cashCostCents;
  Object.entries(committedInputs).forEach(([goodId, qty]) => {
    const item = state.inventory[goodId];
    item.qty -= qty;
    consumePerishableBatches(state, goodId, qty);
    if (item.qty === 0) item.avgCostCents = 0;
    state.market[goodId].flowUnitsToday += qty;
  });

  const outputValueWeight = Object.entries(outputGoods).reduce((sum, [goodId, qty]) => sum + getGood(goodId).baseCents * qty, 0);
  const investedCents = choice.cashCostCents + inputValueCents;
  Object.entries(outputGoods).forEach(([goodId, qty]) => {
    const item = state.inventory[goodId];
    const oldValue = item.avgCostCents * item.qty;
    const allocatedCost = outputValueWeight > 0 ? Math.round(investedCents * getGood(goodId).baseCents * qty / outputValueWeight) : 0;
    item.qty += qty;
    item.avgCostCents = Math.round((oldValue + allocatedCost) / item.qty);
    addPerishableBatch(state, goodId, qty);
    state.market[goodId].flowUnitsToday += qty;
  });

  const cashRewardCents = outcome.cashRewardCents || 0;
  const reputationDelta = outcome.reputationDelta || 0;
  const conditionDelta = outcome.conditionDelta || 0;
  state.cashCents += cashRewardCents;
  state.reputation = Math.max(0, state.reputation + reputationDelta);
  plot.condition = clamp(0, 100, plot.condition + conditionDelta);
  plot.serviceActivityCount += 1;
  const resolution = {
    opportunityId: record.opportunityId,
    plotIndex,
    facilityId: record.facilityId,
    cycle: record.cycle,
    seed: record.seed,
    choiceId: choice.id,
    outcomeId: outcome.id,
    outcomeLabel: outcome.label,
    resolvedMinute: state.timeMinutes,
    cashCostCents: choice.cashCostCents,
    cashRewardCents,
    netCashCents: cashRewardCents - choice.cashCostCents,
    inputs: committedInputs,
    outputs: outputGoods,
    reputationDelta,
    conditionDelta,
  };
  state.interiors.history.push(resolution);
  if (state.interiors.history.length > 150) state.interiors.history.splice(0, state.interiors.history.length - 150);
  state.interiors.byPlot[String(plotIndex)] = createInteriorOpportunityRecord(
    state,
    plot,
    record.cycle + 1,
    state.timeMinutes + choice.cooldownMinutes,
    { choiceId: choice.id, outcomeId: outcome.id, outcomeLabel: outcome.label, resolvedMinute: state.timeMinutes },
  );
  addLedgerEntry(state, {
    kind: 'interior-opportunity',
    accepted: true,
    opportunityId: record.opportunityId,
    plotIndex,
    facilityId: record.facilityId,
    choiceId: choice.id,
    outcomeId: outcome.id,
    cashCostCents: choice.cashCostCents,
    cashRewardCents,
    inputs: committedInputs,
    outputs: outputGoods,
    reputationDelta,
    conditionDelta,
    totalCents: cashRewardCents - choice.cashCostCents,
    reason: `${choice.name}: ${outcome.label}`,
  });
  updateRank(state);
  assertInvariants(state);
  return state;
}

export function purchasePlot(state, plotIndex) {
  if (!Number.isInteger(plotIndex) || plotIndex < 1 || plotIndex > 5) throw new RangeError('Plot index must be 1 through 5');
  const blockReason = deedPurchaseBlockReason(state, plotIndex);
  if (blockReason) throw new Error(blockReason);
  const plot = state.plots[plotIndex - 1];
  state.cashCents -= plot.deedCostCents;
  plot.owned = true;
  plot.deedPurchasedMinute = state.timeMinutes;
  addLedgerEntry(state, {
    kind: 'deed',
    accepted: true,
    plotIndex,
    totalCents: -plot.deedCostCents,
    reason: `Plot ${plotIndex} deed purchased`,
  });
  assertInvariants(state);
  return state;
}

export function startConstruction(state, plotIndex, facilityId) {
  if (!Number.isInteger(plotIndex) || plotIndex < 1 || plotIndex > 5) throw new RangeError('Plot index must be 1 through 5');
  const facility = FACILITY_BY_ID[facilityId];
  if (!facility) throw new RangeError(`Unknown facility: ${facilityId}`);
  const plot = state.plots[plotIndex - 1];
  const blockReason = constructionBlockReason(state, plotIndex, facilityId);
  if (blockReason) throw new Error(blockReason);
  const totalStartCostCents = facility.costCents + facility.laborCostCents;
  state.cashCents -= totalStartCostCents;
  state.labor.available -= facility.laborRequired;
  delete state.production.operations[String(plotIndex)];
  delete state.casino.operations[String(plotIndex)];
  if (state.interiors?.byPlot) delete state.interiors.byPlot[String(plotIndex)];
  if (facility.phases === 0) {
    plot.facilityId = facilityId;
    plot.builtMinute = state.timeMinutes;
    plot.serviceActivityCount = 0;
    plot.kind = facility.kind;
    plot.condition = 100;
    plot.neglectDays = 0;
    plot.damage = [];
    if (plot.kind !== 'river') {
      state.dockTreatments.forEach((treatment) => {
        if (treatment.plotIndex === plot.index) {
          treatment.plotIndex = null;
          treatment.x = null;
          treatment.y = null;
        }
      });
    }
    state.labor.available = Math.min(state.labor.capacity, state.labor.available + facility.laborRequired);
  } else {
    plot.construction = {
      facilityId,
      laborRequired: facility.laborRequired,
      laborCostCents: facility.laborCostCents,
      materialsType: facility.materialsType,
      phase: 1,
      phaseName: PHASE_NAMES[0],
      phasesTotal: facility.phases,
      phaseDurationMinutes: Math.ceil(facility.buildMinutes / facility.phases),
      phaseProgressMinutes: 0,
      startedMinute: state.timeMinutes,
    };
  }
  addLedgerEntry(state, {
    kind: 'construction',
    accepted: true,
    plotIndex,
    facilityId,
    laborRequired: facility.laborRequired,
    laborCostCents: facility.laborCostCents,
    materialsType: facility.materialsType,
    totalCents: -totalStartCostCents,
    reason: `${facility.name} construction started; labor paid`,
  });
  syncSmokeEmitters(state);
  return state;
}

// A single reviewed project commits both deed and building, or neither. Work
// on a detached plain-data candidate so every ordinary simulation check runs
// before the caller's cash, ledger, ownership or construction can change.
export function startPlotProject(state, plotIndex, facilityId, { cashReserveCents = 0 } = {}) {
  if (!Number.isInteger(plotIndex) || plotIndex < 1 || plotIndex > 5) throw new RangeError('Plot index must be 1 through 5');
  if (!FACILITY_BY_ID[facilityId]) throw new RangeError(`Unknown facility: ${facilityId}`);
  if (!Number.isSafeInteger(cashReserveCents) || cashReserveCents < 0) throw new RangeError('Working cash reserve must be a nonnegative amount in whole cents');
  const blockReason = plotProjectBlockReason(state, plotIndex, facilityId, cashReserveCents);
  if (blockReason) throw new Error(blockReason);
  const candidate = structuredClone(state);
  if (!candidate.plots[plotIndex - 1].owned) purchasePlot(candidate, plotIndex);
  startConstruction(candidate, plotIndex, facilityId);
  assertInvariants(candidate);
  Object.assign(state, candidate);
  return state;
}

export function placeAccessory(state, accessoryId, plotIndex, x, y) {
  const accessory = ACCESSORIES.find((item) => item.id === accessoryId);
  if (!accessory) throw new RangeError(`Unknown accessory: ${accessoryId}`);
  if (accessory.rank > state.rank) throw new Error(`${accessory.name} is not unlocked`);
  if (!Number.isInteger(plotIndex) || plotIndex < 1 || plotIndex > 5) throw new RangeError('Plot index must be 1 through 5');
  if (!state.plots[plotIndex - 1].owned) throw new Error(`Purchase the Plot ${plotIndex} deed first`);
  if (![x, y].every((value) => Number.isFinite(value) && value >= 0 && value <= 1)) throw new RangeError('Accessory coordinates must be normalized from 0 to 1');
  if (state.cashCents < accessory.costCents) throw new Error('Not enough cash for accessory');
  state.cashCents -= accessory.costCents;
  state.decorations.push({ id: `decor-${state.nextDecorationId++}`, accessoryId, plotIndex, x, y, movable: accessory.movable, anchor: accessory.anchor, placedMinute: state.timeMinutes });
  addLedgerEntry(state, { kind: 'decor', accepted: true, accessoryId, plotIndex, totalCents: -accessory.costCents, reason: `${accessory.name} placed from the Decor tab` });
  return state;
}

export function moveAccessory(state, decorationId, plotIndex, x, y) {
  const decoration = state.decorations.find((item) => item.id === decorationId);
  if (!decoration || !decoration.movable) throw new Error('Accessory is not movable');
  if (!Number.isInteger(plotIndex) || plotIndex < 1 || plotIndex > 5) throw new RangeError('Plot index must be 1 through 5');
  if (!state.plots[plotIndex - 1].owned) throw new Error(`Purchase the Plot ${plotIndex} deed first`);
  if (![x, y].every((value) => Number.isFinite(value) && value >= 0 && value <= 1)) throw new RangeError('Accessory coordinates must be normalized from 0 to 1');
  decoration.plotIndex = plotIndex;
  decoration.x = x;
  decoration.y = y;
  return state;
}

export function purchaseDockTreatment(state, treatmentId) {
  const treatment = dockTreatmentType(treatmentId);
  if (!treatment) throw new RangeError(`Unknown dock treatment: ${treatmentId}`);
  if (treatment.rank > state.rank) throw new Error(`${treatment.name} is not unlocked`);
  if (state.cashCents < treatment.costCents) throw new Error('Not enough cash for dock treatment');
  state.cashCents -= treatment.costCents;
  const owned = {
    id: `dock-treatment-${state.nextDockTreatmentId++}`,
    treatmentId,
    purchasedMinute: state.timeMinutes,
    plotIndex: null,
    x: null,
    y: null,
  };
  state.dockTreatments.push(owned);
  addLedgerEntry(state, { kind: 'dock-treatment', accepted: true, treatmentId, ownedTreatmentId: owned.id, totalCents: -treatment.costCents, reason: `${treatment.name} purchased for a river landing` });
  return state;
}

export function placeDockTreatment(state, ownedTreatmentId, plotIndex, x, y) {
  const owned = state.dockTreatments.find((item) => item.id === ownedTreatmentId);
  if (!owned) throw new RangeError(`Unknown owned dock treatment: ${ownedTreatmentId}`);
  if (!Number.isInteger(plotIndex) || plotIndex < 1 || plotIndex > 5) throw new RangeError('Plot index must be 1 through 5');
  const plot = state.plots[plotIndex - 1];
  if (!plot.owned) throw new Error(`Purchase the Plot ${plotIndex} deed first`);
  if (plot.construction || FACILITY_BY_ID[plot.facilityId]?.kind !== 'river') throw new Error('Dock treatments require an operating river facility');
  if (![x, y].every((value) => Number.isFinite(value) && value >= 0 && value <= 1)) throw new RangeError('Dock treatment coordinates must be normalized from 0 to 1');
  owned.plotIndex = plotIndex;
  owned.x = x;
  owned.y = y;
  addLedgerEntry(state, { kind: 'dock-treatment-placed', accepted: true, ownedTreatmentId, treatmentId: owned.treatmentId, plotIndex, totalCents: 0, reason: `${dockTreatmentType(owned.treatmentId).name} placed on Plot ${plotIndex}` });
  return state;
}

export function moveDockTreatment(state, ownedTreatmentId, plotIndex, x, y) {
  return placeDockTreatment(state, ownedTreatmentId, plotIndex, x, y);
}

export function hireEmployee(state, candidateId) {
  const index = state.employees.candidates.findIndex((candidate) => candidate.id === candidateId);
  if (index < 0) throw new Error('Employee candidate is no longer available');
  const candidate = state.employees.candidates[index];
  const signingCostCents = candidate.wageCents * 2;
  if (state.cashCents < signingCostCents) throw new Error('Not enough cash for the two-day hiring advance');
  state.cashCents -= signingCostCents;
  state.employees.candidates.splice(index, 1);
  const employee = {
    ...candidate,
    id: `employee-${state.employees.nextEmployeeId++}`,
    candidateId,
    assignment: null,
    hiredMinute: state.timeMinutes,
    signingCostCents,
  };
  state.employees.hired.push(employee);
  addLedgerEntry(state, { kind: 'employee-hired', accepted: true, employeeId: employee.id, roleId: employee.roleId, totalCents: -signingCostCents, reason: `${employee.name} hired with a two-day wage advance` });
  return state;
}

export function fireEmployee(state, employeeId) {
  const index = state.employees.hired.findIndex((employee) => employee.id === employeeId);
  if (index < 0) throw new Error('Employee is not currently hired');
  const [employee] = state.employees.hired.splice(index, 1);
  state.employees.history.push({ ...employee, firedMinute: state.timeMinutes });
  if (state.employees.history.length > 100) state.employees.history.shift();
  addLedgerEntry(state, { kind: 'employee-fired', accepted: true, employeeId, roleId: employee.roleId, totalCents: 0, reason: `${employee.name}'s employment ended` });
  return state;
}

export function assignEmployee(state, employeeId, assignment) {
  const employee = state.employees.hired.find((candidate) => candidate.id === employeeId);
  if (!employee) throw new Error('Employee is not currently hired');
  const assignmentRecord = typeof assignment === 'string' ? { type: assignment } : { ...assignment };
  const role = employeeRole(employee.roleId);
  if (!assignmentRecord.type || !role.assignments.includes(assignmentRecord.type)) throw new Error(`${role.name} cannot take that assignment`);
  if (assignmentRecord.plotIndex != null) {
    if (!Number.isInteger(assignmentRecord.plotIndex) || !state.plots[assignmentRecord.plotIndex - 1]?.owned) throw new Error('Assignment target must be an owned plot');
  }
  employee.assignment = assignmentRecord;
  addLedgerEntry(state, { kind: 'employee-assigned', accepted: true, employeeId, roleId: employee.roleId, totalCents: 0, reason: `${employee.name} assigned to ${assignmentRecord.type}` });
  return state;
}

export function auditEmployee(state, employeeId) {
  const employee = state.employees.hired.find((candidate) => candidate.id === employeeId);
  if (!employee) throw new Error('Employee is not currently hired');
  const auditCostCents = Math.max(100, roundTo5(300 - employeeRolePower(state, 'investigator') * 25));
  if (state.cashCents < auditCostCents) throw new Error('Not enough cash for an audit');
  state.cashCents -= auditCostCents;
  employee.knowledge = { skill: 100, loyalty: 100, integrity: 100, traits: 100 };
  state.employees.lastAudit = { employeeId, minute: state.timeMinutes, auditCostCents, findings: 'documented-records' };
  addLedgerEntry(state, { kind: 'employee-audit', accepted: true, employeeId, totalCents: -auditCostCents, reason: `${employee.name}'s records were audited; findings come from records, not appearance` });
  return state;
}

export function repairProperty(state, plotIndex, damageId = null) {
  if (!Number.isInteger(plotIndex) || plotIndex < 1 || plotIndex > 5) throw new RangeError('Plot index must be 1 through 5');
  const plot = state.plots[plotIndex - 1];
  if (!plot.owned) throw new Error(`Purchase the Plot ${plotIndex} deed first`);
  if (plot.construction) throw new Error('Finish construction before repairs');
  if (state.labor.available < 1) throw new Error('Need one available town labor unit for repairs');
  const damageIndex = damageId == null ? (plot.damage.length ? 0 : -1) : plot.damage.findIndex((damage) => damage.id === damageId);
  if (damageId != null && damageIndex < 0) throw new Error('Unknown property damage');
  if (damageIndex < 0 && plot.condition >= 100) throw new Error('Property does not need repair');
  const damage = damageIndex >= 0 ? plot.damage[damageIndex] : null;
  const materialsCostCents = damage?.repairCostCents || Math.max(200, Math.ceil((100 - plot.condition) / 10) * 200);
  const laborCostCents = 175;
  const totalCents = materialsCostCents + laborCostCents;
  if (state.cashCents < totalCents) throw new Error('Not enough cash for repair materials and labor');
  state.cashCents -= totalCents;
  if (damage) {
    plot.damage.splice(damageIndex, 1);
    plot.condition = Math.min(100, plot.condition + damage.conditionLoss);
  } else {
    plot.condition = Math.min(100, plot.condition + 15);
  }
  plot.neglectDays = Math.max(0, plot.neglectDays - 5);
  addLedgerEntry(state, { kind: 'property-repair', accepted: true, plotIndex, damageId: damage?.id || null, laborRequired: 1, laborCostCents, totalCents: -totalCents, reason: `Plot ${plotIndex} repairs completed with paid town labor` });
  return state;
}

export function tick(state, deltaMinutes) {
  if (!Number.isFinite(deltaMinutes) || deltaMinutes < 0) throw new RangeError('deltaMinutes must be nonnegative');
  state.traffic.forEach((visitor) => {
    if (!Array.isArray(visitor.goodsSoldToPost)) visitor.goodsSoldToPost = [];
  });
  let remaining = Math.round(deltaMinutes);
  while (remaining > 0) {
    const elapsed = Math.min(60, remaining);
    const oldDay = currentDay(state);
    state.timeMinutes += elapsed;
    advanceConstruction(state, elapsed);
    ensureInteriorOpportunities(state);
    processOrders(state);
    processTraffic(state);
    processAmbientActors(state);
    processPerishables(state);
    processMeetings(state);
    const newDay = currentDay(state);
    for (let day = oldDay + 1; day <= newDay; day += 1) dailyUpdate(state, day);
    ensureSchedule(state);
    remaining -= elapsed;
  }
  ensureInteriorOpportunities(state);
  syncSmokeEmitters(state);
  armRecoveryIfNeeded(state);
  assertInvariants(state);
  return state;
}

// A short, explicit work shift advances the same simulation as the clock.
// Stop at the next arrival, delivery or construction phase; never silently
// expire an actionable visible meeting. All costs, risks and wages still run.
export function advanceToNextOpportunity(state) {
  if (!openingComplete(state)) throw new Error('Complete Eli Mercer’s opening fur trade first');
  const now = state.timeMinutes;
  const events = [now + 120];
  for (const visitor of state.traffic) {
    if (visitor.status === 'scheduled' && visitor.arrivalMinute > now) events.push(visitor.arrivalMinute);
  }
  for (const order of state.orders) {
    if (order.status === 'in-transit' && order.arrivalMinute > now) events.push(order.arrivalMinute);
  }
  for (const plot of state.plots) {
    if (plot.construction) events.push(now + plot.construction.phaseDurationMinutes - plot.construction.phaseProgressMinutes);
  }
  for (const offer of state.meetings.pending) {
    if (!meetingIsVisible(state.meetings.policy, offer) || offer.expiresMinute == null || meetingBlockReason(state, offer)) continue;
    if (offer.expiresMinute - now <= 1) throw new Error(`Resolve or decline ${offer.vendorName}’s expiring offer in Meetings before advancing`);
    events.push(offer.expiresMinute - 1);
  }
  return tick(state, Math.max(1, Math.min(...events) - now));
}

export function getDerived(state) {
  const day = currentDay(state);
  const season = seasonForDay(day);
  const identity = identityTotals(state);
  const capacityBulk = storageCapacity(state);
  const usedBulk = inventoryBulk(state);
  const reservedCapacityBulk = reservedBulk(state);
  const freeCapacityBulk = Math.max(0, capacityBulk - usedBulk - reservedCapacityBulk);
  const ledgerRows = GOODS.map((good) => {
    const item = state.inventory[good.id];
    const market = state.market[good.id];
    const facilityBlockReason = goodFacilityBlockReason(state, good);
    const blockReason = good.rank > state.rank ? `${good.name} is not unlocked` : facilityBlockReason;
    return {
      ...good,
      unlocked: blockReason === null,
      blockReason,
      ...item,
      marketCents: market.priceCents,
      marginCents: item.listPriceCents - item.avgCostCents,
      nextExpiryMinute: item.perishableBatches?.[0]?.expiresMinute ?? null,
    };
  });
  const arrivals = state.traffic.map((visitor) => {
    const etaMinutes = visitor.status === 'scheduled' ? Math.max(0, visitor.arrivalMinute - state.timeMinutes) : 0;
    const knowledge = etaMinutes > 480 ? 'silhouette' : etaMinutes > 180 ? 'category' : 'cargo';
    return {
      id: visitor.id,
      typeId: visitor.typeId,
      name: visitor.name,
      lane: visitor.lane,
      activity: visitor.activity,
      origin: knowledge === 'cargo' ? visitor.origin : null,
      status: visitor.status,
      direction: visitor.direction,
      etaMinutes,
      knowledge,
      groupSize: visitor.groupSize,
      stopReasons: visitor.stopReasons,
      cargo: visitor.cargo,
      needs: visitor.needs,
    };
  });
  const derivedPlots = state.plots.map((plot) => {
    const interiorRecord = state.interiors.byPlot[String(plot.index)] || null;
    const purchaseBlockReason = deedPurchaseBlockReason(state, plot.index);
    const risks = propertyRisks(state, plot, day);
    const repairMaterialsCostCents = plot.damage[0]?.repairCostCents || (plot.condition < 100 ? Math.max(200, Math.ceil((100 - plot.condition) / 10) * 200) : 0);
    const repairTotalCents = repairMaterialsCostCents > 0 ? repairMaterialsCostCents + 175 : 0;
    const repairBlockReason = !plot.owned ? 'Purchase the deed first'
      : plot.construction ? 'Finish construction before repairs'
        : state.labor.available < 1 ? 'Need one available town labor unit'
          : repairTotalCents > state.cashCents ? `Need ${cashLabel(repairTotalCents - state.cashCents)} more for repair materials and labor`
            : repairTotalCents === 0 ? 'Property does not need repair'
              : null;
    return {
      index: plot.index,
      owned: plot.owned,
      deedCostCents: plot.deedCostCents,
      deedPurchasedMinute: plot.deedPurchasedMinute,
      canPurchase: !plot.owned && purchaseBlockReason === null,
      purchaseBlockReason,
      kind: plot.kind,
      facilityId: plot.facilityId,
      builtMinute: plot.builtMinute,
      interiorMaturity: interiorMaturitySnapshot(state, plot),
      interiorOpportunity: interiorRecord ? interiorOpportunityView(state, interiorRecord) : null,
      condition: plot.condition,
      neglectDays: plot.neglectDays,
      derelict: plot.owned && (plot.condition < 45 || plot.neglectDays >= 12 || plot.damage.length >= 3),
      risks,
      damage: plot.damage.map((damage) => {
        const type = damageType(damage.typeId);
        return { ...type, ...damage, name: type.name, overlay: { ...type.overlay } };
      }),
      repair: {
        needed: plot.damage.length > 0 || plot.condition < 100,
        nextDamageId: plot.damage[0]?.id || null,
        materialsCostCents: repairMaterialsCostCents,
        laborRequired: 1,
        laborCostCents: 175,
        totalCostCents: repairTotalCents,
        canRepair: repairBlockReason === null,
        blockReason: repairBlockReason,
      },
      construction: plot.construction ? { ...plot.construction } : null,
      buildOptions: PLOT_OPTIONS[plot.index].map((facilityId) => {
        const facility = FACILITY_BY_ID[facilityId];
        const blockReason = constructionBlockReason(state, plot.index, facilityId);
        const projectBlockReason = plotProjectBlockReason(state, plot.index, facilityId);
        return {
          facilityId,
          name: facility.name,
          facilityCostCents: facility.costCents,
          laborRequired: facility.laborRequired,
          laborCostCents: facility.laborCostCents,
          totalStartCostCents: facility.costCents + facility.laborCostCents,
          deedCostCents: plot.owned ? 0 : plot.deedCostCents,
          totalProjectCostCents: (plot.owned ? 0 : plot.deedCostCents) + facility.costCents + facility.laborCostCents,
          canStartProject: projectBlockReason === null,
          projectBlockReason,
          materialsType: facility.materialsType,
          coldCapacity: facility.coldCapacity || 0,
          coldShelfLifeMinutes: facility.coldShelfLifeMinutes || 0,
          kind: facility.kind,
          description: facility.description || null,
          productionFireRiskBps: facility.productionFireRiskBps || 0,
          production: facility.production ? {
            cycleDays: facility.production.cycleDays,
            inputs: { ...facility.production.inputs },
            outputs: { ...facility.production.outputs },
          } : null,
          repairService: facility.repairService ? { ...facility.repairService } : null,
          casino: facility.casino ? { ...facility.casino } : null,
          marketAccessGoodIds: [...(facility.marketAccessGoodIds || [])],
          roadTravelBonusBps: facility.roadTravelBonusBps || 0,
          freightDiscountBps: facility.freightDiscountBps || 0,
          incomeDependencies: facility.kind === 'prestige' || facility.reputationIncomeThreshold ? {
            visitorWealthMin: facility.visitorWealthMin || 0,
            reputationThreshold: facility.reputationIncomeThreshold || 0,
            hotelSynergyBps: facility.hotelSynergyBps || 0,
            safetySensitive: Boolean(facility.safetySensitive),
            eventIncomeTags: facility.eventIncomeTags || [],
          } : null,
          canStart: blockReason === null,
          blockReason,
        };
      }),
    };
  });
  const visibleMeetings = state.meetings.pending
    .filter((offer) => meetingIsVisible(state.meetings.policy, offer))
    .map((offer) => {
      const blockReason = meetingBlockReason(state, offer);
      return {
        ...offer,
        portrait: { ...offer.portrait },
        conversation: { ...offer.conversation },
        canAccept: blockReason === null,
        blockReason,
        relationship: { ...meetingRelationship(state, offer.profileId) },
      };
    });
  const revealMetric = (value, knowledge) => {
    if (knowledge >= 100) return { exact: value, band: `${value}` };
    if (knowledge >= 60) {
      const low = Math.floor(value / 20) * 20;
      return { exact: null, band: `${low}-${Math.min(100, low + 19)}` };
    }
    return { exact: null, band: 'unknown' };
  };
  const employeeView = (employee, candidate = false) => ({
    id: employee.id,
    personId: employee.personId,
    candidateId: employee.candidateId || (candidate ? employee.id : null),
    name: employee.name,
    roleId: employee.roleId,
    roleName: employeeRole(employee.roleId).name,
    wageCents: employee.wageCents,
    signingCostCents: candidate ? employee.wageCents * 2 : employee.signingCostCents,
    appearance: { ...employee.appearance, honestyCue: false },
    assignment: employee.assignment ? { ...employee.assignment } : null,
    availableDay: employee.availableDay,
    expiresDay: employee.expiresDay,
    hiredMinute: employee.hiredMinute,
    knowledge: { ...employee.knowledge },
    skill: revealMetric(employee.skill, employee.knowledge.skill),
    loyalty: revealMetric(employee.loyalty, employee.knowledge.loyalty),
    integrity: revealMetric(employee.integrity, employee.knowledge.integrity),
    traits: employee.knowledge.traits >= 100 ? employee.traitIds.map((traitId) => ({ ...employeeTrait(traitId) })) : [],
    traitKnowledge: employee.knowledge.traits >= 100 ? 'known' : 'unknown',
    canHire: candidate ? state.cashCents >= employee.wageCents * 2 : undefined,
  });
  const horticultureDecorations = state.decorations
    .map((decoration) => ({ decoration, accessory: ACCESSORIES.find((item) => item.id === decoration.accessoryId) }))
    .filter(({ accessory }) => accessory?.horticulture)
    .map(({ decoration, accessory }) => {
      const lifecycle = treeLifecycleSnapshot(state, decoration, accessory);
      return { ...decoration, name: accessory.name, horticulture: { ...accessory.horticulture }, identity: lifecycle?.identity || { ...accessory.identity }, lifecycle };
    });
  const horticultureOperations = state.plots
    .filter((plot) => plot.owned && !plot.construction && FACILITY_BY_ID[plot.facilityId]?.garden)
    .map((plot) => {
      const operation = state.horticulture.operations[String(plot.index)] || null;
      const greenhouseProtected = greenhouseOnPlot(state, plot.index);
      return {
        plotIndex: plot.index,
        facilityId: plot.facilityId,
        phase: gardenPhaseForDay(day, greenhouseProtected),
        gameYear: gameYearForDay(day),
        plantedThisYear: operation?.plantedYear === gameYearForDay(day),
        greenhouseProtected,
        outputGoodId: FACILITY_BY_ID[plot.facilityId].garden.goodId,
        baseYieldUnits: FACILITY_BY_ID[plot.facilityId].garden.baseYieldUnits,
        lastYieldUnits: operation?.lastYieldUnits || 0,
        totalYieldUnits: operation?.totalYieldUnits || 0,
        totalHarvests: operation?.totalHarvests || 0,
        nextSeasonAction: gardenPhaseForDay(day, greenhouseProtected),
      };
    });
  const productiveFacilities = Object.values(FACILITY_BY_ID).filter((facility) => facility.production || facility.marketAccessGoodIds?.length);
  const productionOperations = state.plots
    .filter((plot) => plot.owned && !plot.construction && FACILITY_BY_ID[plot.facilityId]?.production)
    .map((plot) => {
      const facility = FACILITY_BY_ID[plot.facilityId];
      const operation = state.production.operations[String(plot.index)] || null;
      const missingInputs = Object.entries(facility.production.inputs)
        .filter(([goodId, qty]) => state.inventory[goodId].qty < qty)
        .map(([goodId]) => goodId);
      const status = plot.condition <= 20 || plot.damage.some((damage) => damage.severity >= 3) ? 'halted-damage'
        : missingInputs.length > 0 ? 'waiting-inputs'
          : !productionStorageFits(state, facility.production) ? 'waiting-storage'
            : operation?.lastStatus || 'ready';
      return {
        plotIndex: plot.index,
        facilityId: facility.id,
        facilityName: facility.name,
        status,
        progressDays: operation?.progressDays || 0,
        cycleDays: facility.production.cycleDays,
        progressBps: Math.floor((operation?.progressDays || 0) * 10000 / facility.production.cycleDays),
        cyclesCompleted: operation?.cyclesCompleted || 0,
        lastCompletedDay: operation?.lastCompletedDay ?? null,
        lastOutput: { ...(operation?.lastOutput || {}) },
        inputs: Object.entries(facility.production.inputs).map(([goodId, requiredQty]) => ({
          goodId,
          name: getGood(goodId).name,
          requiredQty,
          availableQty: state.inventory[goodId].qty,
        })),
        outputs: Object.entries(facility.production.outputs).map(([goodId, qty]) => ({ goodId, name: getGood(goodId).name, qty })),
        blockReason: status === 'halted-damage' ? 'Repair severe property damage before production can resume'
          : status === 'waiting-inputs' ? `Missing ${missingInputs.map((goodId) => getGood(goodId).name).join(', ')}`
            : status === 'waiting-storage' ? 'Not enough storage capacity for the finished goods'
              : null,
      };
    });
  const repairServices = state.plots
    .filter((plot) => plot.owned && !plot.construction && FACILITY_BY_ID[plot.facilityId]?.repairService)
    .map((plot) => {
      const facility = FACILITY_BY_ID[plot.facilityId];
      const service = facility.repairService;
      const availableInputQty = state.inventory[service.inputGoodId].qty;
      const servicesCompleted = plot.serviceActivityCount;
      return {
        plotIndex: plot.index,
        facilityId: facility.id,
        facilityName: facility.name,
        lane: service.lane,
        label: service.label,
        inputGoodId: service.inputGoodId,
        inputGoodName: getGood(service.inputGoodId).name,
        inputQty: service.inputQty,
        availableInputQty,
        servicesCompleted,
        canServe: availableInputQty >= service.inputQty,
        blockReason: availableInputQty >= service.inputQty ? null : `Stock ${getGood(service.inputGoodId).name} before accepting repairs`,
      };
    });
  const casinoPlots = state.plots.filter((plot) => plot.owned && plot.facilityId === 'river-casino');
  const casinoProperties = casinoPlots.map((plot) => {
    const operation = state.casino.operations[String(plot.index)] || {};
    const blockReason = casinoOperatingBlockReason(state, plot);
    return {
      plotIndex: plot.index,
      facilityId: plot.facilityId,
      open: blockReason === null,
      blockReason,
      visitorsToday: operation.visitorsToday || 0,
      lastVisitors: operation.lastVisitors || 0,
      lastGrossWagersCents: operation.lastGrossWagersCents || 0,
      lastPayoutsCents: operation.lastPayoutsCents || 0,
      lastOperatingCostCents: operation.lastOperatingCostCents || 0,
      lastHouseResultCents: operation.lastHouseResultCents || 0,
      lastNetCents: operation.lastNetCents || 0,
      lifetimeVisitors: operation.lifetimeVisitors || 0,
      lifetimeGrossWagersCents: operation.lifetimeGrossWagersCents || 0,
      lifetimePayoutsCents: operation.lifetimePayoutsCents || 0,
      lifetimeOperatingCostCents: operation.lifetimeOperatingCostCents || 0,
      lifetimeNetCents: operation.lifetimeNetCents || 0,
      winningDays: operation.winningDays || 0,
      losingDays: operation.losingDays || 0,
      daysOpen: operation.daysOpen || 0,
      status: blockReason ? 'blocked' : operation.status || 'open',
    };
  });
  const casinoTotals = casinoProperties.reduce((totals, property) => ({
    lifetimeVisitors: totals.lifetimeVisitors + property.lifetimeVisitors,
    lifetimeGrossWagersCents: totals.lifetimeGrossWagersCents + property.lifetimeGrossWagersCents,
    lifetimePayoutsCents: totals.lifetimePayoutsCents + property.lifetimePayoutsCents,
    lifetimeOperatingCostCents: totals.lifetimeOperatingCostCents + property.lifetimeOperatingCostCents,
    lifetimeNetCents: totals.lifetimeNetCents + property.lifetimeNetCents,
    winningDays: totals.winningDays + property.winningDays,
    losingDays: totals.losingDays + property.losingDays,
  }), { lifetimeVisitors: 0, lifetimeGrossWagersCents: 0, lifetimePayoutsCents: 0, lifetimeOperatingCostCents: 0, lifetimeNetCents: 0, winningDays: 0, losingDays: 0 });
  const liveryPlots = state.plots.filter((plot) => plot.owned && !plot.construction && plot.facilityId === 'livery').map((plot) => plot.index);
  const openingElapsedMinutes = openingComplete(state) ? Math.max(0, state.timeMinutes - state.opening.completedMinute) : 0;
  const openingLaterTransactions = openingComplete(state) ? Math.max(0, state.transactions - state.opening.transactionsAtCompletion) : 0;
  const landMarketOpen = landMarketIsOpen(state);
  return {
    day,
    clockMinutes: state.timeMinutes % DAY_MINUTES,
    season,
    rank: RANKS[state.rank],
    shopGoodsAtlas: {
      ...SHOP_GOODS_ATLAS,
      usedCells: GOODS.length,
      remainingCells: SHOP_GOODS_ATLAS.capacity - GOODS.length,
    },
    opening: {
      status: state.opening.status,
      tutorialOfferId: state.opening.tutorialOfferId,
      completedMinute: state.opening.completedMinute,
      transactionsAtCompletion: state.opening.transactionsAtCompletion,
      elapsedMinutes: openingElapsedMinutes,
      laterTransactions: openingLaterTransactions,
      landMarketOpen,
      landMarketBlockReason: landMarketOpen ? null : openingComplete(state)
        ? `Land deeds open in ${Math.max(0, 720 - openingElapsedMinutes)} minutes or after ${Math.max(0, 4 - openingLaterTransactions)} further trades`
        : 'Complete Eli Mercer’s opening fur trade first',
    },
    plots: derivedPlots,
    interiors: {
      supportedFacilityIds: Object.keys(INTERIOR_OPPORTUNITIES),
      opportunities: Object.values(state.interiors.byPlot)
        .sort((left, right) => left.plotIndex - right.plotIndex)
        .map((record) => interiorOpportunityView(state, record)),
      history: state.interiors.history.slice(-30).map((resolution) => ({
        ...resolution,
        inputs: { ...resolution.inputs },
        outputs: { ...resolution.outputs },
      })),
    },
    labor: {
      available: state.labor.available,
      capacity: state.labor.capacity,
      committed: state.labor.capacity - state.labor.available,
      blockReason: state.labor.available > 0 ? null : 'All town labor is assigned to active construction',
    },
    coldStorage: {
      enabled: coldStorageCapacity(state) > 0,
      capacity: coldStorageCapacity(state),
      used: state.inventory['fresh-fish'].qty,
      available: availableColdStorage(state),
      shelfLifeMinutes: refrigeratedShelfLife(state),
      blockReason: coldStorageCapacity(state) > 0 ? null : 'Build an Ice House on an owned non-shop plot to purchase fresh fish',
    },
    meetings: {
      policy: state.meetings.policy,
      policyOptions: MEETING_POLICIES.map((policy) => ({ ...policy })),
      pending: visibleMeetings,
      totalPending: state.meetings.pending.length,
      hiddenPending: state.meetings.pending.length - visibleMeetings.length,
      expired: state.meetings.expired.slice(-20).map((offer) => ({ ...offer })),
      history: state.meetings.history.slice(-30).map((offer) => ({ ...offer })),
      relationships: Object.fromEntries(Object.entries(state.meetings.relationships).map(([profileId, memory]) => [profileId, { ...memory }])),
      lastResolution: state.meetings.lastResolution ? { ...state.meetings.lastResolution } : null,
      profilePoolSize: VENDOR_PROFILES.length,
      profilesUsedThisCycle: state.meetings.profileCycle.length,
    },
    horticulture: {
      catalog: ACCESSORIES.filter((accessory) => accessory.horticulture).map((accessory) => ({
        ...accessory,
        horticulture: { ...accessory.horticulture },
        treeLifecycle: accessory.treeLifecycle ? { ...accessory.treeLifecycle, stages: accessory.treeLifecycle.stages.map((stage) => ({ ...stage, identity: { ...stage.identity } })) } : undefined,
      })),
      placed: horticultureDecorations,
      trees: horticultureDecorations.filter((decoration) => decoration.lifecycle),
      operations: horticultureOperations,
      support: horticultureSupport(state),
      seasonId: season.id,
      gardenerPower: employeeRolePower(state, 'gardener', 'gardens'),
    },
    productiveLand: {
      catalog: productiveFacilities.map((facility) => ({
        facilityId: facility.id,
        name: facility.name,
        description: facility.description || null,
        rank: facility.rank,
        kind: facility.kind,
        productionFireRiskBps: facility.productionFireRiskBps || 0,
        production: facility.production ? {
          cycleDays: facility.production.cycleDays,
          inputs: { ...facility.production.inputs },
          outputs: { ...facility.production.outputs },
        } : null,
        repairService: facility.repairService ? { ...facility.repairService } : null,
        marketAccessGoodIds: [...(facility.marketAccessGoodIds || [])],
      })),
      operations: productionOperations,
      repairServices,
      horseTrade: {
        enabled: liveryPlots.length > 0,
        requiredFacilityId: 'livery',
        liveryPlots,
        roadTravelBonusBps: liveryPlots.length > 0 ? FACILITY_BY_ID.livery.roadTravelBonusBps : 0,
        freightDiscountBps: liveryPlots.length > 0 ? FACILITY_BY_ID.livery.freightDiscountBps : 0,
        blockReason: liveryPlots.length > 0 ? null : 'Build and operate a Livery Stable to trade horses',
      },
    },
    casino: {
      facilityId: 'river-casino',
      name: FACILITY_BY_ID['river-casino'].name,
      ownedPlots: casinoPlots.map((plot) => plot.index),
      open: casinoProperties.some((property) => property.open),
      blockReason: casinoPlots.length === 0 ? 'Build a River Casino & Gambling Hall on an owned non-shop plot'
        : casinoProperties.every((property) => !property.open) ? casinoProperties[0].blockReason
          : null,
      properties: casinoProperties,
      houseIncomeMetrics: casinoTotals,
      tableStakes: {
        minCents: FACILITY_BY_ID['river-casino'].casino.minTableStakeCents,
        maxCents: FACILITY_BY_ID['river-casino'].casino.maxTableStakeCents,
      },
      poker: {
        activeSession: state.poker.activeSession ? JSON.parse(JSON.stringify(state.poker.activeSession)) : null,
        history: state.poker.history.slice(-30).map((session) => JSON.parse(JSON.stringify(session))),
        tableStakeCents: {
          min: FACILITY_BY_ID['river-casino'].casino.minTableStakeCents,
          max: FACILITY_BY_ID['river-casino'].casino.maxTableStakeCents,
        },
        maxExposureCents: state.poker.activeSession?.maxExposureCents ?? null,
        hardMaxExposureCents: POKER_HARD_MAX_EXPOSURE_CENTS,
        escrowCents: state.poker.activeSession?.escrowCents ?? 0,
        currentStackCents: state.poker.activeSession?.currentStackCents ?? 0,
        canSettle: Boolean(state.poker.activeSession?.status === 'active' && pokerSnapshotIsSafe(state.poker.activeSession.engineSnapshot)),
        blockReason: !state.poker.activeSession ? 'No poker session is active'
          : state.poker.activeSession.status === 'frozen' ? state.poker.activeSession.frozenReason
            : pokerSnapshotIsSafe(state.poker.activeSession.engineSnapshot) ? null : 'Finish or settle the current hand before closing the poker session',
        // Read-only compatibility views for older integrations.
        openHands: state.poker.activeSession ? [{ ...state.poker.activeSession }] : [],
        reservedWagersCents: state.poker.activeSession?.escrowCents ?? 0,
        playerLifetimeNetCents: state.poker.history.reduce((sum, session) => sum + session.playerNetCents, 0),
      },
    },
    dockTreatments: {
      catalog: DOCK_TREATMENTS.map((treatment) => ({
        ...treatment,
        unlocked: treatment.rank <= state.rank,
        canPurchase: treatment.rank <= state.rank && state.cashCents >= treatment.costCents,
        placement: { ...treatment.placement },
        animation: { ...treatment.animation },
      })),
      owned: state.dockTreatments.map((owned) => {
        const treatment = dockTreatmentType(owned.treatmentId);
        return { ...owned, name: treatment.name, placement: { ...treatment.placement }, animation: { ...treatment.animation } };
      }),
      serviceBonusBps: state.dockTreatments.reduce((sum, owned) => sum + (owned.plotIndex == null ? 0 : dockTreatmentType(owned.treatmentId).serviceBonusBps || 0), 0),
    },
    employees: {
      roles: EMPLOYEE_ROLES.map((role) => ({ ...role, assignments: [...role.assignments] })),
      candidates: state.employees.candidates.map((candidate) => employeeView(candidate, true)),
      hired: state.employees.hired.map((employee) => employeeView(employee)),
      dailyWagesCents: state.employees.hired.reduce((sum, employee) => sum + employee.wageCents, 0),
      nextRefreshDay: state.employees.nextRefreshDay,
      auditBaseCostCents: 300,
      lastAudit: state.employees.lastAudit ? { ...state.employees.lastAudit } : null,
      effects: Object.fromEntries(EMPLOYEE_ROLES.map((role) => [role.id, employeeRolePower(state, role.id)])),
    },
    townSafety: townSafety(state),
    fireProtection: fireProtection(state),
    displaySlots: displaySlots(state),
    capacity: capacityBulk,
    usedCapacity: usedBulk,
    reservedCapacity: reservedCapacityBulk,
    freeCapacity: freeCapacityBulk,
    storage: {
      capacityBulk,
      usedBulk,
      reservedBulk: reservedCapacityBulk,
      freeBulk: freeCapacityBulk,
    },
    inventoryUnits: GOODS.reduce((sum, good) => sum + state.inventory[good.id].qty, 0),
    netWorthCents: state.cashCents + GOODS.reduce((sum, good) => sum + state.inventory[good.id].qty * state.market[good.id].priceCents, 0),
    popularity: townPopularity(state),
    publicComfort: publicComfort(state),
    supplyRoutes: SUPPLY_ROUTES.map((route) => {
      const isChicago = route.id === 'chicago';
      const freightDiscountBps = isChicago ? chicagoFreightDiscountBps(state) : 0;
      const transportReady = isChicago ? chicagoTransportLevel(state) > 0 : advancedRiverTransportReady(state);
      const routeGateReady = isChicago ? openingComplete(state) : (
        openingComplete(state)
        && state.rank >= route.minRank
        && state.reputation >= route.minReputation
        && state.affluentClientsServed >= route.minAffluentClients
        && transportReady
      );
      const routeGoods = GOODS
        .filter((good) => isChicago ? !good.tags.includes('import') && !good.tags.includes('no-wholesale') : route.goodIds.includes(good.id))
        .map((good) => {
          const quotedUnitCents = isChicago ? (CHICAGO_CATALOG.starterQuotesCents[good.id] || roundTo5(state.market[good.id].priceCents * CHICAGO_CATALOG.premiumBps / 10000)) : null;
          const quoteMinUnitCents = isChicago ? quotedUnitCents : roundTo5(state.market[good.id].priceCents * CUBA_CATALOG.premiumBpsMin / 10000);
          const quoteMaxUnitCents = isChicago ? quotedUnitCents : roundTo5(state.market[good.id].priceCents * CUBA_CATALOG.premiumBpsMax / 10000);
          const blockReason = isChicago ? chicagoAccessBlockReason(state, good)
            : !openingComplete(state) ? 'Complete Eli Mercer’s opening fur trade first'
              : state.rank < route.minRank || state.reputation < route.minReputation || state.affluentClientsServed < route.minAffluentClients ? 'Regional Port rank, 850 reputation, and five affluent clients required'
                : !transportReady ? 'Build and operate a Steamboat Dock for Havana freight'
                  : null;
          return {
            goodId: good.id,
            unlocked: blockReason === null,
            blockReason,
            starter: isChicago && CHICAGO_CATALOG.starterQuotesCents[good.id] != null,
            origin: isChicago && CHICAGO_CATALOG.starterQuotesCents[good.id] != null ? CHICAGO_CATALOG.starterServiceName : route.origin,
            quotedUnitCents,
            quoteMinUnitCents,
            quoteMaxUnitCents,
            freightPerBulkCents: route.freightPerBulkCents,
            freightDiscountBps,
            requiredTransportLevel: isChicago && CHICAGO_CATALOG.starterQuotesCents[good.id] == null ? Math.max(1, good.rank) : 0,
            minimumOrderQty: route.minQuantity,
            minimumOrderBulk: good.bulk * route.minQuantity,
            minimumOrderFits: freeCapacityBulk >= good.bulk * route.minQuantity,
          };
        });
      const readinessGoodId = isChicago ? 'rope' : route.goodIds[0];
      const readinessGood = getGood(readinessGoodId);
      const readinessView = routeGoods.find((good) => good.goodId === readinessGoodId);
      const minimumOrderBulk = readinessGood.bulk * route.minQuantity;
      const estimatedUnitCents = readinessView.quoteMaxUnitCents;
      const estimatedFreightCents = Math.ceil(readinessGood.bulk * route.minQuantity * route.freightPerBulkCents * (10000 - freightDiscountBps) / 10000);
      const estimatedTotalCents = estimatedUnitCents * route.minQuantity + estimatedFreightCents;
      const capacityReady = freeCapacityBulk >= minimumOrderBulk;
      const cashReady = state.cashCents >= estimatedTotalCents;
      const readinessBlockReason = readinessView.blockReason
        ? readinessView.blockReason
        : !capacityReady ? `Reserve ${minimumOrderBulk} free bulk capacity for the minimum order`
          : !cashReady ? `Keep at least ${cashLabel(estimatedTotalCents)} for the conservative minimum-order estimate`
            : null;
      return {
        ...route,
        freightDiscountBps,
        unlocked: routeGateReady,
        transportReady,
        transportLevel: isChicago ? chicagoTransportLevel(state) : transportReady ? 4 : 0,
        reservedBulk: reservedCapacityBulk,
        freeBulk: freeCapacityBulk,
        starterQuotesCents: isChicago ? { ...CHICAGO_CATALOG.starterQuotesCents } : undefined,
        minimumOrderReadiness: {
          goodId: readinessGoodId,
          qty: route.minQuantity,
          bulk: minimumOrderBulk,
          accessReady: readinessView.blockReason === null,
          capacityReady,
          cashReady,
          estimatedUnitCents,
          estimatedTotalCents,
          ready: readinessBlockReason === null,
          blockReason: readinessBlockReason,
        },
        goods: routeGoods,
      };
    }),
    identity,
    ledgerRows,
    activeEvents: state.events.filter((event) => event.startDay <= day && event.endDay >= day),
    forecastEvents: state.events.filter((event) => event.startDay > day),
    ambientActors: state.ambientActors.map((actor) => ({ ...actor })),
    arrivals,
  };
}

export function assertInvariants(state) {
  const fail = (message) => { throw new Error(`Simulation invariant failed: ${message}`); };
  const facilityCatalog = Object.values(FACILITY_BY_ID);
  if (facilityCatalog.filter((facility) => facility.kind !== 'shop').length < 22) fail('non-shop facility catalog is too small');
  if (ACCESSORIES.length < 28) fail('accessory catalog is too small');
  if (GOODS.length < 30) fail('goods catalog is too small');
  if (new Set(GOODS.map((good) => good.id)).size !== GOODS.length) fail('good ids must be unique');
  GOODS.forEach((good) => {
    if (!Number.isInteger(good.rank) || good.rank < 0 || !Array.isArray(good.tags) || good.tags.length === 0) fail(`${good.id} catalog metadata is invalid`);
    if (!Number.isInteger(good.atlasFrame) || good.atlasFrame < 0 || good.atlasFrame >= SHOP_GOODS_ATLAS.capacity) fail(`${good.id} shop atlas frame is invalid`);
    for (const probabilityField of ['trafficSupplyChanceBps', 'trafficDemandChanceBps']) {
      if (good[probabilityField] != null && (!Number.isInteger(good[probabilityField]) || good[probabilityField] <= 0 || good[probabilityField] > 10000)) fail(`${good.id} ${probabilityField} is invalid`);
    }
    if (good.perishable && (!Number.isInteger(good.shelfLifeMinutes) || good.shelfLifeMinutes <= 0)) fail(`${good.id} perishable shelf life is invalid`);
    (good.requiredFacilityIds || []).forEach((facilityId) => {
      if (!FACILITY_BY_ID[facilityId] || FACILITY_BY_ID[facilityId].kind === 'shop') fail(`${good.id} requires an invalid market facility`);
    });
  });
  if (SHOP_GOODS_ATLAS.columns !== 7 || SHOP_GOODS_ATLAS.rows !== 6 || SHOP_GOODS_ATLAS.capacity !== 42 || GOODS.length !== 39) fail('shop goods atlas must reserve 39 sequential cells in its 7x6 plan');
  if (GOODS.some((good, index) => good.atlasFrame !== index)) fail('shop goods atlas frames must follow catalog order');
  const artwork = getGood('artwork');
  const artworkTraffic = TRAFFIC_TYPES.filter((type) => type.supply.includes('artwork') || type.demand.includes('artwork'));
  if (!artwork || artwork.atlasFrame !== 38 || artwork.rank < 3 || artwork.baseCents < 5000 || !artwork.requiredFacilityIds?.includes('art-gallery') || !artwork.tags.includes('rare') || !artwork.tags.includes('luxury')) fail('artwork catalog progression is invalid');
  if (artwork.trafficSupplyChanceBps >= 2500 || artwork.trafficDemandChanceBps >= 2500 || !artworkTraffic.some((type) => type.supply.includes('artwork')) || !artworkTraffic.some((type) => type.demand.includes('artwork')) || artworkTraffic.some((type) => type.wealth < 2)) fail('artwork traffic must remain rare and affluent');
  const galleryCuration = FACILITY_BY_ID['art-gallery']?.curation;
  if (galleryCuration?.goodId !== 'artwork' || !Number.isInteger(galleryCuration.chanceBps) || galleryCuration.chanceBps <= 0 || galleryCuration.chanceBps >= 2500 || !Number.isInteger(galleryCuration.minCostBps) || !Number.isInteger(galleryCuration.maxCostBps) || galleryCuration.minCostBps >= galleryCuration.maxCostBps || !Number.isInteger(galleryCuration.maxStock) || galleryCuration.maxStock <= 0) fail('gallery paid-curation metadata is invalid');
  const starterQuoteEntries = Object.entries(CHICAGO_CATALOG.starterQuotesCents);
  if (starterQuoteEntries.length !== 2 || !CHICAGO_CATALOG.starterQuotesCents.rope || !CHICAGO_CATALOG.starterQuotesCents.provisions) fail('Chicago starter catalog must contain only rope and dried provisions');
  starterQuoteEntries.forEach(([goodId, quotedUnitCents]) => {
    const good = getGood(goodId);
    if (!good || good.tags.includes('import') || good.tags.includes('no-wholesale')) fail(`${goodId} is invalid starter freight`);
    if (!Number.isInteger(quotedUnitCents) || quotedUnitCents <= 0) fail(`${goodId} starter freight quote is invalid`);
  });
  if (!CHICAGO_CATALOG.starterServiceName) fail('Chicago starter freight service name is missing');
  TRAFFIC_TYPES.forEach((type) => {
    [...type.supply, ...type.demand].forEach((goodId) => { if (!getGood(goodId)) fail(`${type.id} references unknown good ${goodId}`); });
  });
  if (new Set(facilityCatalog.map((facility) => facility.id)).size !== facilityCatalog.length) fail('facility ids must be unique');
  const horse = getGood('horse');
  const livery = FACILITY_BY_ID.livery;
  if (!horse?.requiredFacilityIds?.includes('livery') || !livery?.marketAccessGoodIds?.includes('horse') || livery.kind === 'shop') fail('horse trade must require a non-shop livery');
  if (![1, 2, 4, 5].every((plotIndex) => PLOT_OPTIONS[plotIndex].includes('livery')) || PLOT_OPTIONS[3].includes('livery')) fail('livery plot options are invalid');
  if (!TRAFFIC_TYPES.some((type) => type.supply.includes('horse')) || !TRAFFIC_TYPES.some((type) => type.demand.includes('horse'))) fail('horse must have automatic market supply and demand');
  const productiveFacilities = facilityCatalog.filter((facility) => facility.production);
  if (productiveFacilities.length < 3) fail('productive-land facility catalog is too small');
  productiveFacilities.forEach((facility) => {
    if (facility.kind === 'shop' || !facility.description || !Number.isInteger(facility.productionFireRiskBps) || facility.productionFireRiskBps < 0) fail(`${facility.id} production metadata is invalid`);
    if (!Number.isInteger(facility.production.cycleDays) || facility.production.cycleDays <= 0) fail(`${facility.id} production cycle is invalid`);
    for (const [goodId, qty] of [...Object.entries(facility.production.inputs), ...Object.entries(facility.production.outputs)]) {
      if (!getGood(goodId) || !Number.isInteger(qty) || qty <= 0) fail(`${facility.id} production recipe is invalid`);
    }
    const permittedPlots = [1, 2, 4, 5].filter((plotIndex) => PLOT_OPTIONS[plotIndex].includes(facility.id));
    if (permittedPlots.length === 0 || PLOT_OPTIONS[3].includes(facility.id)) fail(`${facility.id} productive plot options are invalid`);
  });
  const repairFacilities = facilityCatalog.filter((facility) => facility.repairService);
  if (!repairFacilities.some((facility) => facility.id === 'repair-workshop') || !repairFacilities.some((facility) => facility.id === 'shipwright')) fail('road and river repair businesses are required');
  repairFacilities.forEach((facility) => {
    const service = facility.repairService;
    const inputGood = getGood(service.inputGoodId);
    if (!inputGood || !['road', 'river'].includes(service.lane) || !Number.isInteger(service.inputQty) || service.inputQty <= 0 || !service.label) fail(`${facility.id} repair-service metadata is invalid`);
    if (!facility.production?.outputs?.[service.inputGoodId]) fail(`${facility.id} must produce its repair-service input`);
    if (!Number.isInteger(facility.serviceCents) || facility.serviceCents <= inputGood.baseCents) fail(`${facility.id} repair service does not cover one normal part`);
    if (!facility.serviceTags?.length || facility.serviceTags.some((typeId) => TRAFFIC_BY_ID[typeId]?.lane !== service.lane)) fail(`${facility.id} repair traffic lane is invalid`);
  });
  for (const goodId of ['canoe', 'oars-fittings', 'wagon-parts']) {
    if (!getGood(goodId) || !TRAFFIC_TYPES.some((type) => type.supply.includes(goodId)) || !TRAFFIC_TYPES.some((type) => type.demand.includes(goodId))) fail(`${goodId} must participate in automatic supply and demand`);
  }
  const casinoFacility = FACILITY_BY_ID['river-casino'];
  if (!casinoFacility || casinoFacility.kind === 'shop' || casinoFacility.rank < 3 || casinoFacility.costCents < 30000 || casinoFacility.upkeepCents < 300 || casinoFacility.laborRequired < 4) fail('late-game casino facility metadata is invalid');
  if (PLOT_OPTIONS[3].includes(casinoFacility.id) || ![1, 2, 4, 5].every((plotIndex) => PLOT_OPTIONS[plotIndex].includes(casinoFacility.id))) fail('casino must remain a non-shop plot option');
  for (const field of ['minReputation', 'minSafety', 'averageStakeCents', 'houseEdgeBps', 'varianceBps', 'tableOperatingCents', 'minTableStakeCents', 'maxTableStakeCents', 'maxPayoutBps']) {
    if (!Number.isInteger(casinoFacility.casino?.[field]) || casinoFacility.casino[field] <= 0) fail(`casino ${field} is invalid`);
  }
  if (casinoFacility.casino.varianceBps <= casinoFacility.casino.houseEdgeBps || casinoFacility.casino.minTableStakeCents >= casinoFacility.casino.maxTableStakeCents) fail('casino variance or table stakes are invalid');
  if (!casinoFacility.serviceTags.every((typeId) => TRAFFIC_BY_ID[typeId]?.wealth >= casinoFacility.visitorWealthMin)) fail('casino traffic must meet its wealth gate');
  Object.entries(CHICAGO_CATALOG.transportUnlocks).forEach(([facilityId, freightLevel]) => {
    const facility = FACILITY_BY_ID[facilityId];
    if (!facility || facility.kind === 'shop') fail(`${facilityId} is not a valid freight-unlock facility`);
    if (!Number.isInteger(freightLevel) || freightLevel < 1 || freightLevel > 4) fail(`${facilityId} freight-unlock level is invalid`);
  });
  CUBA_CATALOG.requiredFacilityIds.forEach((facilityId) => {
    if (FACILITY_BY_ID[facilityId]?.kind !== 'river') fail(`${facilityId} is not valid advanced river transport`);
  });
  if (new Set(ACCESSORIES.map((accessory) => accessory.id)).size !== ACCESSORIES.length) fail('accessory ids must be unique');
  const lifecycleTrees = ACCESSORIES.filter((accessory) => accessory.treeLifecycle);
  if (lifecycleTrees.length < 1) fail('a long-lived tree lifecycle accessory is required');
  lifecycleTrees.forEach((accessory) => {
    const stages = accessory.treeLifecycle.stages;
    if (!Array.isArray(stages) || stages.map((stage) => stage.id).join(',') !== 'sapling,young,mature,ancient,decline,snag,fallen') fail(`${accessory.id} lifecycle stages are invalid`);
    stages.forEach((stage, index) => {
      if (!Number.isInteger(stage.minAgeDays) || stage.minAgeDays < 0 || index > 0 && stage.minAgeDays <= stages[index - 1].minAgeDays) fail(`${accessory.id} lifecycle timing is invalid`);
      if (![stage.dailyCents, stage.stormRiskBps, stage.fireRiskBps, stage.visualScale, stage.visualFrame].every(Number.isFinite)) fail(`${accessory.id} lifecycle effects are invalid`);
    });
    if (stages.find((stage) => stage.id === 'decline').minAgeDays < accessory.treeLifecycle.gameYearDays * 40 || stages.find((stage) => stage.id === 'fallen').minAgeDays < accessory.treeLifecycle.gameYearDays * 50) fail(`${accessory.id} must remain living for many game years`);
  });
  if (VENDOR_PROFILES.length < 64) fail('vendor profile catalog is too small');
  if (new Set(VENDOR_PROFILES.map((profile) => profile.id)).size !== VENDOR_PROFILES.length) fail('vendor profile ids must be unique');
  if (new Set(VENDOR_PROFILES.map((profile) => profile.name)).size !== VENDOR_PROFILES.length) fail('vendor profile names must be unique');
  const originalPortraits = VENDOR_PROFILES.filter(profile => !profile.portraitReuseOf);
  if (new Set(originalPortraits.map(profile => `${profile.portrait.atlasId}:${profile.portrait.frame}`)).size !== originalPortraits.length) fail('original vendor portrait assignments must be unique');
  VENDOR_PROFILES.forEach((profile) => {
    if (profile.portraitReuseOf) {
      const original = VENDOR_PROFILES.find(item => item.id === profile.portraitReuseOf && !item.portraitReuseOf);
      if (!original || JSON.stringify(original.portrait) !== JSON.stringify(profile.portrait)) fail(`${profile.id} portrait reuse must reference matching existing artwork`);
    }
    if (!profile.personality || !Number.isInteger(profile.patience) || profile.patience <= 0) fail(`${profile.id} negotiation metadata is invalid`);
    if (!['a', 'b', 'c', 'd', 'e', 'f'].includes(profile.portrait?.atlasId) || !Number.isInteger(profile.portrait.frame)) fail(`${profile.id} portrait metadata is invalid`);
    const expectedColumns = profile.portrait.atlasId === 'a' ? 3 : 4;
    const expectedRows = profile.portrait.atlasId === 'a' ? 2 : 3;
    if (profile.portrait.columns !== expectedColumns || profile.portrait.rows !== expectedRows || profile.portrait.frame < 0 || profile.portrait.frame >= expectedColumns * expectedRows) fail(`${profile.id} portrait atlas geometry is invalid`);
    if (profile.portrait.honestyCue !== false) fail(`${profile.id} portrait must not encode honesty`);
    if (!profile.conversation?.greeting || !profile.conversation?.counter || !profile.conversation?.accept || !profile.conversation?.decline) fail(`${profile.id} conversation metadata is incomplete`);
    if (profile.trafficTypes.some((typeId) => !TRAFFIC_BY_ID[typeId])) fail(`${profile.id} references unknown traffic`);
  });
  facilityCatalog.forEach((facility) => {
    if (!Number.isInteger(facility.laborRequired) || facility.laborRequired < 0) fail(`${facility.id} labor requirement is invalid`);
    if (!Number.isInteger(facility.laborCostCents) || facility.laborCostCents < 0) fail(`${facility.id} labor cost is invalid`);
    if (facility.id !== 'shop-shack' && (facility.laborRequired === 0 || facility.laborCostCents === 0)) fail(`${facility.id} must have paid labor`);
  });
  const expectedInteriorFacilityIds = ['art-gallery', 'livery', 'pottery-kiln', 'repair-workshop', 'shipwright'];
  if (Object.keys(INTERIOR_OPPORTUNITIES).sort().join(',') !== expectedInteriorFacilityIds.join(',')) fail('interior opportunity facility catalog is incomplete');
  Object.entries(INTERIOR_OPPORTUNITIES).forEach(([facilityId, catalog]) => {
    const facility = FACILITY_BY_ID[facilityId];
    if (!facility || facility.kind === 'shop' || !catalog.title || !Array.isArray(catalog.choices) || catalog.choices.length < 2 || catalog.choices.length > 3) fail(`${facilityId} interior opportunity catalog is invalid`);
    if (new Set(catalog.choices.map((choice) => choice.id)).size !== catalog.choices.length) fail(`${facilityId} interior choice ids must be unique`);
    catalog.choices.forEach((choice) => {
      if (!choice.name || !choice.description || !Number.isInteger(choice.cashCostCents) || choice.cashCostCents < 0 || !Number.isInteger(choice.cooldownMinutes) || choice.cooldownMinutes < DAY_MINUTES || !Array.isArray(choice.outcomes) || choice.outcomes.length < 2) fail(`${choice.id} interior choice metadata is invalid`);
      if (choice.cashCostCents === 0 && Object.keys(choice.inputs || {}).length === 0) fail(`${choice.id} must commit cash or inventory`);
      for (const [goodId, qty] of [...Object.entries(choice.inputs || {}), ...Object.entries(choice.requires || {})]) {
        if (!getGood(goodId) || !Number.isInteger(qty) || qty <= 0) fail(`${choice.id} interior input is invalid`);
      }
      if (new Set(choice.outcomes.map((outcome) => outcome.id)).size !== choice.outcomes.length) fail(`${choice.id} outcome ids must be unique`);
      choice.outcomes.forEach((outcome) => {
        if (!outcome.label || !Number.isInteger(outcome.weight) || outcome.weight <= 0 || !Number.isInteger(outcome.cashRewardCents || 0) || !Number.isInteger(outcome.reputationDelta || 0) || !Number.isInteger(outcome.conditionDelta || 0)) fail(`${choice.id} outcome metadata is invalid`);
        for (const [goodId, qty] of Object.entries(outcome.outputs || {})) {
          if (!getGood(goodId) || !Number.isInteger(qty) || qty <= 0) fail(`${choice.id} outcome output is invalid`);
        }
      });
      if (!choice.outcomes.some((outcome) => (outcome.cashRewardCents || 0) > 0 || Object.keys(outcome.outputs || {}).length > 0 || (outcome.reputationDelta || 0) > 0 || (outcome.conditionDelta || 0) > 0)) fail(`${choice.id} lacks a possible reward`);
      if (!choice.outcomes.some((outcome) => (outcome.cashRewardCents || 0) < choice.cashCostCents || (outcome.reputationDelta || 0) < 0 || (outcome.conditionDelta || 0) < 0)) fail(`${choice.id} lacks meaningful risk`);
    });
  });
  const marketGarden = FACILITY_BY_ID['market-garden'];
  const windmill = FACILITY_BY_ID['working-windmill'];
  if (!marketGarden?.garden || !getGood(marketGarden.garden.goodId) || marketGarden.kind === 'shop') fail('market-garden facility metadata is invalid');
  if (!windmill?.gardenSupport || windmill.kind === 'shop' || Object.values(windmill.gardenSupport).some((value) => !Number.isInteger(value) || value <= 0)) fail('working-windmill garden support is invalid');
  if (PLOT_OPTIONS[3].includes('market-garden') || PLOT_OPTIONS[3].includes('working-windmill')) fail('garden facilities cannot replace the Plot 3 shop');
  if (DOCK_TREATMENTS.length < 8 || new Set(DOCK_TREATMENTS.map((item) => item.id)).size !== DOCK_TREATMENTS.length) fail('dock treatment catalog is invalid');
  DOCK_TREATMENTS.forEach((treatment) => {
    if (!treatment.placement?.movable || !treatment.animation?.type || !Number.isInteger(treatment.costCents) || treatment.costCents <= 0) fail(`${treatment.id} dock treatment metadata is invalid`);
  });
  if (EMPLOYEE_ROLES.length !== 9 || new Set(EMPLOYEE_ROLES.map((role) => role.id)).size !== EMPLOYEE_ROLES.length) fail('employee role catalog is invalid');
  if (new Set(EMPLOYEE_TRAITS.map((trait) => trait.id)).size !== EMPLOYEE_TRAITS.length) fail('employee trait ids must be unique');
  if (DAMAGE_TYPES.length !== 8 || new Set(DAMAGE_TYPES.map((type) => type.id)).size !== DAMAGE_TYPES.length) fail('damage type catalog is invalid');
  for (const [plotIndex, choices] of Object.entries(PLOT_OPTIONS)) {
    choices.forEach((facilityId) => {
      const facility = FACILITY_BY_ID[facilityId];
      if (!facility) fail(`Plot ${plotIndex} refers to an unknown facility`);
      if ((Number(plotIndex) === 3) !== (facility.kind === 'shop')) fail(`Plot ${plotIndex} has an invalid shop choice`);
    });
  }
  if (!Number.isInteger(state.cashCents) || state.cashCents < 0) fail('cash must be nonnegative integer cents');
  if (!Number.isInteger(state.timeMinutes) || state.timeMinutes < 0) fail('time must be nonnegative integer minutes');
  if (!Number.isInteger(state.rank) || state.rank < 0 || state.rank >= RANKS.length) fail('rank is invalid');
  if (!state.opening || !['awaiting-fur-trader', 'completed'].includes(state.opening.status)) fail('opening status is invalid');
  if (state.opening.status === 'awaiting-fur-trader') {
    if (state.opening.completedMinute !== null || state.opening.transactionsAtCompletion !== null) fail('pending opening cannot have completion counters');
    if (state.transactions !== 0 || GOODS.some((good) => state.inventory[good.id].qty !== 0)) fail('opening inventory and transactions must remain empty until Eli is accepted');
    if (state.recovery.active) fail('recovery cannot activate before the opening trade');
  } else if (!Number.isInteger(state.opening.completedMinute) || !Number.isInteger(state.opening.transactionsAtCompletion) || state.opening.transactionsAtCompletion < 0 || state.opening.transactionsAtCompletion > state.transactions) {
    fail('completed opening counters are invalid');
  }
  if (!state.labor || !Number.isInteger(state.labor.available) || !Number.isInteger(state.labor.capacity)) fail('labor pool must use integer units');
  if (state.labor.capacity <= 0 || state.labor.available < 0 || state.labor.available > state.labor.capacity) fail('labor availability is invalid');
  if (!Number.isInteger(state.dailyGoldBought) || state.dailyGoldBought < 0 || state.dailyGoldBought > 2) fail('daily rare-gold limit is invalid');
  if (!state.horticulture || !Number.isInteger(state.horticulture.lastProcessedDay) || state.horticulture.lastProcessedDay < 1 || state.horticulture.lastProcessedDay > currentDay(state) || !state.horticulture.operations || typeof state.horticulture.operations !== 'object') fail('horticulture lifecycle state is invalid');
  Object.entries(state.horticulture.operations).forEach(([plotIndex, operation]) => {
    if (!['1', '2', '4', '5'].includes(plotIndex) || operation.plotIndex !== Number(plotIndex)) fail('market-garden operation targets an invalid plot');
    for (const field of ['totalHarvests', 'totalYieldUnits', 'lastYieldUnits']) {
      if (!Number.isInteger(operation[field]) || operation[field] < 0) fail(`market-garden ${field} is invalid`);
    }
    if (!Number.isInteger(operation.lastProcessedDay) || operation.lastProcessedDay < 1 || !['planting', 'growing', 'harvest', 'protected-harvest', 'fallow'].includes(operation.phase)) fail('market-garden phase state is invalid');
  });
  if (!state.production || !Number.isInteger(state.production.lastProcessedDay) || state.production.lastProcessedDay < 1 || state.production.lastProcessedDay > currentDay(state) || !state.production.operations || typeof state.production.operations !== 'object') fail('productive-land state is invalid');
  Object.entries(state.production.operations).forEach(([plotIndex, operation]) => {
    const plot = state.plots[Number(plotIndex) - 1];
    const facility = FACILITY_BY_ID[operation.facilityId];
    if (!['1', '2', '4', '5'].includes(plotIndex) || operation.plotIndex !== Number(plotIndex) || !plot?.owned || plot.construction || plot.facilityId !== operation.facilityId || !facility?.production) fail('production operation targets an invalid facility');
    if (!Number.isInteger(operation.progressDays) || operation.progressDays < 0 || operation.progressDays >= facility.production.cycleDays) fail('production progress is invalid');
    if (!Number.isInteger(operation.cyclesCompleted) || operation.cyclesCompleted < 0 || !Number.isInteger(operation.lastProcessedDay) || operation.lastProcessedDay < 1) fail('production history is invalid');
    if (!['idle', 'working', 'produced', 'waiting-inputs', 'waiting-storage', 'halted-damage'].includes(operation.lastStatus)) fail('production status is invalid');
  });
  if (!state.casino || !Number.isInteger(state.casino.lastProcessedDay) || state.casino.lastProcessedDay < 1 || state.casino.lastProcessedDay > currentDay(state) || !state.casino.operations || typeof state.casino.operations !== 'object') fail('casino state is invalid');
  Object.entries(state.casino.operations).forEach(([plotIndex, operation]) => {
    const plot = state.plots[Number(plotIndex) - 1];
    if (!['1', '2', '4', '5'].includes(plotIndex) || operation.plotIndex !== Number(plotIndex) || operation.facilityId !== 'river-casino' || plot?.facilityId !== 'river-casino' || plot.construction) fail('casino operation targets an invalid plot');
    for (const field of ['visitorsToday', 'wealthUnitsToday', 'lastVisitors', 'lastGrossWagersCents', 'lastPayoutsCents', 'lastOperatingCostCents', 'lifetimeVisitors', 'lifetimeGrossWagersCents', 'lifetimePayoutsCents', 'lifetimeOperatingCostCents', 'winningDays', 'losingDays', 'daysOpen']) {
      if (!Number.isInteger(operation[field]) || operation[field] < 0) fail(`casino ${field} is invalid`);
    }
    for (const field of ['lastHouseResultCents', 'lastNetCents', 'lifetimeNetCents']) {
      if (!Number.isInteger(operation[field])) fail(`casino ${field} is invalid`);
    }
    if (!Number.isInteger(operation.lastProcessedDay) || operation.lastProcessedDay < 1 || !['open', 'blocked', 'underfunded'].includes(operation.status)) fail('casino operating status is invalid');
  });
  if (!state.interiors || !Number.isInteger(state.interiors.nextOpportunityId) || state.interiors.nextOpportunityId < 1 || !state.interiors.byPlot || typeof state.interiors.byPlot !== 'object' || !Array.isArray(state.interiors.history)) fail('interior opportunity state is invalid');
  const activeInteriorRecords = Object.entries(state.interiors.byPlot);
  const allInteriorIds = [...activeInteriorRecords.map(([, record]) => record.opportunityId), ...state.interiors.history.map((resolution) => resolution.opportunityId)];
  if (new Set(allInteriorIds).size !== allInteriorIds.length) fail('interior opportunity ids must be unique');
  activeInteriorRecords.forEach(([plotKey, record]) => {
    const plot = state.plots[Number(plotKey) - 1];
    const catalog = interiorOpportunityCatalog(record.facilityId);
    if (!['1', '2', '4', '5'].includes(plotKey) || record.plotIndex !== Number(plotKey) || !plot?.owned || plot.construction || plot.facilityId !== record.facilityId || !catalog) fail('interior opportunity targets an invalid building');
    if (typeof record.opportunityId !== 'string' || !record.opportunityId || !Number.isInteger(record.cycle) || record.cycle < 1 || !Number.isInteger(record.seed) || record.seed < 0 || !Number.isInteger(record.createdMinute) || record.createdMinute < 0 || record.createdMinute > state.timeMinutes || !Number.isInteger(record.availableMinute) || record.availableMinute < record.createdMinute) fail('interior opportunity identity or timing is invalid');
    if (!Array.isArray(record.choiceIds) || record.choiceIds.length < 2 || record.choiceIds.length > 3 || new Set(record.choiceIds).size !== record.choiceIds.length || record.choiceIds.some((choiceId) => !catalog.choices.some((choice) => choice.id === choiceId))) fail('interior opportunity choices are invalid');
    if (!['available', 'cooldown'].includes(record.status) || record.status === 'available' && record.availableMinute > state.timeMinutes || record.status === 'cooldown' && record.availableMinute <= state.timeMinutes) fail('interior opportunity availability is invalid');
  });
  state.interiors.history.forEach((resolution) => {
    const catalog = interiorOpportunityCatalog(resolution.facilityId);
    const choice = catalog?.choices.find((candidate) => candidate.id === resolution.choiceId);
    const outcome = choice?.outcomes.find((candidate) => candidate.id === resolution.outcomeId);
    if (!catalog || !choice || !outcome || ![1, 2, 4, 5].includes(resolution.plotIndex) || !Number.isInteger(resolution.cycle) || resolution.cycle < 1 || !Number.isInteger(resolution.seed) || !Number.isInteger(resolution.resolvedMinute) || resolution.resolvedMinute < 0 || resolution.resolvedMinute > state.timeMinutes) fail('interior opportunity history is invalid');
    if (![resolution.cashCostCents, resolution.cashRewardCents, resolution.netCashCents, resolution.reputationDelta, resolution.conditionDelta].every(Number.isInteger) || resolution.netCashCents !== resolution.cashRewardCents - resolution.cashCostCents || !resolution.inputs || !resolution.outputs) fail('interior opportunity resolution accounting is invalid');
  });
  state.ledger.filter((entry) => entry.kind === 'interior-opportunity').forEach((entry) => {
    if (![entry.cashCostCents, entry.cashRewardCents, entry.totalCents].every(Number.isInteger) || entry.totalCents !== entry.cashRewardCents - entry.cashCostCents || !entry.inputs || !entry.outputs) fail('interior opportunity ledger entry is invalid');
  });
  if (!state.poker || !Number.isInteger(state.poker.nextSessionId) || state.poker.nextSessionId < 1 || !Object.hasOwn(state.poker, 'activeSession') || !Array.isArray(state.poker.history)) fail('poker session state is invalid');
  const pokerSessions = [...(state.poker.activeSession ? [state.poker.activeSession] : []), ...state.poker.history];
  if (new Set(pokerSessions.map((session) => session.sessionId)).size !== pokerSessions.length) fail('poker session ids must be unique');
  pokerSessions.forEach((session) => {
    if (typeof session.sessionId !== 'string' || !session.sessionId || typeof session.nonce !== 'string' || !session.nonce) fail('poker session identity is invalid');
    const ordinaryBuyIn = session.buyInCents >= casinoFacility.casino.minTableStakeCents && session.buyInCents <= casinoFacility.casino.maxTableStakeCents;
    if (!Number.isInteger(session.buyInCents) || session.buyInCents < 0 || !Number.isInteger(session.escrowCents) || session.escrowCents !== session.buyInCents || !ordinaryBuyIn && !session.migratedLegacy) fail('poker session buy-in or escrow is invalid');
    if (!Number.isInteger(session.currentStackCents) || session.currentStackCents < 0 || !Number.isInteger(session.maxExposureCents) || session.maxExposureCents < session.buyInCents || session.currentStackCents > session.maxExposureCents || session.maxExposureCents > POKER_HARD_MAX_EXPOSURE_CENTS && !session.migratedLegacy) fail('poker session stack or exposure is invalid');
    if (![1, 2, 4, 5].includes(session.plotIndex) || !Number.isInteger(session.openedMinute) || session.openedMinute < 0 || session.openedMinute > state.timeMinutes || !Number.isInteger(session.checkpointMinute) || session.checkpointMinute < session.openedMinute || session.checkpointMinute > state.timeMinutes) fail('poker session plot or timing is invalid');
    if (!Number.isInteger(session.revision) || session.revision < -1) fail('poker session revision is invalid');
    if (session.engineSnapshot) {
      try {
        clonePokerSnapshot(session.engineSnapshot, session.revision, session.currentStackCents);
      } catch {
        fail('poker session engine snapshot is invalid');
      }
      if (session.engineSnapshot.tableChips < session.currentStackCents || session.engineSnapshot.tableChips > session.maxExposureCents) fail('poker session table chips exceed declared exposure');
    } else if (!session.migratedLegacy && !(session === state.poker.activeSession && session.revision === -1 && session.status === 'active')) {
      fail('poker session engine snapshot is required');
    }
    if (session === state.poker.activeSession) {
      if (!['active', 'frozen'].includes(session.status)) fail('active poker session status is invalid');
      if (session.status === 'frozen' && (!Number.isInteger(session.frozenMinute) || typeof session.frozenReason !== 'string' || !session.frozenReason)) fail('frozen poker recovery state is invalid');
    } else {
      if (!['settled', 'aborted'].includes(session.status) || !Number.isInteger(session.settledMinute) || session.settledMinute < session.checkpointMinute || session.creditedCents !== session.currentStackCents || session.playerNetCents !== session.creditedCents - session.buyInCents) fail('settled poker session is invalid');
    }
  });
  state.ledger.filter((entry) => entry.kind === 'poker-session').forEach((entry) => {
    if (![entry.playerCents, entry.escrowCents, entry.pokerHouseCents, entry.autonomousHouseCents, entry.totalCents].every(Number.isInteger)) fail('poker session ledger cents must be integers');
    if (entry.playerCents + entry.escrowCents + entry.pokerHouseCents !== 0 || entry.autonomousHouseCents !== 0 || entry.totalCents !== entry.playerCents) fail('poker session ledger entry is not balanced');
  });
  state.ledger.filter((entry) => entry.kind === 'poker-bankroll').forEach((entry) => {
    if (!Number.isInteger(entry.playerCents) || !Number.isInteger(entry.houseCents) || entry.playerCents + entry.houseCents !== 0 || entry.totalCents !== entry.playerCents) fail('legacy poker ledger entry is not balanced');
  });
  if (!Array.isArray(state.plots) || state.plots.length !== 5) fail('exactly five plots are required');
  state.plots.forEach((plot, index) => {
    const facility = FACILITY_BY_ID[plot.facilityId];
    if (typeof plot.owned !== 'boolean') fail(`Plot ${plot.index} ownership is invalid`);
    if (plot.deedCostCents !== PLOT_DEEDS[plot.index].deedCostCents) fail(`Plot ${plot.index} deed cost is invalid`);
    if (index === 2 && !plot.owned) fail('Plot 3 must remain owned');
    if (!plot.owned && (plot.facilityId || plot.construction)) fail(`unowned Plot ${plot.index} cannot contain construction`);
    if (facility && !PLOT_OPTIONS[plot.index].includes(facility.id)) fail(`facility is not allowed on Plot ${plot.index}`);
    if (index === 2 && facility?.kind !== 'shop') fail('Plot 3 must be the sole shop');
    if (index !== 2 && facility?.kind === 'shop') fail('shops are forbidden outside Plot 3');
    if (facility && (!Number.isInteger(plot.builtMinute) || plot.builtMinute < START_MINUTE || plot.builtMinute > state.timeMinutes)) fail(`Plot ${plot.index} built minute is invalid`);
    if (!facility && plot.builtMinute !== null) fail(`vacant Plot ${plot.index} cannot have a built minute`);
    if (!Number.isInteger(plot.serviceActivityCount) || plot.serviceActivityCount < 0) fail(`Plot ${plot.index} service activity is invalid`);
    const maturity = interiorMaturitySnapshot(state, plot);
    if (maturity && !['beginner', 'intermediate', 'complete'].includes(maturity.stage)) fail(`Plot ${plot.index} interior maturity is invalid`);
    if (!Number.isInteger(plot.condition) || plot.condition < 0 || plot.condition > 100) fail(`Plot ${plot.index} condition is invalid`);
    if (!Number.isInteger(plot.neglectDays) || plot.neglectDays < 0 || !Array.isArray(plot.damage)) fail(`Plot ${plot.index} neglect state is invalid`);
    if (!plot.owned && plot.damage.length > 0) fail(`unowned Plot ${plot.index} cannot have damage`);
    const plotDamageIds = new Set();
    plot.damage.forEach((damage) => {
      if (plotDamageIds.has(damage.id) || !damageType(damage.typeId)) fail(`Plot ${plot.index} damage is invalid`);
      plotDamageIds.add(damage.id);
      if (!Number.isInteger(damage.severity) || damage.severity < 1 || damage.severity > 3 || !Number.isInteger(damage.repairCostCents) || damage.repairCostCents <= 0) fail(`Plot ${plot.index} damage severity or cost is invalid`);
    });
    if (plot.construction) {
      if (!Number.isInteger(plot.construction.phase) || plot.construction.phase < 1 || plot.construction.phase > plot.construction.phasesTotal) fail('construction phase is invalid');
      if (plot.construction.phasesTotal === 4 && !PHASE_NAMES.includes(plot.construction.phaseName)) fail('large build phase name is invalid');
      if (!Number.isInteger(plot.construction.laborRequired) || plot.construction.laborRequired <= 0) fail('active construction must commit labor');
      if (!Number.isInteger(plot.construction.laborCostCents) || plot.construction.laborCostCents <= 0) fail('active construction must have paid labor');
      if (!plot.construction.materialsType) fail('active construction must name its materials');
    }
  });
  const committedLabor = state.plots.reduce((sum, plot) => sum + (plot.construction?.laborRequired || 0), 0);
  if (state.labor.available + committedLabor !== state.labor.capacity) fail('labor pool does not reconcile with active construction');
  for (const good of GOODS) {
    const item = state.inventory[good.id];
    const market = state.market[good.id];
    if (!item || !market) fail(`missing state for ${good.id}`);
    ['qty', 'avgCostCents', 'listPriceCents', 'buyCeilingCents'].forEach((field) => {
      if (!Number.isInteger(item[field]) || item[field] < 0) fail(`${good.id}.${field} must be a nonnegative integer`);
    });
    if (item.autoStockTargetQty != null && (!Number.isSafeInteger(item.autoStockTargetQty) || item.autoStockTargetQty < 0)) fail(`${good.id}.autoStockTargetQty must be a nonnegative whole quantity`);
    if (item.autoStockCashReserveCents != null && (!Number.isInteger(item.autoStockCashReserveCents) || item.autoStockCashReserveCents < 0)) fail(`${good.id}.autoStockCashReserveCents must be a nonnegative integer`);
    if (item.pricingMode != null && !['market', 'fixed'].includes(item.pricingMode)) fail(`${good.id}.pricingMode must be market or fixed`);
    if (good.perishable) {
      if (!Array.isArray(item.perishableBatches)) fail(`${good.id} must track perishable batches`);
      const batchUnits = item.perishableBatches.reduce((sum, batch) => {
        if (!Number.isInteger(batch.qty) || batch.qty <= 0) fail(`${good.id} batch quantity is invalid`);
        if (!Number.isInteger(batch.acquiredMinute) || !Number.isInteger(batch.expiresMinute) || batch.expiresMinute <= batch.acquiredMinute) fail(`${good.id} batch timing is invalid`);
        return sum + batch.qty;
      }, 0);
      if (batchUnits !== item.qty) fail(`${good.id} batch units do not match inventory`);
      if (good.coldChain && item.qty > coldStorageCapacity(state)) fail(`${good.id} exceeds refrigerated capacity`);
    } else if (item.perishableBatches !== null) {
      fail(`${good.id} must not carry perishable batches`);
    }
    if (!Number.isInteger(market.priceCents) || market.priceCents < Math.floor(good.baseCents * 0.55) || market.priceCents > Math.ceil(good.baseCents * 1.8)) fail(`${good.id} market price is outside bounds`);
  }
  if (inventoryBulk(state) > storageCapacity(state)) fail('inventory exceeds storage capacity');
  if (GOODS.filter((good) => state.inventory[good.id].displayed).length > displaySlots(state)) fail('display slots exceeded');
  state.traffic.forEach((visitor) => {
    if (!Number.isInteger(visitor.arrivalMinute) || !Number.isInteger(visitor.departureMinute)) fail('visitor timing must use integer minutes');
    if (visitor.status === 'stopped' && visitor.stopReasons.length === 0) fail('stopped visitor lacks a reason');
    if (!Array.isArray(visitor.goodsSoldToPost) || new Set(visitor.goodsSoldToPost).size !== visitor.goodsSoldToPost.length || visitor.goodsSoldToPost.some((goodId) => !getGood(goodId))) fail('visitor same-stop trade memory is invalid');
    if (visitor.activity === 'fishing' && !visitor.stopReasons.includes('FISHING')) fail('fishing activity must expose a visible stop reason');
    if (visitor.typeId === 'california-prospectors' && visitor.cargo.some((offer) => offer.goodId === 'gold-dust' && offer.qty > 1)) fail('prospector gold consignments must remain small');
  });
  state.orders.forEach((order) => {
    if (!Number.isInteger(order.qty) || order.qty <= 0 || !Number.isInteger(order.arrivalMinute)) fail('order is invalid');
    if (getGood(order.goodId)?.tags.includes('no-wholesale')) fail(`${order.goodId} cannot enter through wholesale orders`);
  });
  state.ambientActors.forEach((actor) => {
    if (actor.interactable || actor.clickable || actor.tradeEligible || actor.countsForTraffic || actor.scores) fail('ambient stragglers must remain non-interactable and non-scoring');
    if (!Number.isInteger(actor.arrivalMinute) || !Number.isInteger(actor.departureMinute)) fail('ambient actor timing must use integer minutes');
  });
  state.decorations.forEach((decoration) => {
    const accessory = ACCESSORIES.find((item) => item.id === decoration.accessoryId);
    if (!accessory) fail('decoration refers to an unknown accessory');
    if (!state.plots[decoration.plotIndex - 1]?.owned) fail('decoration cannot occupy an unowned plot');
    if (!decoration.movable || decoration.plotIndex < 1 || decoration.plotIndex > 5) fail('decoration placement is invalid');
    if (!Number.isInteger(decoration.placedMinute) || decoration.placedMinute < 0 || decoration.placedMinute > state.timeMinutes) fail('decoration placement time is invalid');
    if (![decoration.x, decoration.y].every((value) => Number.isFinite(value) && value >= 0 && value <= 1)) fail('decoration coordinates are invalid');
  });
  const treatmentIds = new Set();
  state.dockTreatments.forEach((owned) => {
    if (treatmentIds.has(owned.id) || !dockTreatmentType(owned.treatmentId)) fail('owned dock treatment is invalid');
    treatmentIds.add(owned.id);
    if (owned.plotIndex == null) {
      if (owned.x != null || owned.y != null) fail('unplaced dock treatment cannot have coordinates');
    } else {
      const plot = state.plots[owned.plotIndex - 1];
      if (!plot?.owned || plot.construction || FACILITY_BY_ID[plot.facilityId]?.kind !== 'river') fail('dock treatment must occupy an operating river facility');
      if (![owned.x, owned.y].every((value) => Number.isFinite(value) && value >= 0 && value <= 1)) fail('dock treatment coordinates are invalid');
    }
  });
  state.smokeEmitters.forEach((emitter) => {
    if (!Number.isInteger(emitter.intensity) || emitter.intensity <= 0) fail('smoke intensity must be positive');
  });
  if (!state.meetings || !MEETING_POLICIES.some((policy) => policy.id === state.meetings.policy)) fail('meeting policy is invalid');
  if (!Array.isArray(state.meetings.profileCycle) || state.meetings.profileCycle.some((profileId) => !vendorProfile(profileId))) fail('meeting profile cycle is invalid');
  const meetingIds = new Set();
  for (const [collectionName, offers] of [['pending', state.meetings.pending], ['expired', state.meetings.expired], ['history', state.meetings.history]]) {
    if (!Array.isArray(offers)) fail(`meeting ${collectionName} must be an array`);
    offers.forEach((offer) => {
      if (meetingIds.has(offer.id)) fail('meeting offer ids must be unique across state');
      meetingIds.add(offer.id);
      if (!vendorProfile(offer.profileId) || !getGood(offer.goodId)) fail('meeting offer references unknown content');
      if (!Number.isInteger(offer.qty) || offer.qty <= 0 || !Number.isInteger(offer.unitCents) || offer.unitCents <= 0) fail('meeting offer quantity or price is invalid');
      if (offer.totalCents !== offer.qty * offer.unitCents) fail('meeting offer total does not reconcile');
      if (!Number.isInteger(offer.createdMinute)) fail('meeting offer creation time is invalid');
      if (offer.mandatory) {
        if (!offer.tutorial || offer.expiresMinute !== null || offer.profileId !== 'fur-trader-eli' || offer.goodId !== 'pelts' || offer.direction !== 'buy') fail('mandatory opening meeting is invalid');
      } else if (!Number.isInteger(offer.expiresMinute) || offer.expiresMinute <= offer.createdMinute) fail('meeting offer timing is invalid');
      if (String(offer.visitorId).startsWith('ambient-')) fail('ambient NPCs cannot create meetings');
      if (collectionName === 'pending' && offer.status !== 'pending') fail('pending meeting status is invalid');
      if (collectionName === 'expired' && offer.status !== 'expired') fail('expired meeting status is invalid');
      if (collectionName === 'history' && !['accepted', 'declined'].includes(offer.status)) fail('meeting history status is invalid');
    });
  }
  Object.values(state.meetings.relationships).forEach((memory) => {
    if (!Number.isInteger(memory.score) || memory.score < -10 || memory.score > 20) fail('meeting relationship score is invalid');
    if (!Number.isInteger(memory.encounters) || memory.encounters < 0) fail('meeting relationship encounters are invalid');
  });
  if (state.opening.status === 'awaiting-fur-trader') {
    const openingOffer = state.meetings.pending.find((offer) => offer.id === state.opening.tutorialOfferId);
    if (!openingOffer?.mandatory) fail('opening tutorial must remain pending and mandatory');
  }
  const tutorialTrapper = state.traffic.find((visitor) => visitor.id === 'tutorial-trapper');
  if (tutorialTrapper && (tutorialTrapper.cargo.length > 0 || !tutorialTrapper.tradeResolved)) fail('tutorial trapper cannot auto-sell extra cargo');
  if (!state.employees || !Array.isArray(state.employees.candidates) || !Array.isArray(state.employees.hired) || !Array.isArray(state.employees.history)) fail('employee state is invalid');
  const employeeIds = new Set();
  [...state.employees.candidates, ...state.employees.hired].forEach((employee) => {
    if (employeeIds.has(employee.id) || !employeeRole(employee.roleId)) fail('employee id or role is invalid');
    employeeIds.add(employee.id);
    if (!VENDOR_PROFILES.some((profile) => profile.id === employee.personId)) fail('employee person roster reference is invalid');
    if (employee.appearance?.honestyCue !== false || !['a', 'b', 'c', 'd', 'e', 'f'].includes(employee.appearance?.atlasId)) fail('employee appearance must use a neutral portrait atlas');
    if (!Number.isInteger(employee.skill) || employee.skill < 1 || employee.skill > 5 || !Number.isInteger(employee.wageCents) || employee.wageCents <= 0) fail('employee skill or wage is invalid');
    if (![employee.loyalty, employee.integrity].every((value) => Number.isInteger(value) && value >= 0 && value <= 100)) fail('employee loyalty or integrity is invalid');
    if (employee.traitIds.some((traitId) => !employeeTrait(traitId))) fail('employee trait is invalid');
    Object.values(employee.knowledge).forEach((knowledge) => { if (!Number.isInteger(knowledge) || knowledge < 0 || knowledge > 100) fail('employee knowledge is invalid'); });
    if (employee.assignment) {
      const role = employeeRole(employee.roleId);
      if (!role.assignments.includes(employee.assignment.type)) fail('employee assignment is incompatible with role');
      if (employee.assignment.plotIndex != null && !state.plots[employee.assignment.plotIndex - 1]?.owned) fail('employee assignment targets an unowned plot');
    }
  });
  if (Object.values(identityTotals(state)).some((value) => !Number.isFinite(value))) fail('town identity must remain finite');
  try {
    JSON.stringify(state);
  } catch {
    fail('state must be JSON serializable');
  }
  return true;
}
